/** API types */
// Type-only import (erased at runtime, so no cycle with streamReducer, which imports
// value types from this file). The persisted trace IS the live ProgressLine[] shape.
import type { ProgressLine } from "../hooks/streamReducer";
import type { ApiFileLocalSource } from "../platform/Platform";
import type { FolderScope } from "./folderScopes";
import type { ImportAcceptedResponse } from "./importAccepted";
import type { MarvinRfiFormEdit } from "./marvinWidgets";
import type { ApiRevisionComparisonSummary } from "./revisionComparisonSummary";
import type { ApiVersionState } from "./versionState";
import type { ApiUserPreferences } from "./userPreferences";
import type { ApiIssueRfi } from "./constructabilityIssueTypes";
import type {
    HandMatch,
    ReconciliationEvidence,
    ReconciliationExceptionCategory,
    SideBySideView,
    TieOut,
} from "./costToBillTypes";
// The Marvin wire domain stays below this broad shared interface hub.
import type {
    ApiBounds,
    ApiFileMetadata,
    ApiMessagePart,
    ApiReference,
    ArtifactMeta,
    ContextStatsEventData,
} from "../marvin/messagePartTypes";
import { ApiDocumentTypes } from "./documentTypes";
import type { RevisionGroupImportState } from "./externalRevisionGroupState";
import type { RevisionStatus } from "../api/revisions";
import type { RfiAssistRowMeta } from "../api/documentWorkflowTypes";
import type { DraftCheck, DraftOrigin } from "../api/rfiDrafts";
import type { FeatureFlagAuditRecord, FlagKind } from "../api/featureFlags";
import type { Phase, StatusTone } from "../api/workflowTemplates";
import type { PreconMemberPreviews } from "../components/workflow/precon/types";
export type {
    InvoiceSides,
    ReconciliationAssociation,
    ReconciliationDateQuality,
    ReconciliationEvidence,
    ReconciliationExceptionCategory,
    ReconciliationRowKind,
    SideBySideEntry,
    SideBySideVendorBlock,
    SideBySideView,
} from "./costToBillTypes";
export type { ApiBounds, ApiReference, ArtifactMeta } from "../marvin/messagePartTypes";
export { ApiDocumentTypes, SPEC_DOCUMENT_TYPES } from "./documentTypes";
export type { ApiDocumentType } from "./documentTypes";

/** Server-owned publication gate, shared by the review grid and publish endpoint. */
export interface PublicationReadiness {
    publishable: boolean;
    blockers: { code: string; message: string; section_group_id?: string | null }[];
    pending_verification_count: number;
    /** Exact candidate counts from the same manifest used by publication. */
    current_count?: number;
    historical_count?: number;
    held_count?: number;
    held_section_group_ids?: string[];
    /** Current plus historical versions to publish; held versions excluded. */
    publishable_count?: number;
}

export interface ResumeHeldRevisionResult {
    operation_id: string;
    revision_id: string;
    remaining_section_group_ids: string[];
    generation: number;
}

/** One platform-wide flag: a single row in platform-central, not one row per tenant. */
export interface GlobalFlagStatus {
    key: string;
    label: string;
    description: string;
    category: string;
    kind: FlagKind;
    default: boolean;
    value: boolean;
    source: "kv" | "default";
    last_changed_by: FeatureFlagAuditRecord | null;
}

export const ApiMessageTypes = {
    Error: "error",
    Markdown: "markdown",
    Table: "table",
};

// Display labels for document types that should read differently in the UI than
// their stored/API value. The wire value stays unchanged (e.g. "Generic"); only
// the user-facing label differs.
const DOCUMENT_TYPE_DISPLAY_LABELS: Record<string, string> = {
    [ApiDocumentTypes.Generic]: "Unclassified",
    [ApiDocumentTypes.Supplemental]: "Supplemental",
    [ApiDocumentTypes.Code]: "Codes",
    [ApiDocumentTypes.PrimeContract]: "Prime Contract",
    [ApiDocumentTypes.ShopDrawing]: "Shop Drawings",
};

export function documentTypeLabel(documentType: string): string {
    return DOCUMENT_TYPE_DISPLAY_LABELS[documentType] || documentType;
}

export const ApiFileTypes = {
    Spec: "spec",
    Drawing: "drawing",
    Sheet: "sheet",
    Upload: "upload",
    Schedule: "schedule",
    Chat: "chat", // a file attached in chat: its document belongs to its conversation
};

export interface ApiUser {
    id: string;
    email: string;
    name: string;
}

export interface ApiFile extends ApiFileMetadata {
    /** The bytes `fileUrl` serves, signed to respond as an attachment (or the
     *  gateway's `download=true`). Browsers ignore `<a download>` on the
     *  cross-origin storage URL, so a PDF would open in the tab instead of saving.
     *  Set by the Documents serializer only; fall back to `fileUrl` when absent. */
    downloadUrl?: string;
    fragmentNodes?: ApiFragmentNode[];
    sheets?: ApiSectionGroup[];
    /** Set when this file is a local one (Open PDF…, This Mac, Favorites,
     *  drag-drop) rather than a cloud document -- see `ApiFileLocalSource`
     *  (`platform/Platform.ts`) and `loadFileAndCache`'s local branch. */
    localSource?: ApiFileLocalSource;
}

export interface ApiDocumentSummary {
    counts: {
        by_file_type: Record<string, number>;
        by_document_type: Record<string, number>;
        section_groups: {
            sheets: number;
            specifications: number;
        };
        total_documents: number;
    };
}

/**
 * Canonical SectionGroup lifecycle status. Values come from
 * ``ai_engine3/db/migrations/0070``. Treated as the single source of truth;
 * `api/revisions` re-exports this name.
 */
export type SgStatus =
    | "processing"
    | "annotation_ready"
    | "review"
    | "published"
    | "inactive"
    | "failed"
    | "cancelled";

/** Pre-migration values still tolerated at the type level. */
type LegacySgStatus = "active" | "pending_revision";

export type RevisionProcessingStatus = "not_started" | "processing" | "processed" | "failed";

export type SheetLifecycleStatus = "basic" | "processing" | "processed" | "failed";

/** Tenant-global drawing-set area (PP-580). `sheet_count` is across the tenant. */
export interface ApiArea {
    id: string;
    name: string;
    is_common: boolean;
    sheet_count: number;
}

/** An area as a review names it: the one it was checked against, or one that could be. */
export type ApiAreaRef = Pick<ApiArea, "id" | "name">;

export interface ApiIdentityVerification {
    sheet_id: string | null;
    sheet_title: string | null;
    disagrees: boolean;
}

export interface ApiVersionRef {
    section_group_id: string;
    /** The sheet's document: the `{doc}` of its viewer address `/doc/{doc}/sg/{sg}`. */
    document_id?: string | null;
    revision_id: string | null;
    revision_name: string | null;
    revision_date: string | null;
    /** Same rule as `RevisionSummary.revision_date_is_calendar` (see formatRevisionDate). */
    revision_date_is_calendar: boolean;
    sheet_number: string;
    title: string | null;
}

export interface ApiSectionGroup {
    /** Late verification is advisory; reviewed identity stays authoritative. */
    identity_verification?: ApiIdentityVerification | null;
    id: string;
    group_code: string;
    /** Global CSI order across spec documents in the batched listing. */
    spec_sort_index?: number;
    /** Server-decided user-facing code: the natural identity (sheet id, spec
     *  code) or null when the type's codes are synthetic bookkeeping
     *  ("GENERIC_0"). Absent on payloads that predate the field. */
    display_code?: string | null;
    group_title: string;
    document_id: string;
    /** Page where this SG begins inside its host file. */
    start_page_in_file: number | null;
    /** A book discriminator: the larger of the distinct section start pages and the
     *  distinct CSI sections on this group's file (any status). Above 1, the file is a book
     *  of sections. Absent on payloads that predate the field. */
    file_section_count?: number;
    revision_number: number;
    is_active: boolean;
    status?: SgStatus | LegacySgStatus;
    version_state?: ApiVersionState; // read through versionStateOf; absent from older servers
    processing_status?: SheetLifecycleStatus | null;
    metadata_version?: number | null;
    discipline?: string;
    /** Non-null when a user pinned the sheet to a discipline;
     *  `discipline` then carries the pinned code instead of the derived one. */
    discipline_id?: string | null;
    file?: ApiFile;
    created_at?: string;
    updated_at?: string;
    title_textbox?: string | null;
    /** Textbox UUID backing the sheet ID (DB column id_textbox_uuid). */
    id_textbox?: string | null;
    /**
     * The sheet's ingest revision — the scope markup persistence/downloads key on.
     * Nullable: the API emits null instead of 500ing when a row's revision_id
     * dangles, and lightweight payloads (router history state) may omit it.
     */
    revision?: {
        id: string;
        name: string;
        /** The revision's position in its document's issue order (larger = issued later):
         *  what orders a sheet's versions (`utils/sheetVersionOrder`), never
         *  `revision_number`. Absent from servers that predate the field. */
        issue_order?: number | null;
    } | null;
    /** Root Section UUID for this sheet — anchor for sheet-level edits like destination_name. */
    root_section_uuid?: string | null;
    /** Sheet-level destination name (lives on the root Section's metadata). */
    destination_name?: string | null;
    /** Area assignment (PP-580). Optional: older payloads may omit these. */
    area_id?: string | null;
    area_name?: string | null;
    area_source_key?: string | null;
    is_common?: boolean;
    /**
     * This version's ledger row is the provider's designated current revision
     * of its drawing (versions endpoint only). Under head_policy
     * "provider_current" the active head may deliberately sit on older content.
     */
    is_provider_current?: boolean;
    /** Integration that supplied this version (e.g. "PROCORE", "ACC"); null for local uploads. */
    provider?: string | null;
    /**
     * The sheet's active version, set only on a non-active row whose sheet has one (a draft too).
     * Absent on payloads from before the field's rollout.
     */
    current_version?: ApiVersionRef | null;
}

export interface PendingRevision {
    tempId: string;
    file: File;
    uploadProgress: number;
    status: "uploading" | "processing" | "error" | "complete";
    errorMessage?: string;
    result?: ApiSectionGroup;
}

export interface SpecJobStatus {
    status: "processing" | "completed" | "failed" | "not_found";
    filename?: string;
}

/**
 * Synced submittal fields from GET /api/ai/documents (Submittal docs only).
 * Sourced from the ACC sync bridge; null until the first sync runs.
 */
export interface ApiSubmittalMetadata {
    status: string | null;
    number: string | null;
    spec: string | null;
    item_type: string | null;
    due_date: string | null;
    description: string | null;
    priority: string | null;
    manager: string | null;
    responsible_contractor: string | null;
    ball_in_court: string | null;
    current_revision: string | null;
    /** Normalized integration source: "acc" | "procore" | "local". */
    source: string;
    external_url: string | null;
}

export interface ApiDocument {
    id: string;
    document_type: string;
    document_subtype?: string | null;
    title: string;
    file?: ApiFile;
    /** Present only for Submittal documents; null when the sync row doesn't exist yet. */
    submittal_metadata?: ApiSubmittalMetadata | null;
    /** Server-derived lifecycle status on register/category listings — absent
     *  (defaults to "ready") on endpoints that only ever return active documents. */
    status?: "processing" | "failed" | "ready";
    /** One plain sentence explaining a failure — present only when status is "failed". */
    error_message?: string;
}

/**
 * Document Management v2 (Documents category / folder tree). A flat folder
 * list from `GET /api/ai/folders` — the tree is built client-side from
 * `parent_id`, siblings ordered by name (`compareFolderNames`).
 */
export interface ApiFolder {
    id: string;
    parent_id: string | null;
    name: string;
    path: string;
    sort_order: number;
    /** Documents directly in this folder (not including subfolders). */
    document_count: number;
    /** The top-level folder it is in (`shared/folderScopes`); absent from the default
     *  `GET /folders`, which lists Additional docs' folders only. */
    scope?: FolderScope;
}

/** Classification lifecycle surfaced to admins for a foldered document. */
type ClassificationStatus = "pending" | "auto" | "low_confidence" | "confirmed" | "overridden";

/** A Documents-library document, as returned by `GET /api/ai/documents?library=true` (or `?folder_id=`). */
export interface ApiFolderedDocument {
    id: string;
    document_type: string;
    title: string;
    is_active: boolean;
    /** Lifecycle status derived server-side (library listings only). */
    status?: "published" | "processing" | "failed";
    created_at: string;
    in_library: boolean;
    folder_id: string | null;
    classification_status: ClassificationStatus | null;
    file?: ApiFile;
}

/** An allowed document type from `GET /api/ai/documents/types`. */
export interface ApiDocumentTypeOption {
    value: string;
    // Backend-supplied label; the UI derives its own display label from `value`
    // via documentTypeLabel(), so this is not read for rendering.
    label?: string;
}

/** A row from `GET /api/ai/documents/{id}/activity` (admin) — one document's activity timeline. */
export interface ApiDocumentActivityEntry {
    at: string;
    kind: string;
    label: string;
    status?: string;
}

export interface ApiCompactDocumentMetadata {
    title?: string;
    summary?: string;
    location?: string | string[] | null;
    scope?: string | null;
    keywords?: string[] | null;
    impact?: string | null;
    references?: string[] | null;
    [key: string]: unknown;
}

export type ApiRichDocumentSource = "local" | "acc" | "procore";

export interface IngestionStatus {
    status: "processing" | "completed" | "failed" | "not found";
    filename: string;
    /** Where processing actually is, so the UI shows a stage instead of a bare
     *  spinner. Absent on the background-task ingest path. */
    stage?: "queued" | "reading" | "indexing" | "published" | "failed";
    /** Sections written so far, for "Indexing · N sections". */
    section_count?: number | null;
}

/** PATCH semantics — only send the fields you intend to change. */
export interface DocumentManagementUpdateInput {
    folder_id?: string | null;
    /** false removes the document from the Documents library (and clears its folder). */
    in_library?: boolean;
}

export interface UploadAutoResponse {
    file: unknown;
    message: string;
    file_id: string;
    /** Same content already uploaded and parsed — offer "reupload?" (adopts, no reprocessing). */
    already_exists?: boolean;
    /** Same content+name+category already exists as another document — no new
     *  document was created, so `file_id` above has nothing to poll. Set exactly
     *  when `already_exists` is true. */
    duplicate_of?: {
        file_id: string;
        title: string;
        document_type: string;
        status: string;
    } | null;
    /** A new document was created but its content was cloned from an identical
     *  existing document (different name/category) — `file_id` is real and
     *  publishes normally, just faster than a fresh parse. */
    cloned_from?: {
        file_id: string;
        title: string;
    } | null;
}

export interface RetypeDocumentResponse extends ApiFolderedDocument {
    job_id?: string;
    revision_id?: string;
}

/** `DocumentImport.status` lifecycle — see ai_engine `DocumentImportStatusEnum`. */
export type DocumentImportStatus =
    | "draft"
    | "analyzing"
    | "ready"
    | "committing"
    | "processing"
    | "completed"
    | "completed_with_errors"
    | "failed"
    | "cancelled";

/** `DocumentImportItem.status` counts — see ai_engine `DocumentImportItemStatusEnum`. */
export interface DocumentImportItemCounts {
    discovered?: number;
    unsupported?: number;
    queued?: number;
    processing?: number;
    published?: number;
    failed?: number;
    skipped?: number;
}

interface DocumentImportSummary {
    id: string;
    root_folder_id: string | null;
    root_name: string | null;
    status: DocumentImportStatus;
    error: string | null;
    expected_items: number | null;
    published_items: number;
    failed_items: number;
    skipped_items: number;
}

export interface DocumentImportStatusResponse {
    import: DocumentImportSummary;
    counts: DocumentImportItemCounts;
    /** File names currently being processed (up to 3), so a long import can name
     *  the document it is on rather than only a count. */
    in_flight?: string[];
}

export interface CreateImportResponse {
    import_id: string;
}

export interface ApiRichDocumentAttachment {
    name: string;
    sourceRevision?: string | null;
    availability?: "cached" | "pending";
    attachmentType?: string | null;
    size?: number | null;
    createdAt?: string | null;
    /** Legacy raw Azure SAS URL. Kept for backward compat; the UI now uses `contentUrl`. */
    fileUrl?: string | null;
    fileId?: string | null;
    azurePath?: string | null;
    /** Stable opaque key used by RFI evidence provenance to identify this attachment. */
    sourceKey?: string | null;
    /**
     * Proxy-relative path to the authenticated in-app blob stream
     * (`/document/{id}/rfi/attachments/{sourceKey}/content`). Prefix with `/api/ai` to
     * fetch. The opaque key never exposes the blob path or tenant container.
     */
    contentUrl?: string | null;
    /** Whether the backend generated this attachment rather than ingesting an original file. */
    isGenerated?: boolean;
    isPrimary: boolean;
    isPdf: boolean;
}

export interface ApiMetadataField {
    label: string;
    value: string;
}

export interface ApiRichDocumentSectionGroup {
    id: string;
    groupCode: string;
    groupTitle: string;
    documentPage: number | null;
    chapterType?: string | null;
    chapterTypeRaw?: string | null;
}

export interface ApiRfiDetail {
    sourceFields?: ApiSourceFields;
    kind: "rfi";
    /** The RFI row id (distinct from documentId); keys the RFI link endpoints. */
    id: string;
    source: ApiRichDocumentSource;
    documentId: string;
    integrationObjectId?: string | null;
    objectId?: string | null;
    title: string;
    summary?: string | null;
    status?: string | null;
    updatedAt?: string | null;
    sourceProjectName?: string | null;
    externalUrl?: string | null;
    file?: ApiFile | null;
    attachments: ApiRichDocumentAttachment[];
    metadataFields: ApiMetadataField[];
    docMetadata?: ApiCompactDocumentMetadata | null;
    contentSections: ApiRichDocumentSectionGroup[];
    ingested: boolean;
    rfiNumber?: string | null;
    /** Details column rows (Status, Ball in court, Due date, RFI manager, Assignees,
     *  Distribution, Received from, …), only the ones the source carries. */
    details?: ApiMetadataField[];
    statusNorm?: string | null;
    ballInCourt?: string | null;
    dueAt?: string | null;
    question?: string | null;
    response?: string | null;
    referencedDrawings: string[];
    referencedSpecs: string[];
    /** Char-offset spans of auto-links found in the question text, for inline highlighting. */
    questionSpans?: LinkSpan[];
    /** Char-offset spans of auto-links found in the response text, for inline highlighting. */
    responseSpans?: LinkSpan[];
}

/**
 * Where an auto-link's evidence was found. "question"/"response" are the only
 * fields with inline-highlightable text; "description"/"document"/
 * "attachment:<opaque-key>" have no text box so their evidence is surfaced per-link
 * instead. Legacy links may still use "attachment:<name>". Widened with `(string & {})`
 * to tolerate future/unknown source fields.
 */
type RfiLinkSourceField = "question" | "response" | "description" | "document" | (string & {});

/** A char-offset span tying a slice of RFI question/response text to an auto-generated link. */
export interface LinkSpan {
    linkId: string;
    field: RfiLinkSourceField;
    start: number;
    end: number;
}

export type RfiLinkTargetKind = "drawing" | "spec";
type RfiLinkSource = "auto" | "manual";

/**
 * A resolved RFI <-> sheet/spec link (PP-724). Mirrors the backend Ref payload:
 * the durable anchor fields plus the current-revision location used to render a
 * viewer highlight and deep-link to the sheet.
 */
export interface ApiRfiLink {
    id: string;
    rfiId: string;
    targetKind: RfiLinkTargetKind;
    groupCode: string;
    hId: string | null;
    /** "auto" = Marvin-generated, "manual" = user-added. */
    source: RfiLinkSource;
    createdBy: string | null;
    confidence: number | null;
    rationale: string | null;
    sourceRef: string | null;
    /** Durable target document (present even when the sheet is stale). */
    documentId: string;
    /** Current-revision sheet UUID for deep-linking; null when stale. */
    sectionGroupId: string | null;
    fileId: string | null;
    sheetTitle: string | null;
    page: number | null;
    bounds: ApiBounds | null;
    /** Anchor no longer resolvable on the current revision (no pin to show). */
    stale: boolean;
    /** Review state: auto links stay "pending" until a user approves them; manual links
     * are born "approved" (with `reviewedBy` = their creator). */
    reviewStatus: "pending" | "approved";
    /** User id who approved the link. Null/absent on legacy payloads and while pending. */
    reviewedBy?: string | null;
    /** Reviewer display name, resolved server-side (the client has no internal-id ->
     * name lookup). Null while pending AND for AI approvals — branch on `reviewedBy`. */
    reviewedByName: string | null;
    /** ISO timestamp of approval; null while pending. */
    reviewedAt: string | null;
    /**
     * Which RFI field the evidence for this link came from: "question", "response",
     * "description", "document", or "attachment:<opaque-key>". Legacy attachment
     * links may contain a filename instead. Null on older links.
     */
    sourceField?: RfiLinkSourceField | null;
    /** Verbatim evidence phrase that justified the link. Null on older links. */
    sourceQuote?: string | null;
}

/** The RFI identity fields shared by the list, search, and review-queue payloads. */
interface ApiRfiIdentity {
    id: string;
    documentId: string | null;
    rfiNumber: string | null;
    title: string;
    status: string | null;
}

/** Compact RFI summary attached to each link on the sheet-overlay endpoint. */
export interface ApiRfiLinkRfiSummary {
    id: string;
    /** The RFI's own document id, for deep-linking to its card at /doc/{documentId}. */
    documentId: string | null;
    rfiNumber?: string | null;
    title: string;
    status?: string | null;
    /** Normalized lifecycle word (open/closed/void/draft) behind the provider's raw
     * workflow step. Optional: an older ai_engine omits it (PP-1047). */
    statusNorm?: string | null;
}

/**
 * A single section within a section group, as returned by
 * `GET /section_groups/{id}/sections` (the backend `to_simple_api_response`).
 * Used to populate the detail (h_id) sub-picker when manually linking an RFI
 * to a specific drawing detail, and to render the spec sections tree.
 */
export interface ApiGroupSection {
    uuid: string;
    h_id: string;
    title: string;
    page?: number;
    is_leaf: boolean;
    file_id: string;
    parent_uuid: string | null;
    /** The detail's crop on its sheet; only when the server sends it (a crop needs it). */
    bounds?: number[] | null;
}

export interface ApiSheetRfiLink extends ApiRfiLink {
    rfi: ApiRfiLinkRfiSummary | null;
}

/**
 * Raw row returned by `GET /api/ai/rfi-links/review-queue`.
 *
 * The evidence fields are optional while the frontend and ai_engine queue reframe
 * deploy independently.
 */
export interface RfiReviewQueueItem extends ApiRfiIdentity {
    uncitedCount?: number;
    citedCount?: number;
    totalAutoLinks: number;
    needsAttention?: boolean;
    /** HUMAN sign-off only (Marvin's autosignoff excluded) — drives the review pane's
     * "Reviewed / Mark as unapproved" state. Optional until ai_engine #2091 deploys. */
    humanSignoff?: boolean;
    /**
     * @deprecated Compatibility fields kept by the backend until the queue reframe
     * (ai_engine #2091) deploys.
     */
    pendingCount?: number;
    reviewedCount?: number;
    reviewed?: boolean;
}

/** Queue row after the API-boundary compatibility normalization. */
export interface NormalizedRfiReviewQueueItem extends RfiReviewQueueItem {
    uncitedCount: number;
    citedCount: number;
    needsAttention: boolean;
    reviewed: boolean;
    humanSignoff: boolean;
}

/** Per-RFI impacted-links summary for the dashboard "Impacted" column. */
interface ApiRfiImpacted {
    count: number;
    /** Distinct group_code preview (first few), for chips. */
    codes: string[];
}

/**
 * Per-RFI auto-link rollup for the dashboard. Two independent facts, two fields:
 *
 * `status` is APPROVAL — null when the RFI has no auto links to review, "approved"
 * once none are pending, else "pending". It answers "who has looked at this".
 *
 * `evidence` is what we can POINT AT — "none" (no auto links), "uncited" (some link's
 * quote was never located in a real source), or "cited" (every link's was). It answers
 * "what can we show them", and it is what the chip's vocabulary actually claims.
 * Optional so a client can still read an older payload that predates the split.
 *
 * (Distinct from the workflow `ApiRfiReview` type further down.)
 */
interface ApiRfiReviewRollup {
    status: "approved" | "pending" | null;
    evidence?: "none" | "uncited" | "cited";
    pending: number;
    approved: number;
    total: number;
    /** RFI-level sign-off identity (Marvin's id when AI-verified, else a human user id);
     * null until the RFI is approved. Drives the dashboard "verified by" treatment. */
    reviewedBy: string | null;
    /** Sign-off display name, resolved server-side; null for AI sign-offs and while
     * never signed off. */
    reviewedByName: string | null;
}

/** Where a Marvin draft came from, as its register row carries it (CONTRACT_V31 §5.4). */
export type RfiRowOrigin = Pick<DraftOrigin, "kind" | "label" | "at" | "created_by_name">;
/** The check a draft's register row leads with: its first warn, else info, else ok (§5.4). */
export type RfiRowCheck = Pick<DraftCheck, "state" | "text">;

/** What a Pending row's No. cell reads (`number_cell`): the text, and the line under it. */
export interface RfiNumberCell {
    text: string;
    caption: string | null;
}

/**
 * A row in the RFI dashboard table (`GET /rfis`). Lightweight by design —
 * question/response text is omitted and lazy-loaded per `ApiRfiDetail` on expand.
 */
export interface ApiRfiListItem extends ApiRfiIdentity {
    /** Normalized lifecycle: "open" | "closed" | "void" | "draft"; absent on older backends. */
    statusNorm?: string | null;
    /** Who owes the next action (company or person), as the provider names them. */
    ballInCourt?: string | null;
    /** Native assignment identity, independent of display names; absent on provider inventories. */
    assignedMemberIds?: string[];
    /** The company that raised the RFI. */
    receivedFrom?: string | null;
    /** The provider payload's own discipline and due date, as this list has always sent them. */
    discipline: string | null;
    dueDate: string | null;
    /**
     * The Project Management projection of the same two facts (normalized discipline, due
     * instant). Sent beside the provider values; absent without Project Management and on
     * older backends. The kit registers read them through `rfiDue` / `rfiDiscipline`.
     */
    disciplineNorm?: string | null;
    dueAt?: string | null;
    /** Modified date (ACC `updatedAt`, else the row's update time); ISO string. */
    updatedAt: string | null;
    priority: string | null;
    source: string; // the record's owner: "primepoint" | "procore" | "acc"
    readOnly?: boolean; // a provider's mirror: the register offers no change to it
    externalUrl: string | null;
    impacted: ApiRfiImpacted;
    /** Auto-link review rollup (approved / pending / null), for the dashboard chip + filter. */
    review: ApiRfiReviewRollup;
    /**
     * A native RFI's place in its workflow (`toRfiListItem`); imported rows carry none and are
     * read by their status text. `status` is then the state's own label.
     */
    phase?: Phase | null;
    statusTone?: StatusTone | null;
    /** Submitted but not yet accepted: the register's Submitted view. */
    intake?: boolean;
    /** The RFI this one revises (a revision child such as 057R1). */
    parentId?: string | null;
    revisionLabel?: string | null;
    /** The submitting company's code, the register's "From" column. */
    companyCode?: string | null;
    /** Marvin's latest review of a draft ("already asked · answered by …"); absent when there is none. */
    marvin?: RfiAssistRowMeta | null;
    /**
     * A native RFI's workflow field values the register asked for, by field key; `null` is a
     * field the RFI never had ("Not captured"). Absent on imported rows, which have none.
     */
    /** Workflow field values the register asked for; a multiple choice's is its list of option values. */
    /** Workflow field values; a company field's is `{id, name}` (CONTRACT_V31 §6.2). */
    customFields?: Record<
        string,
        string | number | boolean | null | string[] | { id: string; name: string }
    > | null;
    /** When the official answer was given, for "Answered <date>"; native rows only. */
    answeredAt?: string | null;
    /** RFI register v3 (native rows only; `toRfiListItem`). The record's current label, as stored. */
    numberLabel?: string | null;
    /** The register tabs the row is in besides All, as the server decides them for this viewer. */
    tabs?: ("mine" | "design" | "gc_check" | "drafts" | "closed" | "project" | "pending")[];
    companyNumber?: string | null;
    /** The author's company name, for the From column. */
    fromName?: string | null;
    /** The parent's label on a revision ("SUNDT-RFI-000213"). */
    parentLabel?: string | null;
    /** When the RFI entered its current step. */
    stateSince?: string | null;
    revisionCount?: number;
    /** The action that discards this draft, when the viewer may take it. */
    discardAction?: { action: string; label: string; note?: string } | null;
    costImpact?: string | null;
    /** Another company holding the RFI, for a subcontractor's register ("Nathan Brandt · Sundt"). */
    courtCompany?: string | null;
    /** The record's version, which a command from the register sends as `expected_version`. */
    version?: number;
    /** A draft Marvin made: where from, by whom and when (the Drafts tab's line). */
    origin?: RfiRowOrigin | null;
    /** What Marvin checked on that draft, for the Drafts tab's Marvin checked column. */
    marvinCheck?: RfiRowCheck | null;
    /** Whom a draft goes to (the Drafts tab's To): its recipients, else Marvin's resolved To. */
    toLabel?: string | null;
    /** Who last touched the RFI, the second line of the Last modified column. */
    updatedByName?: string | null;
    /** The server's words for the Step chip ("Pending", "Pending · GC check"); else the state label. */
    stepLabel?: string | null;
    /** The line under the subject on a Pending row. */
    subtitle?: string | null;
    /**
     * The No. cell of a row on the Pending tab as the server words it (`number_cell` on the wire):
     * a draft is "—", a sub's RFI its own number with the caption "sub's number". Null on every
     * other tab and for a row outside the Pending set.
     */
    numberCell?: RfiNumberCell | null;
    /** An expedited review is asked for. */
    expedite?: "yes" | null;
    /** The date the answer is needed by, and the contract's own date while the request carries one. */
    neededBy?: string | null;
    contractDate?: string | null;
}

/** Lightweight RFI hit from the hybrid search endpoint (`GET /rfis/search`). */
export type ApiRfiSearchResult = ApiRfiIdentity;

/**
 * Structured submittal record for the hover card and viewer toolbar.
 * Mirrors the `/document/{id}/submittal` response: the synced decoration
 * (`ApiSubmittalMetadata`) plus the document title and normalized source.
 * Unlike the list endpoint's `submittal_metadata`, this response carries
 * `source`/`externalUrl` (camelCase) instead of `source`/`external_url` —
 * omit the base fields so the type doesn't claim a snake_case `external_url`
 * that never actually arrives.
 */
export interface ApiSubmittalDetail extends Omit<ApiSubmittalMetadata, "source" | "external_url"> {
    kind: "submittal";
    sourceFields?: ApiSourceFields;
    documentId: string;
    title: string | null;
    source: ApiRichDocumentSource;
    externalUrl: string | null;
    attachments: ApiRichDocumentAttachment[];
    /** The round whose bytes the KG actually holds; null for metadata-only
     * submittals, absent on deployed backends that don't emit it yet. Differs
     * from current_revision when the latest round has no submitted PDF yet —
     * the viewable content is a prior round's. */
    contentRevision?: string | null;
}

interface ApiSubmittalSyncProjectStatus {
    integrationId: string;
    source: "acc" | "procore";
    projectId: string | null;
    projectName: string | null;
    sourceCount: number | null;
    sourceObservedAt: string | null;
    cachedCount: number;
    currentCount: number;
    syncedCount: number;
    failedCount: number;
    /**
     * ACC rows at the retry cap, skipped by every pass until their cool-down elapses.
     * A subset of `failedCount`. Always 0 for Procore, which has no backoff.
     */
    cappedCount: number;
    /**
     * `failedCount - cappedCount`: failed rows the next pass will attempt again. Not a
     * count of retries in flight. Every Procore failure lands here, capped or not.
     */
    failedRetryingCount: number;
    attachmentsCheckedCount: number;
    withAttachmentsCount: number;
    ingestibleCount: number;
    projectedCount: number;
    documentCount: number;
    sourceMissingCount: number | null;
    sourceExtraCount: number | null;
    projectionMissingCount: number;
    documentMissingCount: number;
}

interface ApiRfiSyncProjectStatus {
    integrationId: string;
    source: "acc" | "procore";
    projectId: string | null;
    projectName: string | null;
    sourceCount: number | null;
    sourceObservedAt: string | null;
    cachedCount: number;
    syncedCount: number;
    failedCount: number;
    /**
     * ACC rows at the retry cap, skipped by every pass until their cool-down elapses.
     * A subset of `failedCount`. Always 0 for Procore, which has no backoff.
     */
    cappedCount: number;
    /**
     * `failedCount - cappedCount`: failed rows the next pass will attempt again. Not a
     * count of retries in flight. Every Procore failure lands here, capped or not.
     */
    failedRetryingCount: number;
    hydratedCount: number;
    projectedCount: number;
    sourceMissingCount: number | null;
    sourceExtraCount: number | null;
    projectionMissingCount: number;
    hydrationMissingCount: number;
}

export interface ApiSubmittalSyncStatus {
    projects: ApiSubmittalSyncProjectStatus[];
    rfiProjects?: ApiRfiSyncProjectStatus[];
}

export interface ApiParseDebugSectionStub {
    id: string;
    file_id: string;
    section_group_id: string | null;
    parent_uuid: string | null;
    is_leaf: boolean;
    is_active: boolean | null;
    h_id: string | null;
    serialized_h_id: string | null;
    modifier: number | string | null;
    sort_order: number | null;
    title: string | null;
    text: string | null;
    page: number;
    bounds: ApiBounds;
    locations: unknown;
    section_metadata: Record<string, unknown> | null;
    section_type: string | null;
}

export interface ApiParseDebugSectionGroup {
    id: string;
    document_id: string;
    group_code: string;
    group_title: string;
    discipline: string | null;
    document_page?: number | null;
    start_page_in_file?: number | null;
    revision_number: number;
    revision_id?: string | null;
    revision_name?: string | null;
    is_active: boolean;
    status?: string | null;
    created_at: string | null;
    updated_at: string | null;
    chapter_type: string | null;
    chapter_type_raw: string | null;
    group_metadata: Record<string, unknown> | null;
    title_textbox: string | null;
    id_textbox?: string | null;
    file: ApiFile | null;
    sections: ApiParseDebugSectionStub[];
}

export interface ApiParseDebugDocumentTree {
    document: {
        id: string;
        title: string;
        document_type: string;
        is_active: boolean;
        created_at: string | null;
        summary: string | null;
        doc_metadata: Record<string, unknown> | null;
        toc: Record<string, unknown> | null;
        file: ApiFile | null;
    };
    section_groups: ApiParseDebugSectionGroup[];
    ungrouped_sections: ApiParseDebugSectionStub[];
}

export interface AnchorProvenance {
    source: string;
    by: string | null;
    at: string | null;
}

export interface ApiFragmentNode {
    id: string;
    fileId: string;
    bounds: ApiBounds;
    title: string;
    rawTitle?: string; // Original title from DB, before composition
    text?: string; // Section text content
    hierarchyId: string;
    documentType?: string;
    discipline?: string; // Drawing discipline stored on the node's SectionGroup (constructability issue nodes)
    /** The node's owning SectionGroup. `title` is a composed display string —
     *  it omits the leaf suffix for an untitled leaf and is empty when every
     *  ancestor is a placeholder — so grouping must key on these, never on a
     *  code parsed back out of the title. Absent on responses that predate it. */
    groupCode?: string | null;
    groupTitle?: string | null;
    distance?: number;
    embeddingText?: string;
    num_issues?: number;
    // Component mode fields
    titleTextbox?: string | null; // UUID of textbox used for title
    idTextbox?: string | null; // UUID of textbox used for ID
    anchors?: {
        title: AnchorProvenance | null;
        id: AnchorProvenance | null;
    };
    destinationName?: string | null; // User-specified destination name
    location?: {
        floors?: string[];
        grid?: string;
    } | null;
    drawingType?: "Plan_View" | "Elevation" | "Section" | "Detail" | "Enlarged_Plan" | null;
    parentUuid?: string | null; // For group hierarchy in component mode
    isGroup?: boolean;
    /** false for a detector seed the sheet has not activated yet (first save or
     *  segmentation approval). Sent on every row; absent only from older servers. */
    isActive?: boolean;
    fullText?: string; // Flattened text of full section tree (notes/tables with children)
    componentType?: string; // Component type: "drawing" | "table" | "note" | "legend"
    tableConfig?: TableConfig | null;
    structuredData?: boolean; // TABLE extracted via the image LLM instead of gridlines
    parsePrompt?: string; // Extra LLM instructions for this component's structured-data parse
    /** Detector-seeded table whose HybridTableParser crop parse found no grid
     *  borders. The paired ai_engine detector consumer writes
     *  section_metadata.parse_failed and /elements serializes it for the badge. */
    parseFailed?: boolean;
    detector?: {
        candidate_id: string;
        box_index: number;
        class: DetectorClass;
        score: number;
    } | null;
    // LLM drawing-segmentation results, present only where segment_floorplan / extract_objects ran.
    // Coordinates are absolute PDF points (divide by page width/height to normalize). See SegmentationOverlay.
    drawingSegmentation?: FloorplanSegmentation | null;
    drawingObjects?: DrawingObjects | null;
}

export interface FloorplanRoom {
    name: string;
    number?: string | null;
    polygon?: [number, number][] | null; // PDF points; null when status !== "ok"
    label_box: [number, number, number, number];
    status: string;
}

export interface FloorplanFixture {
    type: string;
    bbox: [number, number, number, number]; // PDF points
    label?: string | null;
    polygon?: [number, number][] | null; // present for openings
    flagged?: boolean; // slab openings: marked incorrect by a user (QA)
    missing?: boolean; // slab openings: a user-drawn "opening missing here" box (QA)
    false_positive?: boolean; // slab openings: marked "not an opening at all" (QA)
}

interface FloorplanSegmentation {
    version: number;
    rooms: FloorplanRoom[];
    fixtures: FloorplanFixture[];
}

interface DrawingObjectGeometry {
    kind: "point" | "bbox" | "polyline" | "polygon";
    points: [number, number][]; // PDF points
}

export interface DrawingObject {
    object_class: string;
    geometry: DrawingObjectGeometry;
    label?: string | null;
    properties: Record<string, string>;
}

interface DrawingObjectsRegion {
    drawing_type: string;
    region: [number, number, number, number];
    objects: DrawingObject[];
}

interface DrawingObjects {
    version: number;
    regions: DrawingObjectsRegion[];
}

export interface ApiConnection {
    id: string;
    name: string; // Raw connection name from database
    description?: string; // Enriched description for tooltip display (may include destination title)
    srcFileId: string;
    srcBounds: ApiBounds;
    srcGroupCode?: string;
    srcGroupTitle?: string;
    srcUuid?: string; // Source section UUID (nullable for tags without source sections)
    dstUuid?: string | null; // Destination section UUID (nullable for unconnected tags)
    // Enriched fields (only available from GET /connection/{id} or resolved connections)
    dstFileId?: string; // Destination file ID (resolved from dstUuid)
    dstBounds?: ApiBounds; // Destination bounds (resolved from dstUuid)
    dstNodeId?: string | null; // Alias for dstUuid for backward compatibility
    destinationName?: string | null; // User-specified destination name
    // Shape fields
    shape_id?: number | null;
    shape_bbox?: ApiBounds | null; // Shape bounding box using same format as other bounds
    // Textbox associations
    textBoxIds?: string[];
    textboxes?: ApiTextBox[];
    // Taglist association
    taglistId?: string | null;
    // Client-side only
    generatePreview?: boolean; // Whether to generate/show preview image on hover
}

/** Canonical serialized geometry produced by ai_engine Shape.to_dict(). */
export interface TagShapeData {
    type: string;
    [key: string]: unknown;
}

export interface ApiTagShape {
    id: number;
    shape_data: TagShapeData;
    svg: string;
    bbox_width: number;
    bbox_height: number;
    created_at?: string;
    updated_at?: string;
}

export interface ApiTagShapeWithUsage {
    id: number;
    shape_type: string;
    svg: string;
    bbox_width: number;
    bbox_height: number;
    usage_count: number;
}

export interface ApiTagList {
    id: string;
    file_id: string;
    page: number;
    src_uuid?: string | null;
    shape_id?: number | null;
    destination_name?: string | null;
    created_at: string;
    updated_at: string;
}

export interface ApiTagListWithConnections extends ApiTagList {
    connections: ApiConnection[];
}

export interface ApiTextBox {
    id: string;
    file_id: string;
    page: number;
    bounds: ApiBounds;
    text: string;
    size: number;
    font: string;
    flags: number;
    parent_id?: string | null;
    is_multiline: boolean;
    is_from_ocr: boolean;
    ocr_confidence?: number | null;
    children?: ApiTextBox[]; // Child textboxes for multiline boxes
    // Canonical detected geometry. Connection mode compares this against the
    // selected tag shape for desktop-style Shift+A bulk selection.
    shape_data?: TagShapeData | null;
    shape_center?: readonly [number, number] | null;
    // Detected tag shape (tag-detector decoration): complete SVG + normalized
    // bounds. Present only on decorated boxes; drives the text-mode overlay.
    shape_svg?: string | null;
    shape_bbox?: ApiBounds | null;
}

export interface ApiElementsResponse {
    fragment_nodes: ApiFragmentNode[];
    connections: ApiConnection[];
    textboxes?: ApiTextBox[]; // Optional textboxes for the page
}

export interface ApiScheduleActivity {
    id: string;
    taskCode: string;
    taskName: string;
    durationDays: number;
    remainingDurationDays: number | null;
    targetStart: string | null;
    targetEnd: string | null;
    actualStart: string | null;
    actualEnd: string | null;
    earlyStart: string | null;
    earlyEnd: string | null;
    lateStart: string | null;
    lateEnd: string | null;
    baselineStart: string | null;
    baselineEnd: string | null;
    baselineDurationDays: number | null;
    totalFloatDays: number;
    freeFloatDays: number | null;
    isCriticalPath: boolean;
    constraintType: string | null;
    constraintDate: string | null;
    secondaryConstraintType: string | null;
    secondaryConstraintDate: string | null;
    status: string;
    percentComplete: number;
    taskType: string;
    calendarName: string | null;
    predecessors: string[];
    successors: string[];
    wbsPath: string[];
    resources: string[];
    activityCodes: Record<string, string>;
    userFields: Record<string, string | number | null>;
}

// WBS Tree response types (for lazy loading schedule viewer)
export interface ApiWBSNode {
    id: string;
    name: string;
    taskCode: string;
    activityCount: number;
    childCount: number;
    hasChildren: boolean;
    earliestStart: string | null;
    latestEnd: string | null;
    depth: number;
}

export interface ApiWBSTreeResponse {
    nodes: ApiWBSNode[];
    totalActivities: number;
}

export interface ApiWBSChildrenResponse {
    wbsNodes: ApiWBSNode[];
    activities: ApiScheduleActivity[];
}

export interface ApiAncestorNode {
    id: string;
    name: string;
    children: ApiWBSChildrenResponse;
}

export interface ApiActivityResolveResponse {
    activity: ApiScheduleActivity;
    ancestorPath: ApiAncestorNode[];
}

// Schedule project types (for multi-project XER files)
export interface ApiScheduleProject {
    id: string; // section_group UUID
    projectId: string; // P6 project ID (e.g., "105909")
    projectName: string;
    activityCount: number;
    wbsCount: number;
    earliestStart: string | null;
    latestEnd: string | null;
    calendars: string[];
    isLoaded: boolean;
}

export interface ApiScheduleProjectsResponse {
    projects: ApiScheduleProject[];
    totalProjects: number;
}

// camelCase — /api/ai/schedule/revisions returns transformed response
export interface ApiScheduleRevision {
    id: string;
    title: string;
    isActive: boolean;
    createdAt: string | null;
    projectCount: number;
    activityCount: number;
}

export type { ApiActiveSchedule, ApiScheduleSource } from "./activeSchedule";
export type { BacklogGroup, BacklogResponse } from "./externalDocsBacklogTypes";

// Lightweight revision reference attached to constructability issues
export interface ApiIssueRevisionRef {
    id: string;
    name: string;
}

export type IssueReviewVerdict = "real" | "non_material" | "not_real" | "needs_info";

// Why an issue was judged not real — labels model failure modes for training.
// Versioned contract with the backend (ReviewerReason): add values, never rename.
export type IssueReviewerReason =
    | "hallucination"
    | "misread_drawing"
    | "wrong_pair"
    | "other"
    // Precon (deterministic-check) failure modes
    | "rule_error"
    | "extraction_noise"
    | "expected_pattern";

// One reviewer's current verdict on an issue (latest verdict event per reviewer)
export interface ApiIssueReviewVerdict {
    reviewerId: string;
    reviewerName?: string | null;
    reviewerEmail?: string | null;
    verdict: IssueReviewVerdict;
    note?: string | null;
    // Only set on not_real verdicts
    reviewerReason?: IssueReviewerReason | null;
    createdAt: string | null;
}

export type {
    ApiConstructabilityPublicationPreview,
    ApiConstructabilityPublicationResult,
    ApiIssueRfi,
    ApiPublicationSeverityRange,
} from "./constructabilityIssueTypes";

// Admin-only review state attached to an issue (present only in admin payloads)
export interface ApiIssueAdminReview {
    verdicts: ApiIssueReviewVerdict[];
    // null = undecided
    showToCustomer: boolean | null;
    /** Review-screen markers from the false-positive ensemble ("suspicious").
     *  Admin-only by construction: the downgrade does not dismiss the finding,
     *  so it still publishes, and naming our own doubt on a customer's
     *  deliverable is not something the customer payload carries. Optional —
     *  responses that predate it omit it. */
    reviewFlags?: string[];
}

// One entry in an issue's review log (verdict saves, show/hide changes, and
// standalone reviewer notes)
export interface ApiIssueReviewEvent {
    id: string;
    issueId: string;
    eventType: "verdict" | "visibility" | "note";
    reviewerId: string;
    reviewerName?: string | null;
    reviewerEmail?: string | null;
    verdict?: IssueReviewVerdict;
    note?: string | null;
    reviewerReason?: IssueReviewerReason | null;
    showToCustomer?: boolean | null;
    createdAt: string | null;
}

export interface ApiConstructabilityIssue {
    id: string;
    title: string;
    category: string;
    severity: string;
    description: string;
    // Audience-dependent BY DESIGN (ai_engine `build_serialized_issues`):
    // CUSTOMER payloads alias this to the customer number, so a customer only
    // ever sees and sorts by the number they can cite; ADMIN payloads keep the
    // internal allocation number and carry customerNumber alongside. Do not
    // render or export it directly — go through util/issueNumber
    // (customerNumberLabel / reviewLocator / issueHandle), the one boundary
    // that knows which of the two identities it is holding.
    issueNumber: number;
    // Customer-facing number, issued when the issue is first published and
    // immutable afterwards. null = not published yet (admin payloads only —
    // customer payloads contain published rows exclusively). Present on EVERY
    // server payload, which makes its ABSENCE the discriminator for "no server
    // row yet" — see isOptimisticPlaceholder.
    customerNumber?: number | null;
    node1?: ApiFragmentNode;
    node2?: ApiFragmentNode;
    status?: string;
    ballInCourt?: string;
    statusChangedByUser?: ApiUser;
    statusChangedAt?: string;
    createdBy?: ApiUser;
    deleted?: boolean;
    debug_info?: any; // Debug information from the AI agent
    /** Authoring source: "marvin" = drafted by Marvin, approved by the user; null/absent = human-authored. */
    source?: "marvin" | null;
    // Revision the issue's run analyzed
    revision?: ApiIssueRevisionRef | null;
    // The run that last wrote this row; a re-run refreshes rows in place and
    // moves this, which is how per-issue reads (precon members) notice it.
    runId?: string | null;
    // Stable cross-revision identity (display only in debug contexts)
    issueKey?: string | null;
    // Revision that introduced the issue
    detectedAsOfRevision?: ApiIssueRevisionRef | null;
    // Revision whose run no longer detects the issue
    clearedAsOfRevision?: ApiIssueRevisionRef | null;
    // Admin first-pass review state — only present in admin payloads
    adminReview?: ApiIssueAdminReview;
    // Grouping badge — present only on a group REPRESENTATIVE that has members;
    // null/absent on a lone issue and on a member serialized on its own.
    group?: ApiIssueGroup | null;
    // The group this issue belongs to (admin payloads only). A representative
    // points at ITSELF, so `groupId !== id` is what identifies a member — the
    // badge alone cannot, since members carry no `group`.
    groupId?: string | null;
    /** The RFI this issue was sent as (Procore or Autodesk Build); null/absent until it is sent. */
    rfi?: ApiIssueRfi | null;
}

/**
 * The "+N similar" badge on a group representative.
 *
 * `memberCount` is audience-dependent BY DESIGN (ai_engine `_serialize_group`).
 * An ADMIN payload counts every other finding of this conflict and carries
 * `visibleMemberCount` alongside — how many of them the admin has published.
 * A CUSTOMER payload's `memberCount` IS that published count, and
 * `visibleMemberCount` is absent: the raw detection count is a fact about the
 * detector (re-detections of one location inflate it ~19%), not about the
 * building, so nothing the admin has not chosen to publish is ever counted to a
 * customer.
 */
export interface ApiIssueGroup {
    memberCount: number;
    visibleMemberCount?: number;
}

/** GET /workflow/issueFormDefaults: the two list-derived values the new-issue form needs. */
export interface ApiIssueFormDefaults {
    categories: string[];
    nextIssueNumber: number;
}

/** Coverage payload of one precon check (GET /precon_runs/latest). */
interface ApiPreconCoverage {
    type: "complete" | "enumerated";
    keys?: string[];
}

/** Admin per-check block of a precon run summary. */
/**
 * One accounting axis of a check: what it examined and how that resolved.
 * A check whose every axis has `population: 0` examined nothing — it did not
 * run, which is not the same as running and finding nothing.
 */
interface ApiPreconAccountingBucket {
    population: number;
    findings: number;
    explained: number;
    noise: number;
    clean: number;
}

export interface ApiPreconCheckSummary {
    /** Which issue-category tab the check's findings belong on. Declared by the
     *  check module; absent on runs written before the categories existed, in
     *  which case the /precon/checks registry answers for the check id. */
    category?: string | null;
    findings: number;
    explained: number;
    noise_counts: Record<string, number>;
    notes: string[];
    customer_notes: string[];
    coverage: ApiPreconCoverage;
    /** Per-axis ledger of what the check examined. Absent on older runs. */
    accounting?: Record<string, ApiPreconAccountingBucket>;
    created: number;
    updated: number;
    retired: number;
    source_unpublished?: number;
    human_still_visible?: string[];
    never_covered_keys?: string[];
}

/** Full precon run summary — present only in admin payloads. */
export interface ApiPreconRunSummary {
    version: number;
    run_id: string;
    document_id: string;
    revision_id: string;
    workflow_id: string;
    completed_at: string;
    checks: Record<string, ApiPreconCheckSummary>;
    totals: { findings: number; explained: number; noise: number };
}

/**
 * Customer report state — computed live from issue rows by the server, never
 * from raw run counts (which would leak held findings).
 */
type ApiPreconReportState = "all_clear" | "published" | "publication_pending";

/** Redacted per-check coverage for the customer summary strip. */
export interface ApiPreconCheckCustomerView {
    /** See ApiPreconCheckSummary.category — the tab, not a number about the
     *  findings, so the customer payload carries it too. */
    category?: string | null;
    coverage: ApiPreconCoverage | null;
    notes: string[];
}

/**
 * One Drawing document's latest completed precon run (GET /precon_runs/latest).
 * Admin payloads carry `summary`; customer payloads carry the tri-state +
 * published-only lifecycle counts instead.
 */
export interface ApiPreconRunDocument {
    document_id: string;
    run_id: string;
    revision_id?: string | null;
    completed_at?: string | null;
    summary?: ApiPreconRunSummary;
    state?: ApiPreconReportState;
    counts?: { active: number; resolved: number; dismissed: number };
    checks?: Record<string, ApiPreconCheckCustomerView>;
    /**
     * Customer payloads only: per-category state, computed server-side so a
     * held-finding count never crosses the wire. `active` is the VISIBLE count;
     * "review_in_progress" is all a customer learns about held rows.
     */
    categories?: Record<string, { state: ApiPreconCategoryState; active: number }>;
}

export type ApiPreconCategoryState =
    "findings" | "review_in_progress" | "clean" | "partially_checked" | "not_configured";

export interface ApiPreconRunsLatestResponse {
    documents: ApiPreconRunDocument[];
}

/** One member of a precon rollup finding, exactly the `debug_info.children[]`
 *  shape (`PreconRollupChild`) — the server pages the members the persisted
 *  row capped off, so a paged member draws like an inline one. */
export interface ApiPreconMember {
    summary: string;
    item_ref: string;
    evidence: Record<string, unknown>;
    sheet_refs?: {
        code: string;
        title: string;
        section_group_id: string;
        file_id: string | null;
    }[];
    /** The member's persisted preview spec; absent on rows written before it. */
    previews?: PreconMemberPreviews;
}

export interface ApiPreconMembersResponse {
    members: ApiPreconMember[];
    total: number;
    offset: number;
    limit: number;
    /** The run the members belong to. A re-run refreshes a finding row in
     *  place and overwrites its member store, so paging is valid only while
     *  every page's `run_id` equals the first page's; on a mismatch the client
     *  restarts from offset 0. Null when the row itself carries no run. */
    run_id: string | null;
}

/** One raw fixture and the schedule/vector-refined box consumed by fixture_diffuser_clash. */
export interface ApiFixtureClashPlacement {
    file_id: string;
    page: number;
    sheet_id: string;
    role: "mechanical" | "lighting";
    mark: string;
    label: string;
    raw_bbox_pt: [number, number, number, number];
    clash_bbox_pt: [number, number, number, number];
    geometry: "drawn" | "detected";
    nominal_in: [number, number];
}

/** A candidate sheet pair the check never compared, and why it stopped.
 *
 * `pair` is one label ("M-1.1 x EL-1.1"), not a per-side split: a floor refused
 * for carrying two mechanical sheets names a group ("M-1.1/M-1.2 x EL-1.1"). */
export interface ApiFixtureClashUnavailablePair {
    pair: string;
    reason: string;
}

export interface ApiFixtureClashGeometryResponse {
    document_id: string;
    revision_id: string;
    placements: ApiFixtureClashPlacement[];
    /** Non-empty means zero placements can mean "unreadable", not "nothing here". */
    unavailable: ApiFixtureClashUnavailablePair[];
}

/** A flagged location on a shop sheet (shop_flag table — any producer). */
export interface ApiShopFlag {
    id: string;
    file_id: string;
    section_group_id: string;
    /** Producer: "detailer_text" now; "cloud", "spec_check", "manual" later. */
    source: string;
    kind: string;
    title: string;
    text: string;
    location: ApiBounds;
    flag_metadata?: { rfi_number?: number } | null;
    /** Revision this flag first appeared in (inherited across re-submittals). */
    introduced_revision_id?: string | null;
    created_at: string | null;
    /** Set when the user dismissed the flag; dismissal is inherited across
     * re-submittals and reversible (reinstate). */
    dismissed_at?: string | null;
}

export interface ApiComment {
    id: string;
    entity_type: string;
    entity_id: string;
    user_id: string;
    user_name?: string;
    user_email?: string;
    content: string;
    created_at: string;
    updated_at: string;
    is_deleted: boolean;
    can_edit: boolean;
    can_delete: boolean;
    /** Authoring source: "marvin" = drafted by Marvin, approved by the user; null/absent = human-authored. */
    source?: "marvin" | null;
}

/**
 * Deep Search suggestion from AI classifier
 */
interface DeepSearchSuggestion {
    reason: string;
    transformedQuery: string | null;
}

/**
 * Tool suggestion from AI classifier
 */
export interface ToolSuggestion {
    toolId: string;
    reason: string;
    args: Record<string, string> | null;
}

export interface ApiConversation {
    id: string;
    createdAt: Date;
    updatedAt: Date;
    user: ApiUser;
    title: string;
    messages: ApiMessage[];
    documentFilters?: string[];
    /**
     * Context-meter seed (same shape as the live `context_stats` stream event data).
     * `windowTokens` — what the ring reads — is the server-side PREDICTION of the next turn's
     * starting window (fixed prefix + fresh history), and `conversationTokens` the recomputed
     * history alone; both are recomputed on every load, so they're up to date (and the
     * SHRUNKEN values right after a compaction, not null). The per-hop nerd stats
     * (inputTokens/…) come from the newest run's canonical context_stats part and are
     * omitted when a compaction checkpoint is newer than that run. Null only when there's
     * no history to seed at all.
     */
    contextStats?: ContextStatsEventData | null;
    /** Set only on a turn read (`GET /conversation/{id}?turn_message_id=`): `messages`
     *  then holds that finished turn and anything written after it, not the thread. */
    turnMessageId?: string;
    /** Latest Marvin markdown reply, server-truncated to 200 chars; absent
     * when the conversation has no Marvin reply yet. List-response only
     * (GET /api/ai/conversation) — for the Questions rail's row snippet. */
    lastMessagePreview?: string;
}

/** A frozen, read-only snapshot of a conversation shared with same-tenant teammates.
 * `conversation` is the exact same shape as the normal get-conversation response, with
 * messages already filtered to the snapshot server-side. */
export interface SharedConversationResponse {
    conversation: ApiConversation;
    sharedBy: ApiUser;
    /** ISO timestamp of when the snapshot was shared. */
    sharedAt: string;
}

export interface ApiMessage {
    id: string;
    createdAt: Date;
    message: string;
    messageType: string;
    references: Map<string, ApiReference> | null;
    user: ApiUser;
    conversationId: string;
    parentId: string | null;
    files: ApiFile[];
    fragmentNode?: ApiFragmentNode;
    response?: ApiMessage | null;
    quality?: string | null;
    feedback?: string | null;
    requestId?: string | null;
    status?: "ok" | "cancelled" | "error" | "streaming";
    deepSearchSuggestion?: DeepSearchSuggestion;
    toolSuggestions?: ToolSuggestion[];
    /** Artifacts generated by Marvin, persisted alongside the message. Also carried as
     * `artifact` parts; retained for legacy/pre-parts messages and non-part consumers. */
    artifacts?: ArtifactMeta[];
    /**
     * The canonical ordered message parts (V2), hydrated by the backend from the message's
     * `message_part` rows. The SINGLE source the renderer reads for a fetched Marvin
     * message: text / activity / reference / question / action / artifact / navigation /
     * memory_notice / context_stats parts, byte-identical to what the live stream produced.
     * Absent on legacy (pre-parts) messages — the renderer synthesizes parts from `message`
     * + `trace` in that case.
     */
    parts?: ApiMessagePart[];
    usage?: { input_tokens: number; output_tokens: number; total_tokens: number };
    /**
     * Activity trace persisted by the client at stream end (display-only). Legacy fallback:
     * a pre-parts message with no `parts` reproduces its streaming view from this + `message`.
     * Absent on the oldest messages — render then falls back to flat.
     */
    trace?: ProgressLine[];
    /**
     * Run request persisted in message metadata by POST /runs (Marvin messages) and
     * retained after completion. Source of truth for the region-attachment chips
     * shown under the parent user message in the thread history.
     */
    runParams?: ApiRunParams;
    /**
     * True on context-compaction checkpoint messages. The backend stores this under
     * `message_metadata._contextCheckpoint` but SPREADS message_metadata onto the top
     * level of the serialized message (same as `usage`/`trace`), so on the wire it is a
     * top-level flag. The internal summary stays hidden; Messages.tsx renders a friendly
     * checkpoint notice in its chronological place instead.
     */
    _contextCheckpoint?: boolean;
    /** Miranda runs started from this admin-chat turn, by run id (queued, running, completed, failed, cancelled). */
    mirandaRuns?: Record<string, string>;
    /** The author's saved edits of this answer's RFI cards, by form id (spread from message_metadata). */
    rfiForms?: Record<string, MarvinRfiFormEdit>;
    /** Set on the Marvin notice message the Miranda worker appends when a run finishes. */
    mirandaRunNotice?: { runId: string; status: string; [key: string]: unknown };
    completedAt?: Date;
}

export const FULL_PAGE_BOUNDS: ApiBounds = { page: 0, xMin: 0, yMin: 0, xMax: 1, yMax: 1 };

/** A user-drawn rectangle on a sheet/PDF that a Marvin question is about (normalized [0,1] coords). */
export interface ApiSheetRegion extends ApiBounds {
    fileId: string;
    /** Display label, e.g. 'Region on A-101'. Round-trips through the persisted
     *  runParams metadata so attachment history can render it. */
    label?: string;
}

/** Run parameters ai_engine persists in message metadata (spread top-level onto the
 *  message API payload) and retains after the run completes. Only the fields the
 *  frontend renders are typed; the payload also echoes the rest of the run request. */
export interface ApiRunParams {
    sheetRegions?: ApiSheetRegion[] | null;
    /** Legacy single-region field from older clients. */
    sheetRegion?: ApiSheetRegion | null;
}

/** GET /api/ai/overlay/align response envelope (transform entries stay loose —
 * the overlay session narrows them to OverlayDetailPair). */
export interface ApiOverlayAlignResponse {
    transforms?: Record<string, unknown>[];
    /** Response-level fallback; prefer the per-transform aspect_aware. */
    aspect_aware?: boolean;
    /** True when the base and overlay sheets live in different documents. */
    cross_document?: boolean;
    /** True when the base and overlay sheets are different revisions of the
     * same sheet slot (document + group_code + area). Optional: older
     * servers omit it and the client falls back to its own detection. */
    same_slot_revision?: boolean;
}

/** One drawing covering a queried sheet region, placed in the virtual grid space. */
export interface ApiRegionCoverageHit {
    file_id: string;
    group_code: string;
    group_title: string;
    section_uuid: string;
    title: string;
    /** Fraction of the queried region inside this drawing's footprint. */
    coverage: number;
    confidence: number;
    axis_unverified: boolean;
    same_floor: boolean | null;
    /** The queried region in this drawing's own normalized page coords. */
    region_in_sheet: [number, number, number, number];
    /** This drawing's footprint in the queried sheet's normalized page coords. */
    footprint_on_base: [number, number, number, number];
    /** This drawing's full page ([0,1]^2) in the queried sheet's normalized page coords. */
    page_on_base: [number, number, number, number];
}

export interface ApiRegionCoverage {
    sheet_uuid: string;
    region: number[];
    covering: ApiRegionCoverageHit[];
    /** Present when the query could not be answered (space not built / sheet unplaced). */
    reason?: string;
}

export interface SectionLeafContent {
    id: string;
    title: string;
    text?: string;
    hId: string;
    bounds: ApiBounds;
    hasChildren: boolean;
}

interface TableRowKey {
    /** Display string: multi-part as "[SS|1]", single-part as "SS-1". */
    key: string;
    /** True if an incoming connection matches this specific key. */
    connected: boolean;
}

export interface TableRowSection extends SectionLeafContent {
    /** True if any connection (across any sheet) has this row as its destination. */
    connected: boolean;
    /** Extracted keys for this row (present for table rows); connection mode
     *  renders one colored label per key. */
    keys?: TableRowKey[];
    /** Parse-time "HEADING: value" lines (generate_section) — what the desktop
     *  tooltip shows; drives the row-values tooltip. */
    description?: string;
    /** Sheet-destination details only: the twin whose incoming connections make
     *  the shared key connected — where a chip click finds the references. */
    referenceId?: string | null;
}

export type ValidationIssue<K extends string> = { key: K; label: string };

interface ControlTowerCompanyOption {
    label: string;
    count: number;
}

interface ControlTowerCustomFieldOption {
    title: string;
    values: string[];
}

export interface ControlTowerFilterOptions {
    rfiHydration?: { total: number; ready: number; remaining: number };
    companies: ControlTowerCompanyOption[];
    rawStatuses: string[];
    customFields: ControlTowerCustomFieldOption[];
}

export interface MarvinDetailReferencesWidgetDirective {
    type: "detail_references";
    reference_index: number;
}

interface MarvinProjectControlsMetric {
    key: string;
    label: string;
    value: string;
}

export interface MarvinProjectControlsSeriesPoint {
    label: string;
    opened?: number;
    closed?: number;
    open?: number;
    late?: number;
    late_pct?: number | null;
}

interface MarvinProjectControlsBreakdown {
    dimension: string;
    rows: Array<{
        label: string;
        count: number;
        open: number;
        late: number;
        avg_open_age_days?: number | null;
    }>;
}

export interface MarvinProjectControlsRow {
    id: string;
    kind: string;
    number: string;
    title: string;
    summary?: string;
    status?: string;
    company?: string;
    ballInCourt?: string;
    ballInCourtName?: string;
    ballInCourtCompany?: string;
    openedBy?: string;
    openedByCompany?: string;
    openedAt?: string | null;
    dueAt?: string | null;
    overdueDays?: number | null;
    sourceUrl?: string;
    trades?: string[];
}

export interface MarvinProjectControlsWidgetDirective {
    type: "project_controls";
    title: string;
    summary: string;
    intent: string;
    kinds: string[];
    timeRange: {
        preset?: string;
        from: string;
        to: string;
        granularity?: string;
    };
    metrics: MarvinProjectControlsMetric[];
    series: MarvinProjectControlsSeriesPoint[];
    breakdowns: MarvinProjectControlsBreakdown[];
    rows: MarvinProjectControlsRow[];
    // Readable query string (no leading "?") that reproduces this result via the Control Tower
    // /query endpoint, so the widget can be opened and bookmarked as a standalone report.
    reportQuery?: string;
}

// Artifacts arrive as `artifact_ready` SSE events (see ArtifactMeta), not ::widget tokens;
// the ::widget path handles detail_references, project_controls, dig_deeper, rfi_form.
export type { MarvinWidgetDirective } from "./marvinWidgets";

export interface ApiPaginatedConnections {
    items: ApiConnection[];
    offset: number;
    limit: number;
    hasMore: boolean;
    total: number;
}

export interface ApiDetailReferenceGroup {
    id: string;
    sheetCode: string;
    sheetTitle: string;
    fileId: string;
    bounds: ApiBounds[];
    referenceCount: number;
}

export interface ApiReferenceGroupsPage {
    groups: ApiDetailReferenceGroup[];
    offset: number;
    limit: number;
    hasMore: boolean;
    total: number;
    totalReferences: number;
}

/**
 * Resolved-connection counts for a destination table/sheet, keyed by destination
 * section uuid in the /document/{id}/destination_connection_counts response.
 * `connections` is the count to this target; `total` is the sum across all
 * destinations sharing the name, so `total > connections` exactly when the name
 * is shared by more than one destination.
 */
export interface ApiDestinationConnectionCount {
    connections: number;
    total: number;
}

export interface ApiScheduleSearchInfo {
    task_name?: string;
    task_code?: string;
    target_start?: string;
    target_end?: string;
    duration_days?: number;
    total_float_days?: number;
    is_critical_path?: boolean;
    status?: string;
    percent_complete?: number;
    wbs_name?: string;
}

/** Human-display text from the API: markdown is already projected out server-side
 * (ai_engine `utils.to_plain_text`). Render verbatim — never markdown-parse, never
 * re-sanitize. Advisory brand: documents intent without forcing casts. */
type PlainText = string;

export interface ApiSearchResult {
    fragment_node_id: string;
    file_id: string;
    title: PlainText;
    hierarchy_id: string;
    page: number;
    similarity_score: number;
    explanation?: PlainText;
    /** Provenance of `explanation`: "quote" is verbatim document text (the only kind
     * the query may be highlighted inside), "summary" is LLM prose about the section.
     * Absent for structured snippets (schedule metadata) — render plain, never highlighted. */
    snippet_kind?: "quote" | "summary";
    bounds?: ApiBounds;
    section_group?: ApiSectionGroup;
    is_title_match?: boolean;
    document_type?: string;
    /** Canonical type from the search API: sheet | spec | schedule | rfi | submittal | document. */
    result_type?: string;
    /** Title of the containing Document — the result-card title for text results
     * (section titles alone read as bare locators like "Line 3"). */
    source_document_title?: PlainText;
    /** Heading trail above the hit (closest heading last), locator junk pre-filtered. */
    section_path?: PlainText[];
    /** LLM relevance score (0–10) merged in client-side from the `explanation` SSE
     * event; undefined until the result has been scored. */
    relevance_score?: number;
    /** Retrieval methods that surfaced this hit ("fulltext" / "fuzzy" / "identifier" /
     * "semantic") — powers the "Related" (embedding-only) card marker. */
    match_methods?: string[];
    schedule_info?: ApiScheduleSearchInfo;
    /** Documents-layer folder names, root → containing folder; present only for
     * Documents-layer docs. An array so folder names may contain any character. */
    folder_path_parts?: string[];
    /** Display form of folder_path_parts ("A / B") — kept for the legacy search UI. */
    folder_path?: string;
    /** Documents-layer folder UUID, for "reveal in folder" navigation. Present only for Documents-layer docs. */
    folder_id?: string;
}

/** An `external_result` SSE event payload — a synced-but-not-yet-imported file matching the search query. */
export interface ApiExternalSearchResult {
    id: string;
    file_name: string;
    source: string;
    status: string;
    revision_group_id: string | null;
}

export interface TableConfig {
    columns?: {
        name: string;
        type: "key" | "data";
        // The edit dialog saves the parser as its UI name string (+ pattern);
        // the describe pipeline persists a serialized parser object. Readers
        // must accept both (mirrors the backend's _parser_config_from_column).
        parser?:
            string | { type?: string; pattern?: string; separator?: string; substitution?: string };
        pattern?: string;
    }[];
    excludedKeys?: string[];
    destinationMapping?: Record<string, string>;
    // Parse-time directive: first column becomes the header row. Applied during
    // describe (re-extracts cells flipped); preview/save carry it through.
    transposed?: boolean;
    // Exact-match rewrites applied to raw key-column cell text before parsing.
    // Key is the whitespace-normalised, uppercased single-part form.
    keyRewrites?: Record<string, string>;
    // Connection-time tag word-split separators. When set, callout tags are split by
    // these separators and each word is matched against the key column, so e.g. a tag
    // `3" FS-1` connects to schedule key `FS-1`. null/absent = no word-split.
    connectWordSeparators?: string[] | null;
}

/**
 * Response of ``POST /section/{uuid}/preview_table_keys`` — keys extracted from
 * a table section under a candidate tableConfig (pure server-side computation,
 * no DB writes). Drives the live "Extracted Keys" list in the table-edit
 * dialog. stalePersistedKeys are keys in the persisted connection map that a
 * save with this config would no longer write — surfaced so the dialog can
 * offer to exclude them from future matching.
 */
export interface ApiTableKeysPreview {
    extractedKeys: string[];
    excludedKeys: string[];
    totalRows: number;
    stalePersistedKeys?: string[];
    /** Omitted by older ai_engine versions that require re-describe on transpose save. */
    atomicConfigRefresh?: boolean;
}

export interface ApiDisconnectPreviewBucket {
    /** Leading alphabetic prefix of the sheet's group code, uppercased (e.g. "ED102" -> "ED"). */
    discipline: string;
    /** The sheet's group code (e.g. "E102"). */
    group_code: string;
    /** Tag-shape registry index; -1 is the shapeless bucket. */
    shape_id: number;
    connections: number;
}

/**
 * Per (sheet group_code, shape) breakdown of the ACTIVE connections a
 * document-scope table disconnect would touch. Powers the cascading filter
 * pickers in the Disconnect Table dialog — the client aggregates the buckets
 * locally (disciplines, per-shape counts) so the cascade needs no refetch.
 * Shape geometry is not shipped; thumbnails come from the client's shape cache.
 */
export interface ApiDisconnectTablePreview {
    destination_name: string;
    total_connections: number;
    buckets: ApiDisconnectPreviewBucket[];
}

export interface ComponentDescription {
    componentId: string;
    response: string;
    type: string;
    model?: string | null;
}

export interface ComponentDescriptionUpdate {
    response: string;
}

export interface DescribeJobStatus {
    status: "queued" | "running" | "succeeded" | "failed" | "unknown";
    error?: string;
}

export interface TableCellOut {
    rowIndex: number;
    colIndex: number;
    content: string;
    isHeader: boolean;
    rowSpan: number;
    colSpan: number;
    bounds: [number, number, number, number] | null;
}

export interface ApiRfiReview {
    id: string;
    created_at: Date;
    file_id: string;
    referenced_files: ApiFile[];
    input_rfi_question?: string;
    is_rfi_recommended?: boolean;
    recommendation_text?: string;
    recommendation_text_references?: Record<string, ApiReference>;
    draft_text?: string;
    draft_text_references?: Record<string, ApiReference>;
    /** Who asked for the review; only they may write its draft (a shared review aside). */
    user_id?: string;
    /** An integration wrote it from register data: every member's to open and draft from. */
    source_integration_object_id?: string | null;
    /** Whether this reader may write its draft, by the server's own rule (author, or a shared review). */
    may_write_draft?: boolean;
    /**
     * Wording in it named RFIs this reader cannot open: `recommendation_text` or `draft_text`
     * reads `RFI_WITHHELD`, and the recommendation flag is left out.
     */
    withheld?: boolean;
    /** The areas the review read (its `documentFilters`' area tokens); empty: every area. */
    areas?: ApiAreaRef[];
}

/**
 * Parsing
 */
export interface ContentPart {
    isReference: boolean;
    rawContent: string;
}

export type Cell = ContentPart[];

/**
 * Integrations
 */
export enum IntegrationType {
    ACC = "acc",
    PROCORE = "procore",
    SHAREPOINT = "sharepoint",
}

export interface ApiIntegration {
    /** Integration row UUID — keys the project-files/sync-prefs endpoints (PP-1063). */
    id?: string;
    type: IntegrationType;
    config: Record<string, any>;
}

/** PP-1083 SharePoint — the two D3 revision conventions the backend accepts. */
export type SharePointRevisionConvention = "folder_per_issue" | "flat";

/** A completed SharePoint connect: the pending integration row exists. */
export interface SharePointConnectResult {
    success: boolean;
    integration_id: string;
    site_id: string;
    site_name: string;
}

/** The customer admin's one-time setup for a SharePoint site (POST /integrations/sharepoint/setup). */
export interface SharePointSetup {
    site_url: string;
    /** The site's Microsoft 365 tenant; null when only a recorded grant bound it. */
    tenant_id: string | null;
    /** Graph PowerShell: approve Primepoint with Sites.Selected, grant read on the site. */
    script: string;
}

/** PP-1063 Project Files Sync — per-integration What-to-sync checkbox state. */
export type ApiSyncPrefs = Record<string, boolean>;

export type ApiFileSyncMode = "everything" | "selected";

export interface ApiFolderMapping {
    id: string;
    sourceFolderId: string;
    sourceFolderPath: string;
    destination: string;
    /** An RFIs folder's statement about its RFIs: "closed" closes every RFI from it. */
    rfiStatus?: string | null;
    isActive: boolean;
    generation: number;
    updatedAt: string | null;
    /** In-scope ledger counts under this folder's subtree, by sync state. */
    syncedPdfCount?: number;
    pendingCount?: number;
    failedCount?: number;
    /** Ingestion counts for the subtree's synced files (binding states). */
    ingestedCount?: number;
    ingestPendingCount?: number;
    ingestProcessingCount?: number;
    /** Spec revisions parked in human review — waiting, not executing. */
    awaitingReviewCount?: number;
    ingestFailedCount?: number;
}

/** Latest sync-run state for the Sync-now chip and per-category Status column. */
interface ApiSyncJobState {
    jobStatus: "todo" | "doing" | "succeeded" | "failed" | null;
    scheduledAt: string | null;
}

export interface ApiProjectFilesSyncStatus extends ApiSyncJobState {
    lastSyncAt: string | null;
    /** What-to-sync category key -> that category's latest job state. */
    categories?: Record<string, ApiSyncJobState>;
    /** Newest recorded export run, whatever its lane. `lastSyncAt` only moves when a
     * file lands, so this is what proves a run over an empty library happened. */
    lastRun?: {
        lane: string;
        outcome: string | null;
        startedAt: string;
        finishedAt: string | null;
        itemsSeen: number | null;
    } | null;
}

export interface ApiFileSyncConfig {
    integrationId: string;
    /** Projected from the What-to-sync checkbox (single source of truth). */
    enabled: boolean;
    mode: ApiFileSyncMode;
    generation: number;
    configured: boolean;
    mappings: ApiFolderMapping[];
    syncPrefs: ApiSyncPrefs;
    allowedDestinations: string[];
}

/** PP-1063 part 2 — member-facing Provider Files (documents-view sections). */
export interface ApiProviderFileSection {
    integrationId: string;
    provider: "acc" | "procore" | "sharepoint";
    label: string;
    syncedCount: number;
    ingestedCount: number;
}

export interface ApiProviderTreeFolder {
    path: string;
    name: string;
    parentPath: string | null;
    directFileCount: number;
}

export interface ApiProviderTree {
    integrationId: string;
    rootFileCount: number;
    /** Most recent synced_at across the tree's files; null before the first sync. */
    lastSyncedAt?: string | null;
    totalFileCount: number;
    folders: ApiProviderTreeFolder[];
}

type ProviderFileStatus =
    "synced" | "processing" | "awaiting_review" | "published" | "failed" | "outdated";

export interface ApiProviderFile {
    revisionId: string;
    fileName: string | null;
    title: string | null;
    status: ProviderFileStatus;
    documentId: string | null;
    destination: string;
    fileSize: number | null;
    sourceUpdatedAt: string | null;
}

export interface ApiIngestStatus {
    counts: Partial<Record<ProviderFileStatus, number>>;
    syncedTotal: number;
    /** Synced rows stamped under an older mapping generation — sync before ingesting. */
    staleRouteCount?: number;
    pending: number;
    lastIngest: {
        status?: "queued" | "running" | "completed" | "failed";
        total?: number;
        completed?: number;
        remaining?: number;
        currentFile?: string | null;
        failed?: number;
        failures?: Array<{ file: string; error: string }>;
        /** Folder subtree the run was scoped to; null/absent = all pending. */
        scopeFolderPath?: string | null;
        error?: string;
    } | null;
}

export interface ApiProviderFolder {
    id: string;
    name: string;
    path: string;
    hasSubfolders: boolean;
}

export interface ApiFolderListing {
    /** False when the connected provider account cannot see the files module at all. */
    filesAccess: boolean;
    directPdfCount: number;
    directNonPdfCount: number;
    folders: ApiProviderFolder[];
}

export interface ExternalFilesPagination {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
}

export interface ExternalFileRevisionGroup extends RevisionGroupImportState {
    bindingVerified?: boolean;
    contentPresent?: boolean;
    linkedFileCount?: number | null;
    linkedCurrentFileCount?: number | null;
    inconsistentLinkCount?: number | null;
    contentReadiness?: {
        expected: number | null;
        ready: number | null;
        failed: number | null;
    } | null;
    integrationType: string;
    projectId: string;
    projectName: string | null;
    fileCategory: string;
    revisionGroupKey: string;
    externalRevisionGroupId: string | null;
    externalRevisionGroupName: string | null;
    externalRevisionName: string | null;
    fileCount: number;
    /** Zero means every file is superseded; import explicitly as history. */
    currentFileCount?: number | null;
    syncedCount: number;
    newestSyncedAt: string | null;
    newestSourceUpdatedAt: string | null;
    /** When the group's newest content landed at the provider — upload, not issuance
     * (issue dates are routinely backdated to the date printed on the sheet). */
    newestSourceCreatedAt?: string | null;
    issueDate: string | null;
    /** A revision group can span multiple areas — Procore doesn't scope one to a building. */
    areaNames: string[];
}

type ExternalFileMatchStatus = "unmatched" | "candidate" | "matched" | "imported";

export interface ExternalFileRevision {
    id: string;
    integrationId: string;
    integrationType: string;
    projectId: string;
    projectName: string | null;
    providerObjectType: string;
    fileCategory: string;
    externalFileId: string;
    externalRevisionId: string;
    externalRevisionName: string | null;
    externalRevisionGroupId: string | null;
    externalRevisionGroupName: string | null;
    revisionGroupKey: string;
    fileNumber: string | null;
    fileName: string | null;
    title: string | null;
    discipline: string | null;
    areaId: string | null;
    areaName: string | null;
    issueDate: string | null;
    sourceCreatedAt: string | null;
    sourceUpdatedAt: string | null;
    isCurrent: boolean | null;
    isDeleted: boolean;
    blobPath: string | null;
    versionedBlobPath: string | null;
    fileSize: number | null;
    contentHash: string | null;
    pageCount: number | null;
    syncStatus: string;
    syncJobId: string | null;
    firstSeenAt: string | null;
    lastSeenAt: string | null;
    syncedAt: string | null;
    localFileId: string | null;
    localSectionGroupId: string | null;
    matchStatus: ExternalFileMatchStatus | null;
    matchConfidence: number | null;
    fileUrl?: string;
    /** Provider-specific extra fields (e.g. the spec lane's stable `section_id`) —
     * only present when the request opts in via `includeSourceMetadata`. */
    sourceMetadata?: Record<string, unknown> | null;
}

export interface ExternalFileRevisionGroupsResponse {
    success: boolean;
    revisionGroups: ExternalFileRevisionGroup[];
    pagination: ExternalFilesPagination;
}

/** A named Revision on the canonical drawing document — the Primepoint side of the connect UI. */
export interface LocalRevision {
    id: string;
    name: string;
    status: string;
    createdAt: string | null;
    publishedAt: string | null;
    externalDate: string | null;
    /** Set once this revision was connected to (or imported from) an external revision group. */
    revisionGroupKey: string | null;
    revisionGroupName: string | null;
    areaNames: string[];
    /** Latest persisted import validation; present for provider spec imports. */
    validation: SpecValidationReport | null;
}

export interface LocalRevisionsResponse {
    success: boolean;
    revisions: LocalRevision[];
}

export interface ExternalDocsBaseline {
    revisionGroupKey: string;
    revisionGroupName: string | null;
    baselineDate: string | null;
    setAt: string;
}

export interface SetBaselineResponse {
    success: boolean;
    baseline: ExternalDocsBaseline;
}

export interface AcknowledgeBacklogResponse {
    success: boolean;
    acknowledgedGroupKeys: string[];
}

export interface ImportBacklogResponse {
    success: boolean;
    scheduled: boolean;
    jobId?: number;
    alreadyQueued?: boolean;
}

export interface ExternalFileRevisionsResponse {
    success: boolean;
    files: ExternalFileRevision[];
    pagination: ExternalFilesPagination;
}

export interface ExternalFileUrlResponse {
    success: boolean;
    id: string;
    fileUrl: string;
}

export interface ExternalFileLocalMatchesResponse {
    success: boolean;
    externalFileRevisionId: string;
    matches: ApiSectionGroup[];
}

export interface ImportRevisionGroupResponse {
    success: boolean;
    revisionId: string;
    revisionName: string;
    documentId: string;
    importedSheets: number;
    /** Sheets that became the active copy. The rest arrived already superseded by a
     * more current import of the same drawing and stay as historical revisions. */
    activatedSheets: number;
    failedSheets: number;
    skippedDuplicateSheets: number;
    /** Synced rows excluded because they fall outside the configured project collection scope. */
    skippedOutOfScopeSheets: number;
    /** Reused sheets whose provider area changed but could not be moved without a slot conflict. */
    areaMoveBlockedSheets: number;
    workflowId: string | null;
    enrichmentScheduled: boolean;
}

/**
 * Additive per-import validation surfaced by the spec import lane
 * (`import_spec_revision_group`) — the drawing lane has no analogous check
 * pipeline and never returns this field.
 */
export type SpecValidationStatus = "pass" | "warning" | "failed";

type SpecValidationCheckCode =
    | "snapshot"
    | "csi_numbers"
    | "slot_collisions"
    | "area_resolution"
    | "heading_detection"
    | "activation";

export interface SpecValidationCheck {
    code: SpecValidationCheckCode;
    label: string;
    status: SpecValidationStatus;
    detail: string;
    count: number;
}

interface SpecValidationAreaResolution {
    override: number;
    sticky: number;
    seed: number;
    unassigned: number;
}

type SpecValidationSectionFlagCode = "raw_text_fallback" | "heading_fallback" | "empty_text";

export interface SpecValidationSectionFlag {
    sectionId: string;
    groupCode: string;
    title: string;
    code: SpecValidationSectionFlagCode;
    detail: string;
}

interface SpecValidationSummary {
    selected: number;
    imported: number;
    activated: number;
    failed: number;
    qualityFlags: number;
}

export interface SpecValidationReport {
    status: SpecValidationStatus;
    summary: SpecValidationSummary;
    checks: SpecValidationCheck[];
    areaResolution: SpecValidationAreaResolution;
    sectionFlags: SpecValidationSectionFlag[];
}

/** Spec lane's counterpart to `ImportRevisionGroupResponse` — counts sections,
 * not sheets, and carries the additive validation report the drawing lane
 * doesn't produce. Both shapes come back from the same POST
 * /integrations/files/import endpoint, selected by the request's `category`. */
export interface ImportSpecRevisionGroupResponse {
    success: boolean;
    revisionId: string;
    revisionName: string;
    documentId: string;
    importedSections: number;
    activatedSections: number;
    failedSections: number;
    areaMoveBlockedSections: number;
    workflowId: string | null;
    enrichmentScheduled: boolean;
    resultSectionGroupIds: Record<string, string>;
    validation?: SpecValidationReport;
}

/** POST /integrations/files/import: the accepted shape for drawings, the inline spec summary for
 * specifications (by `category`); narrow with `"status" in result` / `"importedSections" in result`. */
export type ImportAnyRevisionGroupResponse =
    ImportAcceptedResponse | ImportRevisionGroupResponse | ImportSpecRevisionGroupResponse;

/**
 * GET /integrations/spec-revision-comparison — deterministic (no LLM call in
 * the request path) comparison of two Primepoint spec revisions, matched by
 * (canonical group_code, area_id).
 */
interface SpecRevisionComparisonRef {
    id: string;
    name: string;
}

export type SpecRevisionChangeKind = "added" | "modified" | "removed" | "unchanged";

interface SpecRevisionComparisonDiff {
    addedLines: number;
    removedLines: number;
    baseExcerpt: string;
    targetExcerpt: string;
}

interface SpecRevisionComparisonSection {
    groupCode: string;
    title: string;
    areaId: string | null;
    areaName: string | null;
    changeKind: SpecRevisionChangeKind;
    baseSectionGroupId: string | null;
    targetSectionGroupId: string | null;
    diff: SpecRevisionComparisonDiff;
    narrative: string;
}

interface SpecRevisionComparisonSummary {
    added: number;
    modified: number;
    removed: number;
    unchanged: number;
}

export interface SpecRevisionComparisonResponse {
    success: boolean;
    baseRevision: SpecRevisionComparisonRef;
    targetRevision: SpecRevisionComparisonRef;
    summary: SpecRevisionComparisonSummary;
    sections: SpecRevisionComparisonSection[];
}

/**
 * Externally managed docs — Reports drill-downs
 */
export type ExternalDocsRevisionParityStatus = "current" | "behind" | "ahead" | "unknown";

export interface ExternalDocsRevisionDetailRow {
    revisionGroupKey: string;
    fileCategory: string;
    externalRevisionName: string | null;
    externalRevisionGroupName: string | null;
    newestSourceUpdatedAt: string | null;
    issueDate: string | null;
    localRevisionId: string | null;
    localRevisionName: string | null;
    localRevisionUpdatedAt: string | null;
    syncedCount: number;
    fileCount: number;
    status: ExternalDocsRevisionParityStatus;
}

export interface ExternalDocsRevisionDetailResponse {
    success: boolean;
    rows: ExternalDocsRevisionDetailRow[];
}

/** `sense=synced_not_linked` reuses the existing `list_files`-shaped response. */
export type ExternalDocsMissingSyncedNotLinkedResponse = ExternalFileRevisionsResponse;

/**
 * `sense=not_synced` — ledger rows discovered at the source but not yet synced (pending/
 * failed, from the last sync job). Snapshot-only, so `available` is always `true`; kept
 * on the wire for shape parity with the (now-retired) live-check-gated version.
 */
export interface ExternalDocsMissingNotSyncedResponse extends ExternalFileRevisionsResponse {
    available: boolean;
}

export interface ExternalDocsAreaRow {
    externalId: string;
    name: string | null;
    fileCount: number;
    syncedCount: number;
    localSheetCount: number;
    areaUuid: string | null;
}

export interface ExternalDocsAreasResponse {
    success: boolean;
    areas: ExternalDocsAreaRow[];
}

/**
 * Externally managed docs — collection ↔ area mapping (bridging provider
 * collections/areas onto local KG Areas via `area.source_key`).
 */
type AreaMappingTier =
    "bridged" | "manual" | "name" | "initials" | "merge" | "ambiguous" | "unmatched";

/** One provider collection/area with its proposed (or saved) local Area bridge. */
export interface AreaMappingRow {
    sourceKey: string;
    externalId: string;
    name: string | null;
    fileCount: number;
    tier: AreaMappingTier;
    areaUuid: string | null;
    areaName: string | null;
}

interface LocalAreaOption {
    uuid: string;
    name: string;
    sourceKey: string | null;
    sheetCount: number;
}

export interface AreaMappingsResponse {
    success: boolean;
    mappings: AreaMappingRow[];
    localAreas: LocalAreaOption[];
}

interface ApplyAreaMappingResult {
    sourceKey: string;
    areaUuid: string;
    success: boolean;
}

export interface ApplyAreaMappingsResponse {
    success: boolean;
    stamped: number;
    results: ApplyAreaMappingResult[];
}

/**
 * Externally managed docs — per-sync-run reports
 */
export interface ExternalDocsSyncRunListItem {
    jobId: number | null;
    jobName: string;
    integrationType: string;
    startedAt: string | null;
    completedAt: string | null;
    durationSeconds: number | null;
    category: string | null;
    status: string;
    metrics: Record<string, number> | null;
}

export interface ExternalDocsSyncRunsResponse {
    success: boolean;
    runs: ExternalDocsSyncRunListItem[];
    pagination: ExternalFilesPagination;
}

export interface ExternalDocsSyncRunFileRow {
    externalFileRevisionId: string;
    fileNumber: string | null;
    fileName: string | null;
    fileCategory: string;
    syncStatus: string;
    firstSeenAt: string | null;
    lastSeenAt: string | null;
}

export interface ExternalDocsSyncRunDetailResponse {
    success: boolean;
    run: ExternalDocsSyncRunListItem;
    added: ExternalDocsSyncRunFileRow[];
    updated: ExternalDocsSyncRunFileRow[];
    tombstoned: ExternalDocsSyncRunFileRow[];
    errors: ExternalDocsSyncRunFileRow[];
}

/**
 * Project Settings
 */
export interface ApiProjectSettings {
    [key: string]: any;
}

/**
 * Per-user settings (the User.settings JSONB bag, via /api/ai/user/settings).
 * All keys are optional; an absent key means the default applies.
 */
export interface ApiUserSettings extends ApiUserPreferences {
    /** Show the admin review layer in Constructability Review (default: true). */
    show_constructability_review?: boolean;
    /** Show the admin chat panel in the drawing viewer (default: true). */
    show_admin_chat?: boolean;
}

export interface ApiPositionSettings {
    position?: string; // "top-left", "top-center", "top-right", "center-left", "center", etc.
    width?: number;
    height?: number;
    opacity?: number;
    size?: string; // "cover", "contain", "auto", etc.
}

/**
 * Submittal Review Interfaces
 */
export enum ConformanceStatus {
    CONFORMS = "conforms",
    DOES_NOT_CONFORM = "does_not_conform",
    INSUFFICIENT_INFO = "insufficient_info",
    NOT_REVIEWED = "not_reviewed",
    NOT_APPLICABLE = "not_applicable",
}

interface ApiSubmittalProductInfo {
    spec_section?: string;
    spec_section_title?: string;
    submittal_number?: string;
    product_type?: string;
    product_mark?: string;
    manufacturer?: string;
    model?: string;
    specifications?: Record<string, string | number | null>;
}

export interface ApiSubmittalProductReview {
    product_info: ApiSubmittalProductInfo;
    requirements: Array<{
        text: string;
        source: string;
        source_type?: "spec" | "drawing"; // Whether requirement is from spec or drawing
        references?: string; // Reference numbers like "123" or "123,456"
        original_index?: number; // Original index before any soft-deletes
    }>;
    conformance_checks: ApiConformanceCheck[];
    hidden_checks?: ApiConformanceCheck[]; // Checks filtered out (insufficient_info, not_applicable status)
    hidden_requirements?: Array<{
        // Requirements corresponding to hidden_checks (for clickable references)
        text: string;
        source: string;
        source_type?: "spec" | "drawing";
        references?: string;
        original_index?: number;
    }>;
    references?: Record<string, ApiReference>;
    reference_file_ids: string[];
    deleted_indices?: number[];
    recommendation?: string;
    filter_stats?: {
        total: number;
        shown: number;
        insufficient_info: number;
        /** Requirements the system never reviewed (agent crash, missing images, a
         * check that raised). Distinct from insufficient_info, which is a verdict
         * about the submittal rather than about us (PP-1121). */
        not_reviewed: number;
        not_applicable: number;
        hidden: number; // Total hidden (insufficient_info + not_applicable + not_reviewed)
        deleted: number;
    };
    /** Set by the agentic engine when this product's agent crashed. Absent on
     * reviews saved before the flag existed — read filter_stats.not_reviewed to
     * cover those too. */
    agent_failed?: boolean;
    // Per-product PDF fields (for focused product view)
    product_file_id?: string; // File ID of extracted per-product PDF
    page_indices?: number[]; // Original page indices (0-indexed) for this product
}

export interface ApiConformanceCheck {
    requirement: string;
    source: string; // Document reference (e.g., "Spec 23 33 00.1.3.A")
    evidence: string; // What was found in submittal
    status: ConformanceStatus;
    notes?: string;
    confidence?: "high" | "medium" | "low";
    original_index?: number; // Original index before any soft-deletes
    /** 0-based pages of the whole submittal file that support this check, sorted. Set by the
     * agentic engine; absent on older reviews. */
    pages?: number[];
}

export interface ApiSubmittalReview {
    id: string;
    user_id: string;
    /** PrimeFile id of the uploaded submittal PDF (null for metadata-only ACC reviews, which have no PDF). */
    submittal_file_id: string | null;
    /** PrimeFile id of the spec doc this review was filed against (null for legacy). */
    spec_document_id?: string | null;
    submittal_type: "product_data" | "shop_drawing" | "test_report" | "samples";
    review_status: "pending" | "in_progress" | "completed" | "failed";
    product_reviews: ApiSubmittalProductReview[];
    product_names?: string[];
    referenced_files?: ApiFile[]; // Files referenced in the review
    submittal_package_files?: string[]; // List of files in the submittal package
    sequence_no?: number; // server-assigned incremental number
    display_number?: string; // zero-padded sequence number string, e.g. 001
    document_name?: string; // uploaded PDF filename (without extension)
    created_at: string;
    completed_at?: string;
    progress_stage?: string | null;
    progress_message?: string | null;
    progress_percent?: number | null;
    draft_link_count?: number;
    submitted_link_count?: number;
    /** The area the specs were read from: picked for this run, the synced submittal's, or the
     *  section's only copy (`area_source`). */
    area?: ApiAreaRef | null;
    area_source?: "chosen" | "submittal" | "only_copy" | null;
    /** `needs_area`: copies in several areas differ and none was named; `not_in_area`: the
     *  named area holds no copy. Both hold the review until an area is picked. */
    area_status?: "checked" | "needs_area" | "not_in_area" | null;
    /** The areas that hold the section, to pick from when held. */
    area_candidates?: ApiAreaRef[];
}

export interface ApiSubmittalReviewStatus {
    id: string;
    status: "pending" | "in_progress" | "completed" | "failed";
    created_at: string;
    completed_at?: string;
    has_results: boolean;
    progress_stage?: string | null;
    progress_message?: string | null;
    progress_percent?: number | null;
}

/**
 * Revision Comparison Types
 */
export type ApiRevisionStatus = "added" | "removed" | "same" | "changed";
export type ApiChangeLevel = "none" | "minor" | "moderate" | "major";

export interface ApiPaginatedResponse<T> {
    items: T[];
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
}

export interface ApiRevisionComparison {
    id: string;
    revision_name?: string | null;
    status: "pending" | "processing" | "completed" | "failed" | "cancelling" | "cancelled";
    progress?: number;
    error_message?: string | null;
    summary?: ApiRevisionComparisonSummary | null;
    created_at: string;
    updated_at?: string;
    completed_at?: string | null;
    /** Revision-object provenance. Null on legacy file-pair comparisons. */
    original_revision_id?: string | null;
    revised_revision_id?: string | null;
    original_revision_name?: string | null;
    revised_revision_name?: string | null;
    /** The revisions the sheets were compared with (each sheet against its own previous
     *  version), most sheets first. Absent on payloads that predate the field. */
    baselines?: ApiComparisonBaseline[] | null;
}

/** A revision some of a comparison's sheets were compared with, and how many. */
export interface ApiComparisonBaseline {
    revision_id: string;
    revision_name: string;
    revision_date: string | null;
    revision_date_is_calendar: boolean;
    sheet_count: number;
}

export interface ApiCreateComparisonResponse {
    success: boolean;
    job_id: number;
    comparison_id: string;
}

interface ApiSheetLLMInfo {
    score: number;
    summary: string;
    keywords: string[];
}

interface ApiOverlayBounds {
    x_min: number;
    y_min: number;
    x_max: number;
    y_max: number;
}

/** A normalized [x_min, y_min, x_max, y_max] box, as a fraction of the page. */
export type ApiCarryoverBounds = [number, number, number, number];

/** The states a carry-over row can be in. `folded` and `dropped` collapse into two of these
 * rather than adding states of their own; each keeps its own word under `why`.
 *
 * `unmeasured` is a carry whose landing place the record cannot establish — no anchor survives
 * on one side or the other. Its own value because every other one would be a claim. */
export type ApiCarryoverState = "inplace" | "moved" | "abstain" | "removed" | "unmeasured";

/**
 * One prior-revision component and what the carry-over seam did with it
 * (ai_engine `GET /carryover/sheets/{section_group_id}`).
 *
 * `state`, `attention`, `why` and `verifier` are derived in ai_engine
 * (`app/carryover_provenance.py`) so every reader groups and sorts a record the same way.
 * `decision` is the seam's own record, passed through unchanged: read named fields off it
 * defensively, since a later configuration writes more of them and a deterministic-only run
 * writes fewer.
 */
export interface ApiCarryoverPrior {
    prior_id: string;
    kind: "carried" | "folded" | "dropped" | "abstain" | "removed";
    state: ApiCarryoverState;
    /** "look" when a person has to judge this row; otherwise the row's own state. */
    attention: "look" | ApiCarryoverState;
    /** The one word for why the row reads as it does, in the seam's own vocabulary. */
    why: string;
    /** Whether a verifier ran, and whether it agreed. `null` means none ran — never "agreed". */
    verifier: "ok" | "veto" | null;
    outcome: string;
    method: string | null;
    rescued: boolean;
    cross_location: boolean;
    anchor_shift: number | null;
    title: string | null;
    h_id: string | null;
    section_type: string | null;
    prior_bounds: ApiCarryoverBounds | null;
    clone_bounds: ApiCarryoverBounds | null;
    prior_anchor: ApiCarryoverBounds | null;
    clone_anchor: ApiCarryoverBounds | null;
    decision: Record<string, unknown>;
}

/** One row of the carry-over tool's sheet list. */
export interface ApiCarryoverSheetSummary {
    section_group_id: string;
    group_code: string;
    revision_number: number;
    run: Record<string, unknown>;
    /** Whether the seam had crops to look at. False makes every abstain on the sheet mean
     * "no visual evidence", so the carried/abstained split is a pipeline failure. */
    crops_ok: boolean;
}

/**
 * A sheet's carry-over record. `available: false` means nothing was RECORDED — components may
 * still have transferred, by the deterministic path that writes nothing — so it must never be
 * shown as "nothing was carried".
 */
export interface ApiCarryoverSheet {
    available: boolean;
    reason?: "no_record";
    section_group_id?: string;
    group_code: string;
    revision_number: number;
    prior_section_group_id?: string | null;
    prior_group_code?: string | null;
    /** The prior sheet's own revision number. Never this sheet's minus one: the prior is the
     *  latest EARLIER revision by lineage, which a sheet absent from an intervening one skips. */
    prior_revision_number?: number | null;
    has_prior_page?: boolean;
    run?: Record<string, unknown>;
    crops_ok?: boolean;
    /** Decisions the record carried that could not be read. Non-zero means the run's own
     *  counts describe more rows than the list below them. */
    unreadable_decisions?: number;
    priors?: ApiCarryoverPrior[];
}

export interface ApiSheetComparison {
    id: string;
    comparison_id: string;
    sheet_id: string;
    discipline: string;
    status: ApiRevisionStatus;
    pct_changed: number;
    avg_score: number;
    change_level: ApiChangeLevel;
    original_image_url?: string;
    revised_image_url?: string;
    overlay_image_url?: string;
    overlay_info?: {
        used_method: string;
        offset_x: number;
        offset_y: number;
        scale?: number;
    };
    detail_counts: {
        total: number;
        changed: number;
        added: number;
        removed: number;
        same: number;
    };
    // User-set review fields (manual overrides; not AI-computed)
    review_status?: string | null; // RevisionReviewStatus value
    user_change_level?: string | null; // overrides change_level (none|minor|moderate|major)
    status_changed_at?: string | null;
    status_changed_by_user?: ApiUser;
    llm?: ApiSheetLLMInfo;
    details?: ApiDetailComparison[];
    area_id?: string | null;
    area_name?: string | null;
    /** The version compared from; null for an added sheet. Absent on older payloads. */
    original?: ApiVersionRef | null;
    /** The version compared to; null for a removed sheet. Absent on older payloads. */
    revised?: ApiVersionRef | null;
}

// Row/item-level extracted-content diff for note/table details (PP-815);
// produced by ai_engine revisions/content_diff.py and carried per detail.
interface ApiContentDiffChange {
    column: string;
    before: string;
    after: string;
}

interface ApiTableDiffChangedRow {
    status: "changed";
    key?: string | null;
    changes: ApiContentDiffChange[];
}

interface ApiTableDiffAddedRemovedRow {
    status: "added" | "removed";
    key?: string | null;
    cells: string[];
}

export type ApiTableDiffRow = ApiTableDiffChangedRow | ApiTableDiffAddedRemovedRow;

export type ApiNoteDiffRow =
    | { status: "changed"; before: string; after: string }
    | { status: "added"; after: string }
    | { status: "removed"; before: string };

export type ApiContentDiffRow = ApiTableDiffRow | ApiNoteDiffRow;

interface ApiContentDiffBase {
    counts: { added: number; removed: number; changed: number; same: number };
    truncated: boolean;
}

interface ApiTableContentDiff extends ApiContentDiffBase {
    kind: "table";
    rows: ApiTableDiffRow[];
    version?: number;
    alignment?: {
        status: "matched_headers" | "unchanged_layout" | "unresolved";
        reason?: string;
        columns?: string[];
    };
    unresolved_rows?: { before: number; after: number };
}

interface ApiNoteContentDiff extends ApiContentDiffBase {
    kind: "note";
    rows: ApiNoteDiffRow[];
}

export type ApiContentDiff = ApiTableContentDiff | ApiNoteContentDiff;

export interface ApiDetailComparison {
    id: string;
    sheet_comparison_id: string;
    detail_id: string;
    detail_title: string;
    h_id: string;
    status: ApiRevisionStatus;
    pct_changed: number;
    overlay_image_url?: string;
    bounds?: ApiOverlayBounds;
    overlay_bounds?: ApiOverlayBounds;
    content_diff?: ApiContentDiff | null;
}

export interface ApiDisciplineStats {
    discipline: string;
    total: number;
    changed: number;
    added: number;
    removed: number;
    same: number;
}

export interface ApiAreaStats {
    area_id: string | null; // null (or the "unassigned" sentinel) ⇒ unassigned bucket
    area_name: string | null; // null for the unassigned bucket
    total: number;
    changed: number;
    added: number;
    removed: number;
    same: number;
}

export interface ApiChangeLevelStats {
    level: ApiChangeLevel;
    label: string;
    count: number;
}

export interface ApiReviewStatusStat {
    review_status: string | null; // null = unset
    count: number;
}

/** A filter option's sheet count under every OTHER filter group plus search (`count`), and in
 *  the whole comparison (`all`, which decides whether the option exists). */
export interface ApiFacetCount {
    count: number;
    all: number;
}

// GET /revision/comparison/{id}/facets — the review sidebar's counts for the active filters.
export interface ApiRevisionFacets {
    /** Sheets matching every filter; equals the /sheets total for the same filters. */
    total: number;
    change_levels: (ApiFacetCount & { level: ApiChangeLevel; label: string })[];
    statuses: (ApiFacetCount & { status: ApiRevisionStatus })[];
    review_statuses: (ApiFacetCount & { review_status: string | null })[];
    areas: (ApiFacetCount & { area_id: string | null; area_name: string | null })[];
    disciplines: (ApiFacetCount & { discipline: string })[];
}

// Narrative PDF export run, from POST/GET /revision/comparison/{id}/export
export interface ApiNarrativeExportRun {
    id: string;
    status: "in_progress" | "completed" | "failed";
    progress_percent: number;
    progress_message: string;
    created_at: string;
    completed_at: string | null;
    /** Fresh short-lived SAS URL for the finished PDF; only present when status is "completed". */
    url?: string;
}

export interface ApiRevisionSummary {
    total_sheets: number;
    sheets_added: number;
    sheets_removed: number;
    sheets_same: number;
    sheets_changed: number;
    total_details: number;
    details_added: number;
    details_removed: number;
    details_same: number;
    details_changed: number;
    disciplines: ApiDisciplineStats[];
    change_levels: ApiChangeLevelStats[];
    areas?: ApiAreaStats[];
}

/** The filter part of ApiGetSheetsParams, shared by /sheets and /facets. */
export type ApiSheetFilterParams = Pick<
    ApiGetSheetsParams,
    "status" | "discipline" | "change_level" | "review_status" | "search" | "area_id"
>;

export interface ApiGetSheetsParams {
    limit?: number;
    offset?: number;
    status?: ApiRevisionStatus[];
    discipline?: string[];
    change_level?: ApiChangeLevel[];
    review_status?: string[];
    min_score?: number;
    max_score?: number;
    search?: string;
    /** Repeatable area filter: area uuids and/or the "unassigned" sentinel (OR semantics). */
    area_id?: string[];
    sort?: "sheet_id" | "pct_changed" | "avg_score";
    order?: "asc" | "desc";
}

export interface ProductEventEnvelope {
    event_id: string;
    org_slug: string;
    event_name: string;
    event_version: number;
    source: "client";
    occurred_at: string;
    properties: Record<string, unknown>;
}

export interface ProductEventBatch {
    org_slug: string;
    events: ProductEventEnvelope[];
}

export interface EngagementReportDateRange {
    start_at: string;
    end_at: string;
    days: number;
    include_internal: boolean;
}

interface EngagementReportSummary {
    seats: number;
    active_users: number;
    seat_utilization_pct: number;
    tracked_actions: number;
    completed_outcomes: number;
    hours_saved: number;
    last_event_at: string | null;
    usage_sessions: number;
    estimated_active_minutes: number;
    average_session_minutes: number;
}

interface EngagementReportWeeklyTrend {
    week_start: string;
    active_users: number;
    event_count: number;
}

interface EngagementReportFeatureUsage {
    feature: string;
    event_count: number;
    unique_users: number;
    usage_sessions: number;
    estimated_active_minutes: number;
    average_session_minutes: number;
}

interface EngagementReportEventUsage {
    event_name: string;
    feature: string;
    event_count: number;
}

export type ViewerContext = "direct" | "reference";

interface EngagementReportViewerContextUsage {
    context: ViewerContext;
    label: string;
    event_count: number;
    unique_users: number;
    usage_sessions: number;
    estimated_active_minutes: number;
    average_session_minutes: number;
}

interface EngagementReportTopUser {
    user_id: string;
    name: string | null;
    email: string | null;
    event_count: number;
}

interface EngagementReportOutcome {
    kind: string;
    label: string;
    period_count: number;
    all_time_count: number;
    hours_saved: number;
}

interface EngagementReportScaleMetric {
    metric: string;
    label: string;
    value: number;
}

interface EngagementReportDataQuality {
    warnings: string[];
    unknown_user_count: number;
    registry_version: number;
}

interface EngagementReportMethodology {
    description: string;
    hours_per_outcome: Record<string, number>;
    events: EngagementReportMethodologyEvent[];
}

interface EngagementReportMethodologyEvent {
    event_name: string;
    definition: string;
    category: string;
    counted_towards: string[];
}

interface EngagementReportMemberActivationWeek {
    week_start: string;
    week_end: string;
    provisioned_members: number;
    logged_in_members: number;
    active_members: number;
    inactive_members: number;
    newly_logged_in_members: number;
    login_rate_pct: number;
}

export interface EngagementReportMember {
    member_id: string;
    user_id: string | null;
    name: string;
    email: string;
    status: string;
    provisioned_at: string | null;
    logged_in_in_period: boolean;
    first_login_at: string | null;
    last_login_at: string | null;
    last_product_activity_at: string | null;
    action_count: number;
    active_days: number;
    usage_sessions: number;
    sessions_per_week: number;
    estimated_active_minutes: number;
    average_session_minutes: number;
    longest_session_minutes: number;
}

interface EngagementReportMemberActivation {
    source: "stytch" | "tenant_user";
    source_status: "live" | "cached" | "stale" | "fallback";
    provisioned_members: number;
    logged_in_members_in_period: number;
    active_members_in_period: number;
    inactive_members_in_period: number;
    login_rate_pct: number;
    status_breakdown: Record<string, number>;
    period_start_at: string;
    period_end_at: string;
    coverage_start_at: string | null;
    warnings: string[];
    weekly_login_trends: EngagementReportMemberActivationWeek[];
    members: EngagementReportMember[];
}

export interface EngagementReport {
    org_slug: string;
    generated_at: string;
    date_range: EngagementReportDateRange;
    summary: EngagementReportSummary;
    weekly_trends: EngagementReportWeeklyTrend[];
    feature_usage: EngagementReportFeatureUsage[];
    viewer_context_usage: EngagementReportViewerContextUsage[];
    event_usage: EngagementReportEventUsage[];
    top_users: EngagementReportTopUser[];
    completed_outcomes: EngagementReportOutcome[];
    project_scale: EngagementReportScaleMetric[];
    member_activation: EngagementReportMemberActivation;
    data_quality: EngagementReportDataQuality;
    methodology: EngagementReportMethodology;
}

export type {
    FleetEngagementFeatureAdoption,
    FleetEngagementFeatureBreakdownAdoption,
    FleetEngagementReport,
    FleetEngagementTenantFeatureAdoption,
    FleetEngagementTenantOption,
} from "./fleetEngagementReport";

/**
 * Contracts for ai_engine's jobs-framework API (/api/ai/jobs/*).
 *
 * Mirrors app/routers/jobs_api.py response models. Runs here are
 * file_processing_workflow rows (domain truth), not
 * background-job ledger rows — see the Queue tab for those.
 */

export interface JobCatalogEntry {
    name: string;
    title: string;
    subject_type: "revision" | "document" | "submittal" | "project" | "none";
    audience: string;
    description: string;
    flow_name: string | null;
    launchable: boolean;
    triggers: string[];
    queues: string[];
    /** Wrap jobs (§1.2) dispatch through their own launchers — fill-gaps and
     * step subsets don't apply to them. */
    wrap: boolean;
    /** EFFECTIVE subject-picker flow, server-computed as override-or-flow_name
     * (e.g. submittal_registry picks spec documents, not its own flow). */
    subject_flow: string | null;
    /** Pinned pipeline task of a derived ad-hoc job — the run form needs a
     * document (and optional sheets), never a steps field. */
    adhoc_task: string | null;
    /** The pinned task's scope — the run form offers a sheet picker only for
     * per_sheet / per_discipline tasks. */
    adhoc_scope: "per_sheet" | "per_revision" | "per_document" | "per_discipline" | null;
}

/** One event key with the jobs its payload can launch (GET /jobs/events).
 * Payload-field, self-trigger and type-guard exclusions are all server-side. */
export interface JobEventCapability {
    key: string;
    jobs: string[];
}

export interface FrameworkRunSummary {
    id: string;
    revision_id: string | null;
    document_id: string;
    status: "pending" | "running" | "completed" | "failed" | "cancelled";
    total_nodes: number;
    completed_nodes: number;
    failed_nodes: number;
    created_at: string;
    created_by: string | null;
    error_message: string | null;
    /** Substrate identity: WHAT ran on WHAT — null on pre-framework rows. */
    job_type: string | null;
    subject_type: string | null;
    subject_id: string | null;
}

export interface FrameworkRunNode {
    id: string;
    step: string;
    kind: string;
    status: string;
    section_group_id: string | null;
    version: number;
    error_message: string | null;
}

interface FrameworkStepStats {
    counts: Record<string, number>;
    first_started_at?: string | null;
    last_completed_at?: string | null;
}

export interface FrameworkRunDetail {
    run: FrameworkRunSummary;
    step_stats: Record<string, FrameworkStepStats>;
    nodes: FrameworkRunNode[];
}

export interface GapCell {
    task_name: string;
    reason: "missing" | "failed" | "stranded";
    section_group_id: string | null;
    file_id: string | null;
}

export interface RunJobRequest {
    revision_id?: string;
    document_id?: string;
    /** Job-specific subject (e.g. the submittal_review row id). */
    subject_id?: string;
    steps?: string[];
    sheets?: string[];
    fill_gaps?: boolean;
    dry_run?: boolean;
    /** Schedule the run for a future time (ISO; temporal-engine flow/wrap jobs). */
    run_at?: string;
}

export interface RunJobResponse {
    job_name: string;
    mode: string;
    workflow_ids: string[];
    jobs_scheduled: number;
    queue_job_id?: number | null;
    planned_gaps: GapCell[];
    deferred_fan_in_tasks: string[];
    /** Sheet groups planned but NOT started: one live workflow per revision, so
     * one signature group starts per call and the rest wait for a re-run. */
    deferred_groups: number;
    /** The flattened cells of the deferred sheet groups — exactly what a
     * follow-up call still has to run. Absent/empty means nothing was deferred. */
    deferred_cells?: GapCell[];
}

export interface PipelineSheetRef {
    section_group_id: string;
    file_id: string;
    group_code: string;
    /** 1-based page in the source file — the matrix's page search. */
    start_page_in_file?: number | null;
}

export interface PipelineStepCells {
    task_name: string;
    label: string;
    scope: "per_sheet" | "per_revision" | "per_document" | "per_discipline";
    kind: string;
    /** cell key: section_group_id for per-sheet steps, "__revision__" for fan-in. */
    cells: Record<string, string>;
    /** Direct flow-DAG dependencies (flow tasks only) — the matrix's edges. */
    requires: string[];
    /** Catalog level, for the matrix's level gutter. */
    level: number;
    /** enabled_by_default=false flow extra — an optional row, never a gap. */
    opt_in?: boolean;
}

/** A revision whose upload is still materializing sheets — no run row yet. */
export interface PreparingRevision {
    revision_id: string;
    name: string;
    document_id: string | null;
    document_title: string | null;
    sheet_count: number;
    created_at: string;
}

interface CellRef {
    task_name: string;
    section_group_id: string | null;
}

export interface RunCellsRequest {
    revision_id: string;
    cells: CellRef[];
    dry_run?: boolean;
}

interface PlannedCell {
    task_name: string;
    section_group_id: string | null;
    reason: string;
}

export interface RunCellsResponse {
    job_name: string;
    mode: string;
    cells: PlannedCell[];
    excluded: PlannedCell[];
    workflow_ids: string[];
    deferred_fan_in_tasks: string[];
    /** See RunJobResponse.deferred_groups. */
    deferred_groups: number;
    /** See RunJobResponse.deferred_cells. */
    deferred_cells?: GapCell[];
}

export interface PipelineStatusResponse {
    revision_id: string;
    flow_name: string;
    sheets: PipelineSheetRef[];
    steps: PipelineStepCells[];
    gaps: GapCell[];
    cell_counts: Record<string, number>;
    workflow_ids: string[];
    /** The revision's non-terminal workflow, if any — one live run per revision,
     * so this is both "keep polling" and "why a launch is refused". */
    live_workflow_id: string | null;
}

export interface CancelRunResponse {
    run_id: string;
    mode: "workflow_cancelled" | "revision_cancelled";
    status?: string;
}

interface SubjectRevision {
    id: string;
    name: string;
    status: string;
    document_id: string;
    document_title: string | null;
    document_type: string | null;
    /** The pipeline flow that ingests this revision's document type. */
    flow_name: string | null;
}

interface SubjectDocument {
    id: string;
    title: string;
    document_type: string;
    flow_name: string | null;
}

/** PDF-backed submittal reviews only — metadata-only ACC reviews run inline in the sync. */
interface SubjectSubmittalReview {
    id: string;
    sequence_no: number | null;
    submittal_type: string;
    review_status: string;
}

export interface RunSubjects {
    revisions: SubjectRevision[];
    documents: SubjectDocument[];
    submittal_reviews: SubjectSubmittalReview[];
}

/** One §1.3 trigger: when event_key fires, launch job_name. */
export interface JobSubscription {
    id: string;
    event_key: string;
    job_name: string;
    enabled: boolean;
    payload_template: Record<string, unknown>;
    created_by: string | null;
    created_at: string;
}

export interface CreateSubscriptionRequest {
    event_key: string;
    job_name: string;
    payload_template?: Record<string, unknown>;
}

// =====================================================================}

// Related Items v3 + Taxonomy Console (PP-863)
// =====================================================================}

export type ApiRelatedItemKind =
    "drawing_sheet" | "spec_chapter" | "submittal" | "rfi" | "activity" | "document";

export type ApiRelatedItemTier = "CITED" | "COORDINATION" | "FAMILY" | "SIMILAR";

export type ApiRelatedItemsAnchorKind =
    "section" | "activity" | "sheet" | "spec" | "submittal" | "rfi";

export interface ApiRelatedItemsAnchor {
    kind: ApiRelatedItemsAnchorKind;
    entityId?: string;
    sectionUuid?: string;
    documentId?: string;
    groupCode?: string;
}

export interface ApiRelatedItemCard {
    kind: ApiRelatedItemKind;
    tier: ApiRelatedItemTier;
    score: number;
    reasons: string[];
    title: string;
    documentId?: string | null;
    groupCode?: string | null;
    /** A drawing/spec card's own sheet version. Scopes the target lookup to that sheet's
     *  area chain; absent, the lookup spans every area copy of `groupCode`. */
    sectionGroupId?: string | null;
    entityId?: string | null;
    sectionUuid?: string | null;
    number?: string | null;
    conflict: boolean;
    distance?: number | null;
}

export interface ApiRelatedItemsResponse {
    enabled: boolean;
    cards: ApiRelatedItemCard[];
}

/** Where a tenant stands in the Related Items v3 rollout.
 *  `phase` is the whole state space: `off` (the flag is not set), `preparing` (the
 *  backfill is queued and the taxonomy is still being built), `backfilling`
 *  (documents are being tagged), `ready` (every document is tagged), `failed`
 *  (the last dispatch raised; it retries, but the rollout is not progressing). */
export interface ApiTaxonomyRolloutStatus {
    mode: "off" | "enabled";
    phase: "off" | "preparing" | "backfilling" | "ready" | "failed";
    documentsTotal: number;
    documentsTagged: number;
    queuedDocuments: number | null;
    startedAt: string | null;
}

export interface ApiTaxonomyNode {
    id: number;
    key: string;
    facet: string;
    parentId: number | null;
    label: string;
    aliases: string[];
    anchorIds: number[];
    active: boolean;
    tenantLayer: boolean;
    extra: Record<string, unknown>;
    entityCount: number;
}

export interface ApiTaxonomyTreeResponse {
    version: number;
    nodes: ApiTaxonomyNode[];
}

interface ApiTaxonomyNodeEntitySheet {
    sectionGroupId: string;
    documentId: string;
    groupCode: string;
    title: string | null;
}

interface ApiTaxonomyNodeEntityItem {
    id: string;
    title: string | null;
    number: string | null;
}

export interface ApiTaxonomyNodeEntities {
    sheets: ApiTaxonomyNodeEntitySheet[];
    submittals: ApiTaxonomyNodeEntityItem[];
    rfis: ApiTaxonomyNodeEntityItem[];
}

export type ApiTaxonomyEntityKind = "section_group" | "section" | "submittal" | "rfi";

type ApiTaxonomyAssignmentSource =
    | "title_alias"
    | "anchored"
    | "code"
    | "location"
    | "designator"
    | "role"
    | "discipline"
    | "action"
    | "classifier"
    | "body"
    | "inherited"
    | "manual";

export interface ApiTaxonomyAssignment {
    taxonomyId: number;
    key: string | null;
    label: string | null;
    source: ApiTaxonomyAssignmentSource;
    confidence: number | null;
    matchedSpan: string | null;
    model: string | null;
}

interface ApiTaxonomyInheritedAssignment {
    taxonomyId: number;
    key: string;
    label: string;
    source: "inherited";
}

export interface ApiTaxonomyAssignmentsResponse {
    assignments: ApiTaxonomyAssignment[];
    inherited: ApiTaxonomyInheritedAssignment[];
    arrayIds: number[];
}

interface ApiTaxonomyIdDetail {
    id: number;
    key: string;
    label: string;
    facet: string;
}

export interface ApiTaxonomyExplainPairResponse {
    total: number;
    reasons: string[];
    hasPrimary: boolean;
    conflict: boolean;
    qualified: boolean;
    sourceIds: ApiTaxonomyIdDetail[];
    candidateIds: ApiTaxonomyIdDetail[];
}

export type ApiTaxonomyCorrectionRequest =
    | {
          action: "remove" | "add";
          entityKind: ApiTaxonomyEntityKind;
          entityId?: string;
          documentId?: string;
          groupCode?: string;
          hId?: string;
          taxonomyId: number;
      }
    | { action: "promote_alias"; taxonomyId: number; alias: string };

export interface ApiTaxonomyCorrectionResponse {
    status: string;
    action: string;
}

/** Detector class enum, wire schema v1 (order matters: it is the model's class ids 0-6). */ // eslint-disable-next-line @typescript-eslint/no-unused-vars -- only its type is used
const DETECTOR_CLASSES = [
    "drawing_view",
    "title",
    "table_schedule",
    "notes",
    "legend_key",
    "sheet_furniture",
    "detail_id",
] as const;

export type DetectorClass = (typeof DETECTOR_CLASSES)[number];

/** Normalized [xMin, yMin, xMax, yMax] in rendered-image coordinates (0..1). */
export type XyxyNorm = [number, number, number, number];

/** Complete box snapshot stored on edit/add verdicts. */
export interface DetectorVerdictBox {
    xyxy_norm: XyxyNorm;
    score: number;
    cls: DetectorClass;
}

export interface DetectorBox extends DetectorVerdictBox {
    /** Suppressed duplicate; indices stay stable and point to the retained survivor. */
    duplicate_of?: number;
    /** Absorbed group; children retain their original payload box indices. */
    container_of?: number[];
    /** Candidate-only marker; verdict request bodies deliberately exclude it. */
    double_claim?: true;
    sheet_furniture?: { sheets: number };
}

export interface DetectorCandidatePayloadBase {
    status?: "ok" | "error";
    model?: string;
    image?: { width: number; height: number };
    boxes: DetectorBox[];
    error?: string;
}

/** Complete, canonical set of classes eligible to parent a Run-5 tie marker. */
export const DETECTOR_TIE_PARENT_CLASSES = [
    "drawing_view",
    "table_schedule",
    "notes",
    "legend_key",
] as const;

export type DetectorTieParentClass = (typeof DETECTOR_TIE_PARENT_CLASSES)[number];

export interface DetectorTieCandidateV2 {
    box_index: number;
    class: DetectorTieParentClass;
    det_score: number;
    pointer_logit: number;
}

export interface DetectorTieV2 {
    marker_box_index: number;
    role: "detail_id" | "title";
    no_parent_logit: number;
    candidates: DetectorTieCandidateV2[];
    linked_by?: "pointer" | "geometry" | "container" | "confident_tie";
    /** Decision serving persisted; the review UI renders it verbatim at rest. */
    decision: {
        parent_box_index: number | null;
        abstained: boolean;
        probability: number;
        reason?: "sheet_furniture";
    };
}

/** Run-5 payload with enough provenance to reproduce pointer decoding client-side. */
export interface DetectorCandidatePayloadV2 extends DetectorCandidatePayloadBase {
    schema: 2;
    ties?: DetectorTieV2[];
    applied_thresholds: Partial<Record<DetectorClass, number>>;
    linker_mode: string;
}

// --- Project health station (ai_engine /project-health, PP-976) ---

/** "stale" = a revision was published after the baseline was approved, so the
 * check recorded current metrics without comparing across the boundary. */
export type ProjectHealthCheckStatus = "ok" | "degraded" | "stale";
type ProjectHealthSeverity = "danger" | "warning";

type ProjectHealthTotals = Record<string, number>;

/** Connected-tag subcounts keyed by taglist destination name (DOOR, ROOM, ...). */
export type ProjectHealthConnectedByType = Record<string, number>;

export interface ProjectHealthDegradation {
    scope: "total" | "sheet";
    /** Stable identity: metric key for totals, normalized-code|document for sheets. */
    key: string;
    /** Raw visible sheet code for display (sheet scope only). */
    sheet?: string;
    metric: string;
    label: string;
    baseline: number;
    current: number;
    delta: number;
    severity: ProjectHealthSeverity;
}

export interface ProjectHealthBaseline {
    id: string;
    approvedAt: string | null;
    approvedBy: string | null;
    note: string | null;
    totals: ProjectHealthTotals;
    /** Empty on baselines approved before the breakdown existed. */
    connectedByType: ProjectHealthConnectedByType;
}

/** A revision published since the baseline was approved (newest first, max 10). */
interface ProjectHealthRecentRevision {
    id: string;
    name: string;
    documentTitle: string | null;
    publishedAt: string;
}

/** A revision whose own state may explain the drop (max 10). */
interface ProjectHealthAttentionRevision {
    id: string;
    name: string;
    status: string;
    processingStatus: string | null;
}

/** Why a check degraded: what changed since the baseline, and what looks unhealthy now. */
interface ProjectHealthCheckContext {
    recentRevisions: ProjectHealthRecentRevision[];
    needsAttention: ProjectHealthAttentionRevision[];
}

export interface ProjectHealthCheck {
    id: string;
    checkedAt: string | null;
    status: ProjectHealthCheckStatus;
    totals: ProjectHealthTotals;
    connectedByType: ProjectHealthConnectedByType;
    degradations: ProjectHealthDegradation[];
    degradationCount: number;
    /** Populated on degraded and stale checks; null on healthy ones. On a stale
     * check, recentRevisions names the revisions that made the baseline stale. */
    context: ProjectHealthCheckContext | null;
    /** Set once a Linear issue has been filed for this check. */
    linearIssueUrl: string | null;
}

export interface ProjectHealthHistoryEntry {
    /** Check id: each history entry is drillable via GET /project-health/checks/{id}. */
    id: string;
    checkedAt: string | null;
    status: ProjectHealthCheckStatus;
    degradationCount: number;
}

export interface ProjectHealthStatusResponse {
    baseline: ProjectHealthBaseline | null;
    latestCheck: ProjectHealthCheck | null;
    history: ProjectHealthHistoryEntry[];
}

export interface ProjectHealthFleetRow {
    orgSlug: string;
    approvedAt: string | null;
    checkedAt: string | null;
    status: ProjectHealthCheckStatus | null;
    degradationCount: number;
    topDegradations: ProjectHealthDegradation[];
    /** True when the tenant read failed; the row's other fields are then defaults. */
    error: boolean;
}

export interface ProjectHealthFleetResponse {
    projects: ProjectHealthFleetRow[];
}

// Revision quality report payloads (ai_engine /revision-quality).
export * from "./revisionQualityTypes";

/** `POST /revisions/processing-sets/{id}/import` -- the started workflow.
 *  `processing_set_id` echoes the set that was clicked; the import may end up
 *  applying a newer delta (and superseding that set). */
export interface ProcessingSetImportStarted {
    workflow_id: string;
    processing_set_id: string;
}

/** `GET /revisions/auto-import` -- the tenant's auto-pull opt-in. */
export interface AutoImportState {
    enabled: boolean;
}
// --- Workflow Studio ---

export type WorkflowStatus = "draft" | "published";
export type WorkflowCapabilityCategory = "source" | "transform" | "reconcile" | "review" | "output";

export interface WorkflowNodePosition {
    x: number;
    y: number;
}

export interface WorkflowStudioNode {
    id: string;
    capability: string;
    label: string | null;
    position: WorkflowNodePosition;
    config: Record<string, unknown>;
}

export interface WorkflowStudioEdge {
    id: string;
    source: string;
    target: string;
}

export type PublishedToolInputType = "file" | "text" | "number" | "date" | "select" | "document";

export interface PublishedToolInputDefinition {
    id: string;
    label: string;
    description: string;
    type: PublishedToolInputType;
    required: boolean;
    input_key: string;
    accept: string[];
    options: string[];
}

/** The result types every ai_engine release accepts. */
export type PublishedToolResultViewType = "summary" | "table" | "download";

/** A pay-app view. It travels as `{ type: "table", view }`, so an ai_engine
 *  that predates it (a rollback) still reads a valid table result and renders
 *  the exceptions review instead of rejecting the manifest. */
export type PublishedToolTableView =
    "fix_list" | "side_by_side" | "control_totals" | "month_to_month";

interface PublishedToolResultBase {
    id: string;
    label: string;
    visible: boolean;
}

/** Only table results may name a pay-app view. The API enforces the same rule. */
export type PublishedToolResultView = PublishedToolResultBase &
    (
        | { type: "table"; view?: PublishedToolTableView }
        | { type: "summary" | "download"; view?: never }
    );

export interface PublishedToolExperience {
    slug: string;
    icon: string;
    instructions: string;
    run_button_label: string;
    show_in_tools_menu: boolean;
    show_on_home: boolean;
    /** True: the Run tab is "Files" and shows the drop panel instead of one slot per input. */
    file_drop?: boolean;
    inputs: PublishedToolInputDefinition[];
    results: PublishedToolResultView[];
}

export interface WorkflowDefinition {
    id: string;
    name: string;
    description: string;
    version: number;
    status: WorkflowStatus;
    tool: PublishedToolExperience;
    nodes: WorkflowStudioNode[];
    edges: WorkflowStudioEdge[];
    updated_at: string | null;
    published_at: string | null;
}

export interface WorkflowConfigFieldDefinition {
    key: string;
    label: string;
    type: "text" | "select" | "string_list";
    options: string[];
    help: string | null;
}

export interface WorkflowCapabilityDefinition {
    key: string;
    title: string;
    description: string;
    category: WorkflowCapabilityCategory;
    input_type: string | null;
    output_type: string;
    min_inputs: number;
    max_inputs: number;
    config_fields: WorkflowConfigFieldDefinition[];
}

export interface WorkflowCapabilityCatalogResponse {
    capabilities: WorkflowCapabilityDefinition[];
}

export interface WorkflowListResponse {
    workflows: WorkflowDefinition[];
}

export interface PublishedToolSummary {
    workflow_id: string;
    slug: string;
    name: string;
    description: string;
    icon: string;
    path: string;
    version: number;
    show_in_tools_menu: boolean;
    show_on_home: boolean;
}

export interface PublishedToolListResponse {
    tools: PublishedToolSummary[];
}

/** Wire statuses. `confirmed` is the legacy review's "Confirm issue"; the
 *  pay-app views never offer it and only show it when already set. */
export type ReviewStatus = "unreviewed" | "confirmed" | "dismissed" | "resolved" | "not_an_issue";

/** Server-computed net per invoice key per side (`cmic` or a pay_app_id), so
 *  Side by side never derives money from the evidence preview. */
export interface SideNet {
    source_id: string;
    label: string;
    job: string;
    net: string;
    row_count: number;
    /** This side's own vendor name; absent from an older ai_engine. */
    vendor?: string;
    /** The pay app's draw this net sits in ("19"); absent when empty. */
    draw?: string;
}

export type ReconciliationDestination = "pay_app" | "cmic" | "confirm" | "";
export type ReconciliationVerb = "credit" | "bill" | "move_cost" | "reduce" | "confirm" | "";

export interface ReconciliationException {
    id: string;
    category: ReconciliationExceptionCategory;
    severity: "high" | "medium" | "low";
    title: string;
    explanation: string;
    invoice: string;
    vendor: string;
    cost_vendor: string;
    billing_vendor: string;
    cost_amount: string;
    billing_amount: string;
    difference: string;
    /** Dollars at stake, and the number the queue ranks and the summary totals
     *  by. Not the difference: a group whose charges and credits cancel nets to
     *  zero and is exactly the condition worth surfacing. */
    exposure: string;
    confidence: string;
    reason_codes: string[];
    review_status: ReviewStatus;
    review_note: string;
    reviewed_at: string | null;
    reviewed_by: string;
    /** The person's email, shown when the member directory cannot name reviewed_by. */
    reviewed_by_label?: string;
    cost_row_count: number;
    billing_row_count: number;
    /** A preview. `*_row_count` is the real count and the export holds every
     *  row; anything that has to be complete reads `draws`, not these. */
    cost_rows: ReconciliationEvidence[];
    billing_rows: ReconciliationEvidence[];
    evidence_truncated: boolean;
    draw: string;
    draws: string[];
    has_unassigned_draw: boolean;
    // Staged-matcher fields; the legacy matcher sends them at their defaults.
    pay_app_id: string;
    pay_app_label: string;
    stage: "invoice" | "variant" | "card" | "vendor" | "control" | "";
    vendor_id: string;
    invoice_key: string;
    /** Verbatim CMiC Reference / Invoice # text. */
    raw_references: string[];
    destination: ReconciliationDestination;
    destination_pay_app_id: string;
    destination_label: string;
    verb: ReconciliationVerb;
    /** The action in the reviewer's own wording. */
    action: string;
    detail: string;
    /** Signed, 2dp. */
    amount_to_post: string;
    journal_from_job: string;
    journal_to_job: string;
    progress: string;
    flag: string;
    batches: string[];
    partner_jobs: string[];
    other_pay_app_net: string;
    cat_s_in_running_totals: boolean;
    sides: SideNet[];
    /** 32 hex; '' for legacy output, which never carries a decision forward. */
    decision_key: string;
    carried_from_run_id: string;
    carried_note: string;
    reopened: boolean;
    reopened_note: string;
    /** Plain sentences for the row's secondary line; absent from an older run. */
    plain?: string;
    hand_match_id?: string;
}

/** One reviewer's decision, stored and returned on its own so saving one does
 *  not round-trip the exception's evidence. */
export interface ExceptionReview {
    exception_id: string;
    review_status: ReviewStatus;
    review_note: string;
    reviewed_at: string | null;
    reviewed_by: string;
    /** The person's email, shown when the member directory cannot name reviewed_by. */
    reviewed_by_label?: string;
    /** Returned so the client can merge the decision onto the exception in
     *  place; '' for a legacy exception. */
    decision_key: string;
}

export interface RejectedReconciliationRow {
    source: "cost" | "billing";
    file_name: string;
    row_number: number;
    reason:
        | "missing_date"
        | "invalid_date"
        | "missing_amount"
        | "invalid_amount"
        | "summary_row"
        | "missing_vendor_and_invoice";
    message: string;
    raw_date: string;
    raw_amount: string;
    vendor: string;
    invoice: string;
}

export interface DataQualityNote {
    code:
        | "undated_rows"
        | "unparsed_dates"
        | "dot_dated_rows"
        | "sheet_selected"
        | "header_alias_used"
        | "partner_out_of_scope"
        | "unknown_job"
        | "draw_shifted";
    count: number;
    amount: string;
    message: string;
    sample_rows: number[];
}

/** What the parser found in one upload. */
export interface InputDetection {
    input_key: string;
    file_name: string;
    sheet_name: string;
    header_row: number;
    row_count: number;
    undated_rows: number;
    jobs: string[];
    draws: string[];
    pay_app_id: string;
    notes: DataQualityNote[];
}

/** One payment application: `pay_app_id` is identity, `label` is the tool
 *  input's label and only ever shown. */
export interface PayAppRef {
    pay_app_id: string;
    label: string;
    input_key: string;
    cmic_jobs: string[];
    through_draw: string;
    uploaded: boolean;
}

export interface PayAppSummary {
    pay_app_id: string;
    label: string;
    input_key: string;
    file_name: string;
    cmic_jobs: string[];
    through_draw: string;
    uploaded: boolean;
    input_rows: number;
    included_rows: number;
    undated_rows: number;
    out_of_scope_partner_rows: number;
    billed_total: string;
}

/** One job's row of the control-total table; `billable` = invoice + card +
 *  transfer + subcontract_billed_in_running, `subcontract_textura` is the Cat S
 *  cost the running totals never carry. All amounts are the server's. */
export interface JobControlTotal {
    pay_app_id: string;
    job: string;
    invoice: string;
    card: string;
    transfer: string;
    subcontract_billed_in_running: string;
    billable: string;
    billed: string;
    difference: string;
    journal: string;
    subcontract_textura: string;
}

export interface ControlLine {
    label: string;
    pay_app_id: string;
    cost: string;
    billed: string | null;
    difference: string | null;
    note: string;
    previous_amount: string | null;
}

export interface CardLine {
    pay_app_id: string;
    cost_rows: number;
    bill_rows: number;
    cost_amount: string;
    bill_amount: string;
    merchant_hits: number;
    amount_date_hits: number;
    unmatched_cost_rows: number;
    unmatched_cost_amount: string;
    unmatched_bill_rows: number;
    unmatched_bill_amount: string;
}

export interface PairedReference {
    pay_app_id: string;
    vendor: string;
    vendor_id: string;
    cost_reference: string;
    billing_reference: string;
    amount: string;
    why: string;
}

export interface PartnerTotal {
    input_key: string;
    partner: string;
    rows: number;
    amount: string;
}

export interface VendorRollup {
    vendor_id: string;
    vendor: string;
    groups_collapsed: number;
    net: string;
}

export type FixListDestinationKind = "pay_app" | "cmic" | "confirm";

export interface DestinationTotal {
    key: string;
    kind: FixListDestinationKind;
    title: string;
    next_hint: string;
    count: number;
    credit_total: string;
    bill_total: string;
}

export interface ReconciliationSummary {
    cutoff_date: string | null;
    /** The files' as-of date. */
    data_through?: string | null;
    tie_outs?: TieOut[];
    cost_input_rows: number;
    cost_included_rows: number;
    cost_excluded_rows: number;
    cost_rejected_rows: number;
    billing_input_rows: number;
    billing_included_rows: number;
    billing_excluded_rows: number;
    billing_rejected_rows: number;
    cost_cutoff_excluded_rows: number;
    billing_cutoff_excluded_rows: number;
    cost_total: string;
    billing_total: string;
    net_difference: string;
    matched_group_count: number;
    matched_amount: string;
    exception_count: number;
    category_counts: Record<string, number>;
    category_amounts: Record<string, string>;
    pay_apps: PayAppSummary[];
    partner: string;
    out_of_scope_partners: PartnerTotal[];
    cost_undated_rows: number;
    billing_undated_rows: number;
    /** Computed after carry-forward, over open items only. */
    destinations: DestinationTotal[];
    stage_counters: Record<string, number>;
    open_count: number;
    cleared_count: number;
    reopened_count: number;
    data_quality_notes: DataQualityNote[];
    /** Full row counts per upload; rejected_rows carries only sampled evidence. */
    rejected_row_counts?: RejectedRowCount[];
}

export interface RejectedRowCount {
    input_key: string;
    file_name: string;
    source: "cost" | "billing";
    total: number;
    by_reason: Record<string, number>;
    listed: number;
}

/** One row of the fix list. No review fields: status comes from
 *  `run.exceptions[exception_id]`, which the read path overlays. */
export interface FixListItem {
    exception_id: string;
    verb: string;
    vendor: string;
    vendor_id: string;
    invoice_keys: string[];
    amount_to_post: string;
    why: string;
    rule: string;
    journal: string;
    progress: string;
    flag: string;
    carried_note: string;
    carried_from_run_id: string;
    reopened: boolean;
    severity: "high" | "medium" | "low";
    cost_row_count: number;
    billing_row_count: number;
}

export interface FixListDestination {
    /** A pay_app_id, `cmic` or `confirm`. */
    key: string;
    kind: FixListDestinationKind;
    title: string;
    next_hint: string;
    count: number;
    credit_total: string;
    bill_total: string;
    items: FixListItem[];
}

export interface VendorTriage {
    vendor: string;
    vendor_id: string;
    item_count: number;
    gross_amount: string;
    net_amount: string;
    largest_exception_id: string;
    note: string;
}

export interface FixListView {
    destinations: FixListDestination[];
    cleared_exception_ids: string[];
    new_for_review: VendorTriage[];
}

export interface JobCategoryLine {
    job: string;
    cat: string;
    amount: string;
}

export interface PayAppControl {
    pay_app_id: string;
    label: string;
    uploaded: boolean;
    jobs: JobControlTotal[];
    total: JobControlTotal;
    /** Filled when the pay app was not uploaded. */
    cat_lines: JobCategoryLine[];
}

/** A CMiC vendor and a running-totals vendor name the tool matched
 *  automatically though they are written differently ("Arc Document
 *  Solutions, Llc" and "ARC"). */
export interface VendorNameVariant {
    cmic_vendor: string;
    cmic_vendor_id: string;
    pay_app_id: string;
    billing_vendor: string;
    invoice_count: number;
    amount: string;
}

export interface ControlTotalsView {
    pay_apps: PayAppControl[];
    lines: ControlLine[];
    card_lines: CardLine[];
    paired_references: PairedReference[];
    out_of_scope_partners: PartnerTotal[];
    vendor_rollups: VendorRollup[];
    stage_counters: Record<string, number>;
    /** Absent from an older ai_engine, which did not match name variants. */
    vendor_name_variants?: VendorNameVariant[];
    paired_references_omitted?: number;
    vendor_rollups_omitted?: number;
    vendor_name_variants_omitted?: number;
}

export interface StandingRow {
    kind:
        | "cleared"
        | "half_done"
        | "still_open"
        | "decision_carried"
        | "confirm"
        | "reopened"
        | "verified_done";
    count: number;
    exception_ids: string[];
    description: string;
}

export interface DoneCheck {
    decision_key: string;
    vendor: string;
    invoice_key: string;
    amount_to_post: string;
    marked_by: string;
    /** The person's email, shown when the member directory cannot name marked_by. */
    marked_by_label?: string;
    marked_at: string;
    outcome: "held" | "reopened";
    found: string;
}

export interface MonthToMonthView {
    previous_run_id: string;
    previous_run_created_at: string | null;
    standing: StandingRow[];
    done_checks: DoneCheck[];
    carried_decision_count: number;
    reopened_count: number;
}

/** The screen-shaped payload; holds exception ids and money, never status. */
export interface RunViews {
    fix_list: FixListView;
    side_by_side: SideBySideView;
    control_totals: ControlTotalsView;
    month_to_month: MonthToMonthView;
}

export interface RunInputArtifact {
    input_key: string;
    file_name: string;
    byte_size: number;
    sha256: string;
    pay_app_id: string;
    detection: InputDetection | null;
}

export interface WorkflowNodeRun {
    node_id: string;
    capability: string;
    status: "succeeded";
    duration_ms: number;
    metrics: Record<string, number | string>;
}

export interface WorkflowRunResponse {
    run_id: string;
    workflow_id: string;
    workflow_version: number;
    definition_sha256: string;
    status: "succeeded";
    created_at: string;
    input_files: RunInputArtifact[];
    summary: ReconciliationSummary;
    exceptions: ReconciliationException[];
    rejected_rows: RejectedReconciliationRow[];
    node_runs: WorkflowNodeRun[];
    /** Null for the shipped matcher; the client then renders the legacy review. */
    views: RunViews | null;
    previous_run_id: string;
    carried_decision_count: number;
    reopened_count: number;
    /** Matches an accountant made by hand, kept month to month. */
    hand_matches?: HandMatch[];
    /** "draft" for a builder test run: never indexed, and a review on it
     *  never becomes a carried decision. Absent on runs stored before the
     *  stamp, which read as published. */
    run_kind?: "published" | "draft";
}

export interface DecisionVerification {
    run_id: string;
    checked_at: string;
    outcome: "held" | "reopened";
    delta: string;
}

/** A review decision keyed by what it was about (pay app, vendor, invoice
 *  key, amount), so next month's run finds it. */
export interface CarriedDecision {
    workflow_id: string;
    decision_key: string;
    vendor_id: string;
    vendor: string;
    invoice_key: string;
    amount_to_post: string;
    pay_app_id: string;
    destination: string;
    action: string;
    status: ReviewStatus;
    note: string;
    reviewed_by: string;
    /** The person's email, shown when the member directory cannot name reviewed_by. */
    reviewed_by_label?: string;
    reviewed_at: string;
    decided_in_run_id: string;
    decided_exception_id: string;
    last_seen_run_id: string;
    last_seen_at: string | null;
    verification: DecisionVerification | null;
}

export interface RunIndexFile {
    input_key: string;
    file_name: string;
    sha256: string;
    pay_app_id: string;
}

export interface RunIndexEntry {
    run_id: string;
    created_at: string;
    workflow_version: number;
    definition_sha256: string;
    input_files: RunIndexFile[];
    exception_count: number;
    open_count: number;
    cleared_count: number;
    reopened_count: number;
    destination_counts: Record<string, number>;
    created_by: string;
    /** The person's email, shown when the member directory cannot name created_by. */
    created_by_label?: string;
}

export interface RunIndexResponse {
    workflow_id: string;
    total: number;
    latest_run_id: string;
    runs: RunIndexEntry[];
}

export interface DecisionListResponse {
    workflow_id: string;
    total: number;
    decisions: CarriedDecision[];
    /** The `after` cursor for the next page; null on the last page. Absent
     *  from an ai_engine that did not page, which sent one page only. */
    next_after?: string | null;
}

export interface WorkflowDraftResponse {
    workflow: WorkflowDefinition;
    explanation: string;
    warnings: string[];
}

// --- HBW Quality Dashboard (PP-1077) ---
// ONE-OFF FEATURE — HBW's UCSF INC tenants only, not a general Primepoint
// capability, and gated by `features.quality_dashboard.enabled` (default OFF).
// These shapes describe one customer's inspection export; do not reuse them as
// a model for anyone else's dashboard.
//
// Produced by ai_engine/app/quality_dashboard/parser.py from an uploaded
// Parnassus inspection export. The payload is chart-ready: every roll-up
// happens server-side, so these shapes map one-to-one onto the panels and the
// page does no arithmetic.

/** One project group's IR summary tile, and its pass/fail roll-up. */
export interface QualityGroupStatus {
    group: string;
    contractorNew: number;
    qualityControlNew: number;
    openCount: number;
    totalPassed: number;
    totalFailedIr: number;
    failedOpen: number;
    pending: number;
    reviseReturn: number;
    total: number;
    successRate: number;
}

/** One project group in one month, across all three month-to-month panels.
 *  `month` is `YYYY-MM` (sortable); `label` is the axis text (`Aug-26`). */
export interface QualityGroupMonthPoint {
    group: string;
    month: string;
    label: string;
    opened: number;
    revisionRequired: number;
    passedNoRevision: number;
    passedWithRevisions: number;
    failedCount: number;
}

export interface QualityCompanySummary {
    company: string;
    totalPass: number;
    totalFail: number;
    successRate: number;
}

/** One subcontractor's Tower success rate in one month. `totalPass +
 *  totalFail` is the month's decided volume; it is 0 for a month the sub did
 *  no Tower work, where `successRate` is 0 and means "nothing decided" rather
 *  than "everything failed". */
export interface QualityCompanyMonthPoint {
    company: string;
    month: string;
    label: string;
    totalPass: number;
    totalFail: number;
    successRate: number;
}

/** OILs (issues) and NCRs (non-compliances) rolled up to a project group. */
export interface QualityGroupDdnc {
    group: string;
    oilOpen: number;
    oilClosed: number;
    ncrOpen: number;
    ncrClosed: number;
}

/** Per-project OIL counts. `shortTitle` is the chart axis label (`HMR 6A`);
 *  `group` is null for a project that appears in the DDNC sheet but on no
 *  group sheet. */
export interface QualityProjectDdnc {
    projectId: string;
    projectTitle: string;
    shortTitle: string;
    group: string | null;
    oilOpen: number;
    oilClosed: number;
}

/** The Tower's IRs decided under one increment, all time, from the export's
 *  `Inspection PassFail_INC (Tower)` sheet (HBW, round 4). An IR on no
 *  increment is on the Tower status tile only, so these sum to a little less. */
export interface QualityIncrementIr {
    increment: string;
    totalPass: number;
    totalFail: number;
    successRate: number;
}

/** The Tower's DDNCs under one increment, all time. `openCount` is every DDNC
 *  raised and not yet closed (exact); `closedCount` is everything else ever
 *  raised under it, so a finished increment (Inc 1) shows all closed. */
export interface QualityIncrementDdnc {
    increment: string;
    openCount: number;
    closedCount: number;
}

/** NCRs raised on the Tower under one increment, all time -- a count, not a
 *  status; the sheet behind it has no open/closed split. */
export interface QualityIncrementNcr {
    increment: string;
    ncrCount: number;
}

/** One subcontractor's Tower DDNCs. `openCount` is exact, as above; `closedCount`
 *  is only what closed since the company sheets' first month (no sheet gives a
 *  per-sub all-time count). The chart labels it plain "Closed" (round 4). */
export interface QualityCompanyDdnc {
    company: string;
    openCount: number;
    closedCount: number;
}

export interface QualityDashboardPayload {
    schemaVersion: number;
    groups: string[];
    status: QualityGroupStatus[];
    monthly: QualityGroupMonthPoint[];
    companies: QualityCompanySummary[];
    /** Absent on a payload stored before schema 2. Optional on purpose: the
     *  page renders a version range, so this field's absence is a normal
     *  state, not a violated invariant. */
    companyMonthly?: QualityCompanyMonthPoint[];
    ddncByGroup: QualityGroupDdnc[];
    ddncByProject: QualityProjectDdnc[];
    /** Absent before schema 3, and empty when the export's sheet could not be
     *  read or predates it -- the panel is then left out. Schema 3 also carried
     *  `irSince` / `ddncSince` / `ncrSince`; schema 4 dropped them (unread). */
    irByIncrement?: QualityIncrementIr[];
    ddncByIncrement?: QualityIncrementDdnc[];
    ddncByCompany?: QualityCompanyDdnc[];
    /** From the export's `DDNC Monthly_Cumulative by INC` sheet, first seen on the
     *  21 Sep 2026 export; empty for older ones. Schema 4's `ddncByIncrement` needs it too. */
    ncrByIncrement?: QualityIncrementNcr[];
    /** Why one of the optional charts above is empty: the parse error of the
     *  sheet behind it, shown in the chart's place. Absent on payloads stored
     *  before it existed, and no entry when the export predates the PassFail or
     *  cumulative sheet. */
    missingCharts?: Partial<Record<QualityChartKey, string>>;
}

/** The payload fields a chart can go missing from without failing the upload. */
export type QualityChartKey =
    "irByIncrement" | "companyMonthly" | "ddncByIncrement" | "ddncByCompany" | "ncrByIncrement";

/** SAS URLs for the tenant's logo and project rendering; null when it has none. */
export interface QualityBranding {
    logoUrl: string | null;
    bannerUrl: string | null;
}

/** A stored document link: a label and any address. Only http(s) addresses
 *  are rendered clickable; anything else shows as text. */
export interface QualityDocumentLink {
    label: string;
    url: string;
}

export interface QualityDocumentLinksResponse {
    links: QualityDocumentLink[];
}

/** A version in the picker. Carries no payload -- the list endpoint defers it. */
interface QualitySnapshotSummary {
    id: string;
    dataDate: string;
    sourceFilename: string;
    schemaVersion: number;
    uploadedBy: string | null;
    createdAt: string | null;
}

export interface QualitySnapshot extends QualitySnapshotSummary {
    payload: QualityDashboardPayload;
}

/** `truncated` is true when more versions exist than were returned, so the
 *  picker can say the history is cut rather than looking like the older
 *  versions were never uploaded. */
export interface QualitySnapshotListResponse {
    snapshots: QualitySnapshotSummary[];
    truncated: boolean;
}

/** `snapshot` is null when the tenant has uploaded nothing yet -- an empty
 *  dashboard is a normal state, not an error. */
export interface QualitySnapshotResponse {
    snapshot: QualitySnapshot | null;
}

/** One box the ai_engine find-similar route proposes for an exemplar
 *  (`POST /api/ai/entity-segmentation/similar/{sheet_hash}`). A cross-layer contract, so it
 *  lives here rather than beside the client feature that happens to call it. */
export interface SimilarMatch {
    bbox: [number, number, number, number];
    /** Orientation-channel agreement with the exemplar, 0..1 — the ranking signal. */
    score: number;
    corr: number;
}

export interface ApiSourceFields {
    context?: {
        key: string;
        label: string;
        state: string;
        checked_at?: string;
        retained?: boolean;
        items: {
            id: string;
            title: string | null;
            revision: string | null;
            status: string | null;
            date: string | null;
        }[];
    }[];
    state: "local" | "cached";
    checkedAt: string | null;
    detailAvailable?: boolean;
    syncStatus?: string;
    truncated: boolean;
    fields: {
        id: string;
        label: string;
        type: string;
        value: unknown;
        hasValue: boolean;
        definitionAvailable: boolean;
    }[];
}

/** Reviewed specification ranges use physical PDF pages, one-based and inclusive. */
export interface SpecOutlineSection {
    section_group_id: string | null;
    group_code: string;
    group_title: string;
    start_page: number;
    end_page: number;
}

export interface SpecOutlineExcludedRange {
    start_page: number;
    end_page: number;
    reason: string;
}

export interface SpecOutlineSource {
    file_id: string;
    file_name: string;
    page_count: number;
    version: string;
    sections: SpecOutlineSection[];
    locked_section_group_ids?: string[];
    excluded_ranges: SpecOutlineExcludedRange[];
    issues: Array<{ code: string; message: string }>;
    review_issues?: Array<{
        code: string;
        message: string;
        section_group_id?: string;
        group_code?: string;
    }>;
}

export interface SpecOutlineResponse {
    revision_id: string;
    editable: boolean;
    edit_blocked_reason?: string | null;
    sources: SpecOutlineSource[];
}

export interface SaveSpecOutlineRequest {
    expected_version: string;
    sections: SpecOutlineSection[];
    excluded_ranges: SpecOutlineExcludedRange[];
}

/** Document-control operations identify exact content versions by UUID. */
export interface DocumentControlSheet {
    identity_verification?: ApiIdentityVerification | null;
    revision_number?: number;
    discipline?: string | null;
    discipline_id?: string | null;
    status: SgStatus | "active";
    section_group_id: string;
    group_code: string;
    group_title: string;
    area_id: string | null;
    revision_id: string | null;
    state_token: string;
    is_active: boolean;
    file_id?: string | null;
    start_page_in_file?: number | null;
    source?: {
        file_name?: string;
        page?: number;
        zip_entry?: string | null;
        ingest_id?: string;
    } | null;
    predecessor_ids?: string[];
    historical?: boolean;
    drawing_page_evidence?: {
        superseded: boolean;
        annotation_count?: number;
        superseded_sources?: Array<{
            kind: string;
            annotation_type?: string;
            field?: string;
            text?: string;
        }>;
    } | null;
}

export interface DocumentCorrectionResult {
    operation_id: string;
    generation: number;
    undone: boolean;
}

/** Exactly one of the two: an existing set, or one this request creates.
 *  Optional-and-optional let a caller send both or neither, which the server
 *  rejects — a union makes that unrepresentable instead. */
export type VersionSetTarget =
    { revision_id: string } | { new_revision: { name: string; issue_date?: string } };

export type VersionSetAssignmentRequest = {
    operation_id: string;
    expected_generation: number;
    section_group_ids: string[];
} & VersionSetTarget;

export interface VersionSetAssignmentResult {
    operation_id: string;
    generation: number;
    revision_id: string;
    revision_name: string;
    moved_section_group_ids: string[];
    activated_section_group_ids: string[];
    deactivated_section_group_ids: string[];
    undone: boolean;
}
export interface DocumentCorrectionUndoStatus extends DocumentCorrectionResult {
    document_id: string;
    document_type: "drawing" | "csi";
    reason: string;
    created_at: string;
    version_count: number;
    can_undo: boolean;
    unavailable_reason: string | null;
}
/** One reviewer-chosen target -> candidate pairing, as sent in a replacement
 *  request and echoed back in the manifest. */
export interface ReplacementMapping {
    target_section_group_id: string;
    candidate_section_group_id: string;
}
export interface ReplacementTargetsRequest {
    section_group_ids: string[];
    mappings?: ReplacementMapping[];
    held_section_group_ids?: string[];
    reviewed_target_versions?: Record<string, string>;
}
export interface ReplacementManifest {
    revision_id: string;
    generation: number;
    targets: DocumentControlSheet[];
    matches: {
        target_section_group_id: string;
        candidate_section_group_ids: string[];
        status: "matched" | "missing" | "ambiguous" | "held" | "stale";
    }[];
    mappings?: ReplacementMapping[];
    held_section_group_ids?: string[];
}

export interface DocumentBulkRequest {
    section_group_ids: string[];
    action: "metadata" | "archive" | "restore";
    changes?: { group_title?: string; discipline_code?: string | null; area_id?: string | null };
    reason: string;
}
export interface DocumentBulkPreview {
    preview_token: string;
    items: {
        section_group_id: string;
        before: Record<string, unknown>;
        after: Record<string, unknown>;
    }[];
    warnings: string[];
}
export interface ArchivedDocumentVersion {
    section_group_id: string;
    group_code: string;
    group_title: string;
    area_id: string | null;
    revision_id: string;
    discipline_code: string | null;
    is_active: false;
    archived_at: string;
    archived_by: string;
    archive_reason: string;
}

export interface DocumentControlIssue {
    issue_date?: string | null;
    revision_id: string;
    name: string;
    status: RevisionStatus;
    total_count: number;
    current_count: number;
    historical_count: number;
    /** Of `historical_count`, the versions a later complete reissue withdrew. */
    withdrawn_count?: number;
    /** Absent on a server without complete reissues: read as "revision". */
    set_type?: "revision" | "reissue";
    section_group_ids: string[];
}

export interface TransmittalVersion {
    section_group_id: string;
    document_id: string;
    group_code: string;
    group_title: string | null;
    area_id: string | null;
    area_name: string | null;
    revision_id: string | null;
    revision_name: string | null;
    file_id: string;
    file_name: string;
    source_sha256: string | null;
    start_page_in_file: number;
    end_page_in_file: number;
}

export interface TransmittalPreview {
    preview_token: string;
    items: TransmittalVersion[];
    version_count: number;
}

export interface TransmittalSummary {
    id: string;
    title: string;
    document_id: string;
    actor_id: string;
    actor_name?: string | null;
    created_at: string;
    version_count: number;
}

export interface TransmittalRecord extends TransmittalSummary {
    note: string;
    items: TransmittalVersion[];
}

export interface CreateTransmittalRequest {
    operation_id: string;
    section_group_ids: string[];
    preview_token: string;
    title: string;
    note: string;
}

export interface DocumentControlActivityEvent {
    id: string;
    document_id: string;
    kind: string;
    created_at: string;
    actor_id: string | null;
    actor_name?: string | null;
    summary: string | null;
    source_id: string | null;
    section_group_id: string | null;
    revision_id: string | null;
    transmittal_id: string | null;
    operation_id: string | null;
}

export interface DistributionPage<T> {
    items: T[];
    has_more: boolean;
    offset: number;
}

/** Additive tenancy contract. Absence/null preserves the legacy organization session. */
export interface TenantCompany {
    company_id: string;
    slug: string;
    name: string;
    state: string;
    /** Company-admin Role projection for the authenticated company only; [] for every other company and for legacy project-scoped credentials. */
    capabilities: string[];
    route_generation?: number | null;
}
export interface TenantProject {
    project_id: string;
    company_id: string;
    slug: string;
    name: string;
    state: string;
    role: "super_admin" | "project_admin" | "member";
    capabilities: string[];
    route_generation: number;
    /** Display deduplication only; never membership or admission evidence. */
    provider_organization_id?: string | null;
}
export interface SessionTenancy {
    url_routing?: boolean;
    /** Verified source binding hint for a naked URL, never a scope ceiling. */
    canonical_redirect_project_id?: string | null;
    projects_next_cursor?: string | null;
    companies_next_cursor?: string | null;
    auth_scope: "legacy_org" | "project" | "company";
    legacy_project_id: string | null;
    companies: TenantCompany[];
    projects: TenantProject[];
    is_platform_identity: boolean;
    /** A centrally governed multi-company identity. It is display/provenance
     * only until the user selects one of the granted companies. */
    master_organization?: boolean;
}
export interface TenantSessionResponse {
    member: {
        member_id: string;
        email_address: string;
        name: string;
        roles: { role_id: string }[];
    };
    organization: {
        organization_id: string;
        organization_slug: string;
        organization_name?: string;
        trusted_metadata?: Record<string, unknown>;
    };
    session: { expires_at: string };
    tenancy?: SessionTenancy | null;
    routing_admission?: CompanySessionResponse | null;
    project_urls_available?: boolean;
}
export interface CompanySessionResponse {
    /** Server-owned Access namespace; URL catalogue IDs are not logical project IDs. */
    access_project?: string | null;
    url_route_id?: string | null;
    session_jwt: string;
    exp: number;
    session: TenantSessionResponse;
    selected_company_id: string;
    selected_project_id: string | null;
    canonical_company_slug: string;
    canonical_project_slug: string | null;
    legacy_url: boolean;
    canonical_url_alias?: boolean;
}

/** Centrally governed Mode C organization management. Every association and
 * member row has its own generation for server-enforced compare-and-swap. */
export interface MasterOrganizationMember {
    member_id: string;
    tier: string;
    state: string;
    generation: number;
}
export interface MasterOrganizationCompany {
    company_id: string;
    name: string;
    slug: string;
    state: string;
    generation: number;
}
export interface MasterOrganization {
    organization_id: string;
    anchor_company_id: string;
    state: string;
    generation: number;
}
export interface MasterOrganizationsResponse {
    items: MasterOrganization[];
    next_cursor: string | null;
}
export interface MasterOrganizationCompaniesResponse {
    items: MasterOrganizationCompany[];
    next_cursor: string | null;
}
export interface MasterOrganizationMembersResponse {
    items: MasterOrganizationMember[];
    next_cursor: string | null;
}
export interface MasterOrganizationProjectMember {
    member_id: string;
    tier: string;
}
export interface MasterOrganizationProjectMembersResponse {
    items: MasterOrganizationProjectMember[];
    next_cursor: string | null;
}

/** Shared wire contract for /admin/tenancy. Actions and readiness come from the server. */
export type TenancyPublicRole = "super_admin" | "project_admin" | "member";
export type TenancyPlanKind =
    "adopt_tenant" | "create_company" | "add_project" | "reassign_project" | "migrate_project";
export type TenancyOperationAction =
    | "pause"
    | "resume"
    | "verify"
    | "cutover"
    | "rollback"
    | "retire_rollback"
    | "abandon"
    | "discard";
export type TenancyIsolationMode = "shared" | "company" | "dedicated";
export type TenancyOperationStatus =
    | "queued"
    | "running"
    | "paused"
    | "needs_attention"
    | "ready_for_cutover"
    | "completed"
    | "rolled_back"
    | "failed";

export interface TenancyAccess {
    role: TenancyPublicRole;
    scope: "platform" | "company" | "project";
}

export interface TenancyPlacement {
    database_name: string;
    isolation_mode: TenancyIsolationMode;
    storage_target_id: string | null;
    route_generation: number;
    state: string;
}

interface TenancyNodeBase {
    id: string;
    name: string;
    slug: string;
    access: TenancyAccess | null;
    available_kinds: TenancyPlanKind[];
}
export interface TenancyCompany extends TenancyNodeBase {
    kind: "company";
    project_count: number;
}
export interface TenancyProject extends TenancyNodeBase {
    kind: "project";
    company_id: string;
    placement: TenancyPlacement;
    transition: { operation_id: string; status: TenancyOperationStatus; phase: string } | null;
}
export interface TenancyLegacyTenant extends TenancyNodeBase {
    kind: "legacy_tenant";
    organization_id: string;
    database_name: string;
    adoption_status: "not_adopted" | "blocked";
    blocked_reason: string | null;
}
export type TenancyTreeNode = TenancyCompany | TenancyProject | TenancyLegacyTenant;
export interface TenancyStorageTarget {
    id: string;
    name: string;
    description: string;
    isolation_mode: TenancyIsolationMode;
}
export interface TenancyTreeRequest {
    view?: "companies" | "legacy";
    company_id?: string;
    cursor?: string;
    limit?: number;
    q?: string;
}
export interface TenancyTreePage {
    items: TenancyTreeNode[];
    next_cursor: string | null;
    available_kinds: TenancyPlanKind[];
    storage_targets: TenancyStorageTarget[];
    can_provision_legacy_tenant: boolean;
}

export type TenancyPlanRequest =
    | { kind: "create_company"; name: string; slug: string; decisions?: string[] }
    | {
          kind: "add_project";
          company_id: string;
          name: string;
          slug: string;
          isolation_mode: TenancyIsolationMode;
          target_storage_id?: string;
          decisions?: string[];
      }
    | {
          kind: "adopt_tenant";
          legacy_tenant_id: string;
          target_company_id: string;
          project_name: string;
          project_slug: string;
          member_mappings: TenancyAdoptionMemberMapping[];
          decisions?: string[];
      }
    | {
          kind: "reassign_project";
          project_id: string;
          source_company_id: string;
          target_company_id: string;
          decisions?: string[];
      }
    | {
          kind: "migrate_project";
          project_id: string;
          company_id: string;
          target_storage_id: string;
          isolation_mode?: TenancyIsolationMode;
          decisions?: string[];
      };

export interface TenancyAdoptionMemberMapping {
    source_member_id: string;
    target_member_id: string;
}

// PP-827 refusals: every refusal the control plane emits carries a verdict and
// the one action that resolves it. Mirrors app/tenancy_admin_contracts.py and
// app/tenancy_refusals.py. Owned by FrontendCore; ping before editing.
export type TenancyVerdict = "retry" | "refresh" | "fix_input" | "decide" | "hard_stop";
export type TenancyRemedy =
    "retry" | "refresh_plan" | "discard" | "fix_input" | "acknowledge" | "alternative";
export interface TenancyChange {
    label: string;
    before: string | null;
    after: string | null;
}
export interface TenancyRefusal {
    code: string;
    verdict: TenancyVerdict;
    title: string;
    what: string;
    why: string;
    remedies: TenancyRemedy[];
    diff: TenancyChange[] | null;
    consequence: string | null;
    alternative: string | null;
    field: string | null;
    suggestion: string | null;
    /** fix_input on member_mappings: legacy members who already exist in the company by email. */
    mappings?: TenancyMappingSuggestion[] | null;
}
export interface TenancyMappingSuggestion {
    source_member_id: string;
    email: string;
    candidate_target_member_id: string;
}
export interface TenancyDecision {
    code: string;
    consequence: string;
    acknowledged_by: string;
    acknowledged_at: string;
}
/** Fallback facts for a refusal code, used when a 409 body lacks the full Refusal. */
export interface TenancyRefusalCatalogEntry {
    verdict: TenancyVerdict;
    title: string;
    consequence: string | null;
    alternative: string | null;
}
export interface TenancyRefusalCatalogResponse {
    catalog: Record<string, TenancyRefusalCatalogEntry>;
}

export interface TenancyReadinessCheck {
    code: string;
    label: string;
    status: "ready" | "blocked" | "warning" | "pending" | "decision" | "acknowledged";
    detail: string;
    refusal?: TenancyRefusal | null;
}
export interface TenancyPlan {
    plan_id: string;
    plan_hash: string;
    kind: TenancyPlanKind;
    title: string;
    summary: string;
    created_at: string;
    expires_at: string;
    changes: TenancyChange[];
    readiness: TenancyReadinessCheck[];
    can_apply: boolean;
    decisions: TenancyDecision[];
    /** Set on a plan produced by refreshing a stuck operation: the predecessor and what changed since. */
    refreshed_from?: { run_id: string; diff: TenancyChange[] } | null;
}
export interface TenancyApplyRequest {
    plan_id: string;
    plan_hash: string;
    wave_id?: string;
}
export interface TenancySourceIntegrity {
    status: "pass" | "blocked" | "unknown";
    checked_at: string;
    checked: number;
    not_valid: number;
    stale?: boolean;
    reason?: string;
    findings: { table: string; constraint: string; reason: string }[];
}

export interface TenancyAdoptionWave {
    id: string;
    company_id: string;
    version: number;
    state: "active" | "closed";
    current: number | null;
    items: {
        request: Extract<TenancyPlanRequest, { kind: "adopt_tenant" }>;
        operation_id?: string;
        status: "pending" | "running" | "needs_attention" | "completed" | "skipped";
    }[];
}
export type TenancyCompanyAdministratorPlanRequest =
    | { company_id: string; email: string; name: string }
    | { company_id: string; company_member_id: string };
export interface TenancyCompanyAdministratorPlan {
    plan_id: string;
    plan_hash: string;
    company_id: string;
    actor_member_id: string;
    validation: {
        ready: boolean;
        company_role: "super_admin";
        project_ids: [];
        manifest_sha256: string;
    };
}
export interface TenancyCompanyAdministratorApplyResponse {
    plan_id: string;
    plan_hash: string;
    company_id: string;
    actor_member_id: string;
    receipt: Record<string, unknown>;
}
export interface TenancyOperationActionRequest {
    action: TenancyOperationAction;
    expected_version: number;
}
export interface TenancyOperationError {
    code: string;
    message: string;
    retryable: boolean;
    refusal?: TenancyRefusal | null;
}
export interface TenancyOperation {
    id: string;
    version: number;
    plan_id: string;
    kind: TenancyPlanKind;
    title: string;
    company_id: string | null;
    project_id: string | null;
    status: TenancyOperationStatus;
    phase: string;
    progress: { completed: number; total: number | null; unit: string };
    readiness: TenancyReadinessCheck[];
    error: TenancyOperationError | null;
    available_actions: {
        action: TenancyOperationAction;
        enabled: boolean;
        reason: string | null;
    }[];
    created_at: string;
    updated_at: string;
    events: { at: string; label: string; detail: string; status: string }[];
    decisions: TenancyDecision[];
    supersedes: string | null;
}
export interface TenancyOperationsPage {
    items: TenancyOperation[];
    next_cursor: string | null;
}

// PP-827 setup: prerequisites moved into the UI (Setup & health, Storage,
// Legacy blockers, Schema review) plus the wave-adoption client orchestration.
// Mirrors app/tenancy_setup.py and the legacy-blockers/health routes added to
// app/tenancy_admin.py. Owned by FrontendTabs; ping before editing.
export type TenancyHealthStatus = "ok" | "warn" | "fail";
export interface TenancyHealthCheck {
    code: string;
    label: string;
    status: TenancyHealthStatus;
    detail: string;
    fix: string | null;
}
export interface TenancyHealthResponse {
    checks: TenancyHealthCheck[];
}

export interface TenancySetupStorageTarget {
    id: string;
    name: string;
    database_name: string;
    isolation_mode: TenancyIsolationMode;
    owner_company_id: string | null;
    owner_project_id: string | null;
    occupancy: "empty" | "managed";
    placement_count: number;
    server_major: number;
    state: "active" | "retired";
}
export interface TenancyStorageTargetPreviewRequest {
    name: string;
    db_name: string;
    isolation_mode: TenancyIsolationMode;
    owner_company_id?: string;
    owner_project_id?: string;
    generate_owner_project_id?: boolean;
}
/** Flat read-only attestation of one physical target, reusing
 * scripts/tenant_migration/storage_targets.py::inspect_target's shape. */
export interface TenancyStorageAttestation {
    target: {
        name: string;
        db_name: string;
        isolation_mode: TenancyIsolationMode;
        owner_company_id: string | null;
        owner_project_id: string | null;
    };
    endpoint: { host: string; port: number; database: string };
    postgres: { server_version_num: string; server_major: number };
    operator_capability: {
        superuser: boolean;
        create_role: boolean;
        create_database: boolean;
        create_schema: boolean;
    };
    runtime_acl_catalog: boolean;
    occupancy: "empty" | "managed";
    placement_count: number;
    review_sha256: string;
}
export interface TenancyStorageTargetRegisterRequest extends TenancyStorageTargetPreviewRequest {
    review_sha256: string;
}

export interface TenancySchemaReviewSource {
    db_name: string;
    schema_name: string;
    generation: number;
}
export interface TenancySchemaReview {
    present: boolean;
    source: TenancySchemaReviewSource | null;
    source_head: string | null;
    schema_ddl_digest: string | null;
    captured_at: string | null;
}

export interface TenancySchemaCapture {
    project_id: string;
    source: TenancySchemaReviewSource;
    source_head: string;
    source_catalog_digest: string;
    schema_ddl_digest: string;
    captured_at: string | null;
    captured_by: string | null;
}

/** GET /admin/tenancy/legacy/{organization_id}/blockers -- computed once per
 * legacy organization so the Legacy tenants list can show what adopting it
 * will do before an operator clicks Adopt. */
export interface TenancyLegacyBlockers {
    database_present: boolean;
    member_count: number;
    admin_count: number;
    mfa_enrolled: number;
    sso_active: boolean;
    scim_active: boolean;
    custom_roles: string[];
    inactive: boolean;
    refusals: TenancyRefusal[];
}

/** Canonical OAuth authorize request, parsed from `/api/ai/session/oauth/authorize`'s
 *  query string -- mirrors the ai_engine wire shape (CanonicalOAuthConsent.tsx). */
export interface OAuthRequest {
    client_id: string;
    redirect_uri: string;
    response_type: "code";
    scopes: string[];
    prompt?: "consent";
    state?: string;
    nonce?: string;
    code_challenge?: string;
    code_challenge_method?: "S256";
    resources?: string[];
}

/** Response body for `/api/ai/session/oauth/authorize-start` (CanonicalOAuthConsent.tsx). */
export interface ConsentResponse {
    company_id: string;
    project_id: string;
    client: { client_id: string; client_name: string; client_description?: string | null };
    scopes: { scope: string; description: string }[];
    consent_required: true;
}

// --- Connection health station (ai_engine /connection-health, PP-1109) ---
// Payload shapes mirror app/routers/connection_health_router.py and the definitive
// contract, ai_engine's document_cache/plans/pp1109_api_contract.ts: camelCase at the
// boundary, a missing value is `null` (never an omitted key), ids are uuid strings,
// timestamps are ISO-8601 UTC strings, and `seq` is the total order of snapshots.
// Tone always comes from the server (`severity`, `verdict`); the client never derives
// it from the sign of a delta, because a gain can be the alarm. Endpoints, query
// params and status codes are documented per call in client/src/api/connectionHealth.ts.

/** What asked for the capture. Kick-off is NOT a trigger: it is the `isKickoff` flag. */
export type ConnectionHealthTrigger = "daily" | "revision" | "manual";

/**
 * "captured" = state rows and totals are committed, diff and checks have not run yet
 * (transient; a crash here is recovered by recomputing the checks).
 * "completed" = diffed and judged. "failed" = the capture raised; `error` says why.
 */
export type ConnectionHealthSnapshotStatus = "captured" | "completed" | "failed";

/** Worst finding severity of a completed snapshot. "Not judged" is `null`, not a member. */
export type ConnectionHealthVerdict = "pass" | "warn" | "fail";

export type ConnectionHealthSeverity = "fail" | "warn" | "info";

export type ConnectionHealthCategory =
    "sheets" | "components" | "tables" | "connections" | "destinations";

/**
 * Where a finding belongs: one of the five content categories, or "capture" for the
 * findings about the capture itself (sheets in flight, another revision running, hash
 * churn). No sheet changes in "capture", so it has no variation table, no timeline line
 * and no `category` filter of /sheets; a capture finding is drilled into by `check` alone.
 */
export type ConnectionHealthFindingCategory = ConnectionHealthCategory | "capture";

/**
 * Comparison target. "previous" is the stored reference (the previous completed
 * snapshot); there is no separate "reference" target. Any other string is the uuid of
 * an older completed snapshot. Widened with `(string & {})` so the literals survive.
 */
export type ConnectionHealthAgainst = "previous" | "kickoff" | (string & {});

/**
 * Why a sheet changed inside the compared window.
 * "revised" = in the scope of a sheet_processing workflow that overlaps the window;
 * "human" = route cells whose user-sourced count grew; "unexplained" = neither.
 * Tolerant checks run on revised sheets, strict checks on unexplained sheets, and
 * human edits are info at any volume.
 */
export type ConnectionHealthAttribution = "revised" | "human" | "unexplained";

/**
 * Class of a destination key. "schedule" = a named table/schedule destination (ROOM,
 * DOOR, ...); "DRAWING"; "internal" = CUSTOM_* / DIRECT_*; "reserved" = GRID, SPEC,
 * NOT_A_TAG, CLEARED, MISSED_DETECTION; "none" = ~unnamed / ~no_taglist.
 */
export type ConnectionHealthDestinationClass =
    "schedule" | "DRAWING" | "internal" | "reserved" | "none";

/**
 * What a move cell means.
 * "move" = schedule name -> a different schedule name (the headline case), and the
 *   fallback for any cell no other kind covers (e.g. CUSTOM_* -> ~unnamed);
 * "rename" = the to-name is new and the from-name drained >=95% (ROOM -> ROOMS);
 * "name_lost" = schedule name -> ~unnamed / ~no_taglist / CUSTOM_* / DIRECT_* / DRAWING;
 * "cleared" = into a reserved name (CLEARED, NOT_A_TAG, GRID, ...);
 * "unsuppressed" = CLEARED / NOT_A_TAG -> a connectable name;
 * "improvement" = none / internal -> a schedule name.
 */
export type ConnectionHealthMoveKind =
    "move" | "rename" | "name_lost" | "cleared" | "unsuppressed" | "improvement";

/**
 * Why one unit (a tag whose destination changed, a table, a sheet title, ...) changed.
 * "expected" = the change is attributed to a revision: the sheet is in the scope of a
 * sheet_processing workflow of the compared window and the unit is a real drawing
 * change of that re-issue (a lost tag whose text left the drawing, a gained tag, a tag
 * that moved between destinations on the re-issued sheet, or a sheet, component or
 * table change on it, including a sheet the revision added or removed); "user" = a
 * person did it (user-sourced route cells, a user-owned table config); "suspicious" =
 * everything else, i.e. probable data loss. Only suspicious units drive the verdict
 * and the red colours; the labels are exactly "Suspicious", "Expected (revision)" and
 * "User edit".
 */
export type ConnectionHealthCause = "suspicious" | "expected" | "user";

/** Units of change split by cause; `total` is the sum of the three. */
export interface ConnectionHealthCauseCounts {
    total: number;
    suspicious: number;
    expected: number;
    user: number;
}

/**
 * The kind of a group. Connections: "lost" | "gained"; destinations: "moved"; sheets /
 * components / tables: the id of the check that named the sheets (ConnectionHealthKnownCheck
 * of that category), which is also the group's key.
 */
export type ConnectionHealthGroupKind = "lost" | "gained" | "moved" | (string & {});

/**
 * Check ids the server emits today. `ConnectionHealthFinding.check` stays `string` so a
 * new check never breaks the client; use this union to key presentation copy.
 */
export type ConnectionHealthKnownCheck =
    | "sheet.removed"
    | "sheet.went_dark"
    | "sheet.duplicate_code"
    | "sheet.id_changed"
    | "sheet.title_changed"
    | "comp.wipe"
    | "comp.type_drop"
    | "table.renamed_with_inbound"
    | "table.parse_regression"
    | "table.key_explosion"
    | "table.inbound_drop"
    | "table.name_lump"
    | "table.human_config_lost"
    | "table.config_changed"
    | "conn.sheet_wipe"
    | "conn.sheet_drop"
    | "conn.loss_total"
    | "conn.inbound_collapse"
    | "conn.user_owned_lost"
    | "conn.dup_endpoints"
    | "conn.no_text_box"
    | "dest.move_named"
    | "dest.name_lost"
    | "dest.machine_cleared"
    | "dest.unsuppressed"
    | "dest.cleared_with_destination"
    | "dest.surge"
    | "dest.twin_texts"
    | "untouched.changed"
    | "daily.machine_swing"
    | "daily.human_edits"
    | "capture.in_flight"
    | "capture.other_revision"
    | "capture.hash_churn"
    | "revision.expected_changes";

/**
 * Body of every 4xx/5xx except a FastAPI validation 422. Only the two literal details
 * are contractual: 503 "migration pending" (every endpoint except /fleet, which flags
 * it per row) and 409 "capture in progress" (POST only). The page shows "not available
 * yet" on 404 and 503, because the frontend ships before the API.
 */
export interface ConnectionHealthErrorBody {
    detail: "migration pending" | "capture in progress" | (string & {});
}

/**
 * A sheet_processing workflow that reached a terminal state and is attached to the
 * snapshot that first saw its result. Many milestones can share one snapshot (bursts
 * coalesce into one capture).
 */
export interface ConnectionHealthMilestone {
    workflowId: string;
    revisionId: string | null;
    revisionName: string | null;
    outcome: "completed" | "failed";
    /** The workflow's terminal timestamp (not the capture time). */
    completedAt: string;
}

/** Lean row: timeline point, history row, fleet cell. Never carries findings, totals or payloads. */
export interface ConnectionHealthSnapshotRow {
    id: string;
    seq: number;
    trigger: ConnectionHealthTrigger;
    status: ConnectionHealthSnapshotStatus;
    /**
     * Null = not judged: status is not "completed", or the snapshot had no reference
     * (first capture, nothing to compare; the UI shows a neutral banner).
     */
    verdict: ConnectionHealthVerdict | null;
    /**
     * Worst finding severity per category, stored at capture; drives the category chips
     * (the "capture" entry is shown only when it is set). Null until completed.
     */
    categoryVerdicts: Record<
        ConnectionHealthFindingCategory,
        ConnectionHealthSeverity | null
    > | null;
    findingCount: number;
    /** Sheets that changed vs the reference (the bar height of the health band). */
    sheetsAffected: number;
    /** Tags whose destination name changed vs the reference (the timeline's move scalar). */
    tagsMoved: number;
    /** Sheets not `processed` at capture: carried at their last processed state, not compared. */
    inFlightSheets: number;
    /** Sheet state rows this capture wrote (0 on a no-op recapture; a spike is hash churn). */
    sheetRowsWritten: number;
    /** Kick-off baseline flag; at most one snapshot per tenant. */
    isKickoff: boolean;
    /** "auto" or the email of the internal admin who pinned it. Null unless `isKickoff`. */
    kickoffSetBy: string | null;
    /** Null unless `isKickoff`. */
    kickoffSetAt: string | null;
    /** Newest first, at most 50; `milestoneCount` is the true number. */
    milestones: ConnectionHealthMilestone[];
    milestoneCount: number;
    /**
     * The snapshot this one was compared against: always the previous completed
     * snapshot. Null for the first capture and while status is not "completed".
     */
    referenceId: string | null;
    createdAt: string;
    /** Email for a manual capture; null for daily and revision captures. */
    createdBy: string | null;
    /**
     * Set when the capture or its checks raised: on a "failed" row, and on a "captured" row
     * whose last check attempt failed (retried by the next capture; failed for good after 3).
     * Cleared when a retry completes the row.
     */
    error: string | null;
    /**
     * Which phase raised on a "failed" row: "capture" (no state was saved; the detail's
     * `totals` is null) or "checks" (the state is saved; the checks raised on every attempt,
     * or a later completed window superseded the row). Null on every other row, so the
     * label never has to guess it from the totals.
     */
    failedPhase: "capture" | "checks" | null;
}

/**
 * Tenant aggregates over active Drawing sheets, computed in the capture statement.
 * Context only: detection is per sheet. The per-table-name roll-up the checks use
 * (table.name_lump) stays server-side and is not exposed.
 */
export interface ConnectionHealthTotals {
    /** `unprocessed` is the same population as the row's `inFlightSheets`. */
    sheets: { total: number; titled: number; unprocessed: number; duplicateCodes: number };
    /** `byType` is keyed by section type (drawing, note, table, legend, component, title_area, ...). */
    components: { total: number; byType: Record<string, number> };
    tables: {
        total: number;
        named: number;
        internal: number;
        unnamed: number;
        parseFailed: number;
        noCells: number;
        humanEdited: number;
    };
    connections: {
        /** Visible connections (serving projection), not physical rows. */
        total: number;
        resolved: number;
        /** Connections whose taglist destination name was set by a user. */
        userOwned: number;
        duplicateEndpoints: number;
        noTextBox: number;
        /** Keyed by ConnectionHealthDestinationClass. */
        byClass: Record<string, number>;
        byTargetType: Record<string, number>;
    };
    /**
     * `names` = distinct destination keys after CUSTOM_* and DIRECT_* are collapsed.
     * `byName` holds at most 40 names plus "__other__".
     */
    destinations: { names: number; byName: Record<string, number> };
}

/** Columnar series aligned index-for-index with `timeline.points`. */
export interface ConnectionHealthSeries {
    /**
     * Dotted path into ConnectionHealthTotals, e.g. "connections.total",
     * "components.byType.table", "connections.byClass.schedule",
     * "destinations.byName.ROOM". ConnectionHealthVariationRow.key uses the same space.
     */
    key: string;
    label: string;
    category: ConnectionHealthCategory;
    /** "total" series are always drawn; "part" series are the by-type lines. */
    role: "total" | "part";
    /** Null where the point has no totals (failed capture). */
    values: (number | null)[];
}

/** A completed snapshot with its totals (kick-off and latest on the overview). */
export interface ConnectionHealthSnapshotWithTotals extends ConnectionHealthSnapshotRow {
    totals: ConnectionHealthTotals;
}

/** Revision milestones of one UTC day: one reference line on the timeline. */
export interface ConnectionHealthMilestoneDay {
    /** UTC calendar day of the workflows' terminal timestamps, "YYYY-MM-DD". */
    day: string;
    /** sheet_processing workflows that reached a terminal state that day. */
    total: number;
    /** Of `total`, workflows whose outcome is "failed". */
    failed: number;
    /** Milestone recorded but no snapshot attached yet (the next capture attaches it). */
    pending: number;
    /**
     * Terminal workflows with no milestone row at all, derived at read time; counted
     * only from the first snapshot's `createdAt` onward.
     */
    missing: number;
    /** Distinct revision names, at most 3; label = the name when single, else "N revisions". */
    revisionNames: string[];
    /** Worst verdict among the snapshots this day's milestones attach to (line colour). */
    worstVerdict: ConnectionHealthVerdict | null;
}

/** GET /connection-health/overview: everything above the fold in one call. */
export interface ConnectionHealthOverview {
    /**
     * The designated kick-off snapshot; when none is designated yet, the earliest
     * completed snapshot with `provisionalKickoff: true`. Null with zero completed snapshots.
     */
    kickoff: ConnectionHealthSnapshotWithTotals | null;
    /** True when `kickoff` is the provisional baseline (its `isKickoff` is false). */
    provisionalKickoff: boolean;
    /** Latest COMPLETED snapshot. */
    latest: ConnectionHealthSnapshotWithTotals | null;
    /** Newest failed capture, only when it is newer than `latest`. */
    lastFailedCapture: ConnectionHealthSnapshotRow | null;
    /** The fleet-wide daily sweep schedule (not per tenant). */
    schedule: { dailyEnabled: boolean; dailyCron: string | null };
    /** Worst snapshots in the trailing window, so a later green daily cannot bury a red milestone. */
    attention: {
        /** Always 7. */
        windowDays: number;
        worstVerdict: ConnectionHealthVerdict | null;
        /** Warn and fail snapshots of the window, worst first then newest, at most 5. */
        snapshots: ConnectionHealthSnapshotRow[];
    };
    timeline: {
        /** Echo of the `days` param. */
        days: number;
        /** Ascending by seq, every status, at most 1000; `truncated` when older points were dropped. */
        points: ConnectionHealthSnapshotRow[];
        /** `destinations.byName.*` limited to the top 12 names over the window + "__other__". */
        series: ConnectionHealthSeries[];
        /** Ascending by day; only days that have at least one milestone or missing workflow. */
        milestoneDays: ConnectionHealthMilestoneDay[];
        truncated: boolean;
    };
    /** Internal admins only; the client mounts the fleet tab from this instead of probing a 403. */
    canViewFleet: boolean;
    /** Internal admins only. */
    canSetKickoff: boolean;
}

export interface ConnectionHealthSheetRef {
    /** `documentId|areaId|rawCode`; stable identity of the sheet row across snapshots. */
    sheetKey: string;
    /** Raw visible sheet code (display only; deep links use the uuids below). */
    code: string;
    /** Title as of the snapshot (last known title for a removed sheet). */
    title: string | null;
    documentId: string;
    /** Section group as of the snapshot (may be superseded later). */
    sectionGroupId: string | null;
    /** Live lookup at read time; null when the sheet is no longer active. Use this for "Open sheet". */
    currentSectionGroupId: string | null;
}

export interface ConnectionHealthFinding {
    /** Stable check id (see ConnectionHealthKnownCheck); also the `check` filter of /sheets. */
    check: string;
    category: ConnectionHealthFindingCategory;
    severity: ConnectionHealthSeverity;
    /** Plain and specific, e.g. "120 ROOM tags now point to DOOR on 4 sheets". */
    title: string;
    detail: string;
    /**
     * Attribution of the sheets this finding is about. Null when it does not apply
     * (tenant-level and capture.* findings).
     */
    attribution: ConnectionHealthAttribution | null;
    before: number | null;
    after: number | null;
    /** For destination findings: the move this finding is about. */
    move: { from: string; to: string; tags: number } | null;
    /** At most 10 refs; `sheetCount` is the true number. */
    sheets: ConnectionHealthSheetRef[];
    sheetCount: number;
}

/** Revision-quality report link, resolved at read time and never stored. */
export interface ConnectionHealthQualityReportLink {
    id: string;
    status: string;
    verdict: string | null;
}

export interface ConnectionHealthMilestoneDetail extends ConnectionHealthMilestone {
    /** Null when the workflow has no revision-quality report. */
    qualityReport: ConnectionHealthQualityReportLink | null;
}

/** One overview card: the snapshot's changes in one category, compared with the previous snapshot. */
export interface ConnectionHealthCard {
    category: ConnectionHealthCategory;
    /**
     * Server copy, e.g. "1,215 lost · 980 suspicious · 235 expected" or
     * "12 titles changed · 2 removed"; "No changes" when there is nothing to report.
     */
    headline: string;
    /** Same numbers as `ConnectionHealthSnapshotDetail.causes[category]`. */
    counts: ConnectionHealthCauseCounts;
    /**
     * Colour of the card, from `counts.suspicious` ALONE: null when it is 0 (neutral,
     * whatever the expected and user counts), else the category's worst finding severity
     * (`categoryVerdicts[category]`), else "warn" (suspicious units below every threshold).
     */
    severity: ConnectionHealthSeverity | null;
}

/** GET /connection-health/snapshots/{id}. Variation and moves are NOT here; see the comparison. */
export interface ConnectionHealthSnapshotDetail extends ConnectionHealthSnapshotRow {
    /** Null when the capture itself failed; a "failed" row whose checks failed keeps its totals. */
    totals: ConnectionHealthTotals | null;
    /** The row behind `referenceId` (the previous completed snapshot). */
    reference: ConnectionHealthSnapshotRow | null;
    /** Worst first, at most 50. */
    findings: ConnectionHealthFinding[];
    /** Findings beyond the cap (`findingCount` = findings.length + findingsTruncated). */
    findingsTruncated: number;
    /** Same milestones as the lean row, each with its quality-report link. */
    milestones: ConnectionHealthMilestoneDetail[];
    /** Five cards, always in the order sheets, components, tables, connections, destinations. */
    cards: ConnectionHealthCard[];
    /** Cause totals per category, from the stored change summary (the cards' `counts`). */
    causes: Record<ConnectionHealthCategory, ConnectionHealthCauseCounts>;
}

export interface ConnectionHealthVariationRow {
    /** Same key space as ConnectionHealthSeries.key, so a row can highlight its line. */
    key: string;
    label: string;
    before: number | null;
    after: number;
    delta: number | null;
    /** Percent, not a fraction (-5.4 = a 5.4% drop). Null when `before` is 0 or null. */
    deltaPct: number | null;
    /** Threshold verdict from the server checks; the client never derives tone from the sign. */
    severity: ConnectionHealthSeverity | null;
}

export interface ConnectionHealthMoveCell {
    /** Destination keys: upper-cased names, "CUSTOM_*", "DIRECT_*", "~unnamed", "~no_taglist". */
    from: string;
    to: string;
    fromClass: ConnectionHealthDestinationClass;
    toClass: ConnectionHealthDestinationClass;
    kind: ConnectionHealthMoveKind;
    /** NET tags that moved from -> to between the two states. */
    tags: number;
    /** Of `tags`, the ones explained by user edits (the receiving cell's user-sourced count grew). */
    userTags: number;
    sheetCount: number;
    /** Share (0..1) of the from-name's tags in the compared scope that moved. */
    shareOfFrom: number;
    severity: ConnectionHealthSeverity | null;
}

/** Changed sheets per attribution; the three counts sum to the number of changed sheets. */
export type ConnectionHealthAttributionCounts = Record<ConnectionHealthAttribution, number>;

/** GET /connection-health/snapshots/{id}/comparison. */
export interface ConnectionHealthComparison {
    snapshotId: string;
    /** Echo of the `against` param. */
    against: ConnectionHealthAgainst;
    /** The resolved target. Null when there is no older target: variation rows then carry `before: null`. */
    reference: ConnectionHealthSnapshotRow | null;
    /**
     * True when the as-of computation hit the 2,000-candidate-sheet cap: counts are a
     * lower bound. Always false for against=previous (stored summary).
     */
    approximate: boolean;
    /** Sheets still processing at the snapshot; the UI shows an "in flight" note. */
    inFlightSheets: number;
    /**
     * For against=previous the stored per-window attribution. For other targets each
     * sheet takes the least-explained attribution across the windows in range
     * (unexplained > human > revised).
     */
    attributionCounts: ConnectionHealthAttributionCounts;
    sheetsChanged: Record<ConnectionHealthCategory, number>;
    variation: Record<ConnectionHealthCategory, ConnectionHealthVariationRow[]>;
    moves: {
        /** Ranked by severity then tags, at most 500. */
        cells: ConnectionHealthMoveCell[];
        /** All cells, including the ones rolled into "other". */
        totalTagsMoved: number;
        /** Number of cells beyond the cap, and the tags they hold. */
        cellsTruncated: number;
        otherTags: number;
        /** Tags whose from -> to pairing was not unique (several names changed on both sides). */
        ambiguousTags: number;
    };
}

export type ConnectionHealthSheetSort = "severity" | "code" | "delta";

export type ConnectionHealthSortDirection = "asc" | "desc";

/** Query params of GET /snapshots/{id}/sheets; defaults and caps are on the api call. */
export interface ConnectionHealthSheetsQuery {
    against?: ConnectionHealthAgainst;
    category?: ConnectionHealthCategory;
    /** Finding check id; against=previous only (any other target answers 422). */
    check?: string;
    /** Destination keys of a move cell, given together; against=previous only, like `check`. */
    from?: string;
    to?: string;
    /** Case-insensitive substring of the sheet code, at most 100 chars. */
    q?: string;
    page?: number;
    pageSize?: number;
    sort?: ConnectionHealthSheetSort;
    dir?: ConnectionHealthSortDirection;
}

export type ConnectionHealthTableFlag =
    | "renamed"
    | "unnamed"
    | "config_changed"
    | "parse_failed"
    | "cells_lost"
    | "key_columns_lost"
    | "rows_halved"
    | "rows_doubled"
    | "one_column_collapse"
    | "key_explosion"
    | "junk_keys"
    | "inbound_drop"
    | "human_edit_overwritten";

export interface ConnectionHealthTableChange {
    /** Opaque and unique within the sheet row (tables have no stable id); a list key only. */
    key: string;
    title: string | null;
    /** Null for internal (CUSTOM_* / DIRECT_*) and unnamed tables. */
    nameBefore: string | null;
    nameAfter: string | null;
    change: "added" | "removed" | "changed";
    flags: ConnectionHealthTableFlag[];
    inboundBefore: number | null;
    inboundAfter: number | null;
    rowsBefore: number | null;
    rowsAfter: number | null;
    keysBefore: number | null;
    keysAfter: number | null;
}

/** A move cell on one sheet (`sheetCount` is 1; `shareOfFrom` is relative to this sheet). */
export interface ConnectionHealthSheetMoveCell extends ConnectionHealthMoveCell {
    /** Up to 10 example tag texts, e.g. ["101", "102A"]. */
    examples: string[];
}

/** One typed per-sheet DIFF row of GET /snapshots/{id}/sheets (never a raw payload). */
export interface ConnectionHealthSheetChange extends ConnectionHealthSheetRef {
    /** "renamed" = a removed and an added sheet key paired into ONE row (see `identityChange`). */
    change: "added" | "removed" | "changed" | "renamed";
    /** Multi-window comparisons use the rule on ConnectionHealthComparison.attributionCounts. */
    attribution: ConnectionHealthAttribution;
    /**
     * Set when the sheet's code or area changed; each side is [old, new], null when that
     * part did not change. Area values are display names resolved at read time (the area
     * id when the area row is gone); a null element = the sheet had no area.
     */
    identityChange: {
        code: [string, string] | null;
        area: [string | null, string | null] | null;
    } | null;
    /** The old sheet key of a "renamed" row (the ref itself carries the new one). */
    pairedSheetKey: string | null;
    /** Worst severity of findings touching this sheet; against=previous only, else null. */
    severity: ConnectionHealthSeverity | null;
    /** Categories whose hash differs between the two states. */
    categories: ConnectionHealthCategory[];
    /** Check ids that flagged this sheet; against=previous only, else empty. */
    checks: string[];
    /** Processing status as of the snapshot. */
    processingStatus: string | null;
    /** Null when the title did not change (`title` on the ref holds the current one). */
    titleChange: { before: string | null; after: string | null } | null;
    deltaConnections: number;
    deltaResolved: number;
    deltaComponents: number;
    tagsMoved: number;
    /** Changed rows only. */
    components: ConnectionHealthVariationRow[];
    tables: ConnectionHealthTableChange[];
    /** Changed rows only: total, resolved, duplicateEndpoints, noTextBox, byTargetType.*. */
    connections: ConnectionHealthVariationRow[];
    /** Per-destination counts that changed on this sheet (gains and losses no move explains), top 20 by |delta|. */
    destinations: ConnectionHealthVariationRow[];
    /** Top 20 cells for this sheet. */
    moves: ConnectionHealthSheetMoveCell[];
}

export interface ConnectionHealthSheetsPage {
    items: ConnectionHealthSheetChange[];
    total: number;
    page: number;
    pageSize: number;
}

/** A revision workflow of the compared window whose scope covers a group's (or a sheet's) heads. */
export interface ConnectionHealthGroupRevision {
    workflowId: string;
    revisionId: string | null;
    /** The revision's name ("Bulletin 3"); null when the workflow has no revision or it is unnamed. */
    name: string | null;
}

/**
 * One row of a category detail page: changed units rolled up by destination key
 * (connections), by move (destinations) or by check id (sheets, components, tables).
 */
export interface ConnectionHealthGroup {
    /** Destination key ("DOOR", "~unnamed", "CUSTOM_*"), move "FROM>TO", or the check id. Also the `{key}` of /groups/{key}/sheets. */
    key: string;
    /**
     * What to show: the destination key with sentinels rendered ("~unnamed" -> "(unnamed)",
     * "~no_taglist" -> "(no taglist)"), "FROM → TO" for a move, the check's plain label
     * ("Renamed with inbound connections", "Title changed", ...) for a check group.
     */
    label: string;
    /**
     * Units: connections for connections (a "lost" unit is a tag that left the name, or one still
     * under it whose connection stopped resolving: the wipe incident), tags for destinations,
     * sheets for the check-keyed categories.
     */
    total: number;
    suspicious: number;
    expected: number;
    user: number;
    /** Sheets with at least one unit under this key; the group's sheets endpoint pages them. */
    sheetCount: number;
    /** Window workflows whose scope covers any of the group's sheets, in window order. */
    revisions: ConnectionHealthGroupRevision[];
}

/** Query params of GET /snapshots/{id}/groups; defaults and caps are on the api call. */
export interface ConnectionHealthGroupsQuery {
    category: ConnectionHealthCategory;
    /**
     * "lost" | "gained" for connections (default "lost"), "moved" for destinations, a check
     * id of the category for the others (omitted: every check group of the category in one
     * list); anything else answers 422.
     */
    kind?: ConnectionHealthGroupKind;
    /**
     * "previous" (stored summary, the default), "kickoff" or an older snapshot's uuid
     * (recomputed from as-of states).
     */
    against?: ConnectionHealthAgainst;
    /** 1..200, default 100. */
    limit?: number;
}

/** GET /connection-health/snapshots/{id}/groups. */
export interface ConnectionHealthGroups {
    category: ConnectionHealthCategory;
    /** The kind served; null when a check-keyed category was asked for every check group. */
    kind: ConnectionHealthGroupKind | null;
    /** Echo of the `against` param. */
    against: ConnectionHealthAgainst;
    /** The whole category's units by cause (the cause strip), whatever the kind. */
    causes: ConnectionHealthCauseCounts;
    /** Most suspicious first, then largest, then key; at most `limit`. */
    groups: ConnectionHealthGroup[];
    /** More groups than `limit` exist (the stored summary keeps at most 500 per kind). */
    truncated: boolean;
    /** As ConnectionHealthComparison.approximate; always false for against=previous. */
    approximate: boolean;
}

/** Query params of GET /snapshots/{id}/groups/{key}/sheets; defaults and caps are on the api call. */
export interface ConnectionHealthGroupSheetsQuery {
    category: ConnectionHealthCategory;
    /** As /groups; for sheets / components / tables it defaults to `key` and must equal it when given. */
    kind?: ConnectionHealthGroupKind;
    /** "previous" only: stored per-sheet rows exist for the previous window alone; anything else answers 422. */
    against?: "previous";
    page?: number;
    /** 1..100. */
    pageSize?: number;
}

/** One sheet of a group: the click-through of a category detail page. */
export interface ConnectionHealthGroupSheet extends ConnectionHealthSheetRef {
    /**
     * The sheet's connection (tag) delta over the window, all destinations, e.g. -42. It is 0 on
     * a sheet whose tags stayed and lost their destinations; `counts` still says how many.
     */
    delta: number;
    /** This sheet's units under the group by cause (a check group: the sheet, once). */
    counts: ConnectionHealthCauseCounts;
    /** The one cause the row reads as: suspicious when any unit is, else user / expected (the larger; user on a tie). */
    cause: ConnectionHealthCause;
    /** Window workflows that re-issued THIS sheet; empty on a sheet no revision touched. */
    revisions: ConnectionHealthGroupRevision[];
}

/** GET /connection-health/snapshots/{id}/groups/{key}/sheets. */
export interface ConnectionHealthGroupSheets {
    /**
     * Most suspicious units under the key first (destination-keyed groups) or worst
     * severity first (check groups), then sheet key.
     */
    items: ConnectionHealthGroupSheet[];
    total: number;
    page: number;
    pageSize: number;
}

/** Query params of GET /connection-health/snapshots. */
export interface ConnectionHealthSnapshotsQuery {
    /** Exclusive upper bound on `seq`; omit for the newest page. */
    beforeSeq?: number;
    /** 1..100, default 50. */
    limit?: number;
}

export interface ConnectionHealthSnapshotList {
    /** Descending by seq, every status. */
    items: ConnectionHealthSnapshotRow[];
    /** Pass as `beforeSeq` for the next page; null when there is nothing older. */
    nextBeforeSeq: number | null;
}

/** POST /snapshots answers the capture that holds everything the request could see. */
export interface ConnectionHealthCaptureResult {
    snapshot: ConnectionHealthSnapshotRow;
    /**
     * True when no new capture ran: a live snapshot whose capture began after this request
     * arrived answered it (two admins pressing the button seconds apart cost one capture).
     */
    reused: boolean;
}

/** POST /snapshots/{id}/kickoff answers the updated lean row. */
export type ConnectionHealthKickoffResult = ConnectionHealthSnapshotRow;

export interface ConnectionHealthFleetRow {
    orgSlug: string;
    /** True when the tenant read failed; "migration_pending" is reported separately, not as an error. */
    error: boolean;
    unavailableReason: "migration_pending" | null;
    /** `createdAt` of the designated kick-off snapshot; null while it is only provisional. */
    kickoffAt: string | null;
    /** Latest COMPLETED snapshot. */
    latest: ConnectionHealthSnapshotRow | null;
    /** The snapshot with the worst verdict in the trailing 7 days (newest on a tie). */
    worst7d: ConnectionHealthSnapshotRow | null;
    failing7d: number;
    warning7d: number;
    /** Up to 3 finding titles from `worst7d`, e.g. "412 tags moved ROOM -> DOOR on 19 sheets". */
    topFindings: string[];
    /** Hours since the latest completed snapshot; the table flags > 36 as "stale". */
    hoursSinceLatest: number | null;
}

export interface ConnectionHealthFleetResponse {
    projects: ConnectionHealthFleetRow[];
}

export interface SheetReviewStatus {
    section_group_id: string;
    gate: {
        node: "annotate_segmentation";
        state: "awaiting" | "approved" | "not_applicable";
        approved_by: string | null;
        approved_at: string | null;
        revision_id: string | null;
    };
    verdicts: { count: number; reviewers: string[] };
}
